import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { 
  Check, Camera, FileText, Eye, Download, Save, Printer, Plus, X, Upload, Video, Music, Image, Trash2
} from 'lucide-react';
import { LocalStorageManager } from '../utils/localStorage';
import axios from 'axios';
import { useNotifications } from '../contexts/NotificationContext';
import { candidateStatusOptions } from '../components/ui/StatusBadge';
import { recruitmentTheme } from '../styles/theme';
import { useLocation } from 'react-router-dom';
import { addCVDraft as addCVDraftToFirebase, addDocument as addDocumentToFirebase } from '../services/documentService';
import { getAllCandidates, getCandidateWithCVs, subscribeToCandidateCVs, type CandidateWithMeta, type CandidateWithCVs, type CandidateCV } from '../services/candidateService';


// CV Builder Component
const CVBuilder = () => {
  const { showNotification } = useNotifications();
  const location = useLocation();
  const [previewMode, setPreviewMode] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('template4');
  const [newSkillName, setNewSkillName] = useState('');
  const [uploading, setUploading] = useState(false);
  const printRef = useRef(null);
  
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Multi-CV support state
  const [currentCandidate, setCurrentCandidate] = useState<CandidateWithCVs | null>(null);
  const [candidateCVs, setCandidateCVs] = useState<CandidateCV[]>([]);
  const [selectedCVVersion, setSelectedCVVersion] = useState<string | null>(null);
  const [showCVSelector, setShowCVSelector] = useState(false);
  
  // CV Auto-fill state
  const [cvExtracting, setCvExtracting] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState<{current: number, total: number}>({ current: 0, total: 0 });
  const [extractionResults, setExtractionResults] = useState<any[]>([]);
  const [webhookUrl] = useState('https://n8n.naiminvestments.com/webhook/document-extract'); // Live n8n webhook endpoint
  const autosaveDraftIdRef = useRef<string | null>(null); // Track the auto-save draft ID
  const manualDraftIdRef = useRef<string | null>(null); // Track the manual save draft ID
  
  // Utility function to clear localStorage if needed
  const clearLocalStorageIfNeeded = () => {
    try {
      const storageSize = new Blob(Object.values(localStorage)).size;
      // If storage is getting too large (over 2MB), clear old data more aggressively
      if (storageSize > 2 * 1024 * 1024) {
        console.warn('LocalStorage is getting full, clearing old data...');
        
        // Clear all uploadedDocuments
        localStorage.removeItem('uploadedDocuments');
        localStorage.removeItem('tempDocuments');
        
        // Keep only essential data
        const essentialKeys = ['currentCVDraft'];
        Object.keys(localStorage).forEach(key => {
          if (!essentialKeys.includes(key)) {
            // Remove all non-essential items
            if (key.includes('document') || key.includes('upload') || key.includes('temp')) {
              localStorage.removeItem(key);
            }
          }
        });
        
        // Limit CV drafts to last 3
        const drafts = JSON.parse(localStorage.getItem('cvDrafts') || '[]');
        if (drafts.length > 3) {
          localStorage.setItem('cvDrafts', JSON.stringify(drafts.slice(-3)));
        }
      }
    } catch (error) {
      console.error('Error checking localStorage size:', error);
      // If there's an error, try to clear non-essential data anyway
      try {
        localStorage.removeItem('uploadedDocuments');
        localStorage.removeItem('tempDocuments');
      } catch (clearError) {
        console.error('Error clearing storage:', clearError);
      }
    }
  };
  
  // Manual clear storage function for users
  const clearStorageManually = () => {
    if (window.confirm('This will clear all stored documents and drafts to free up space. Are you sure you want to continue?')) {
      try {
        // Clear non-essential items
        localStorage.removeItem('uploadedDocuments');
        localStorage.removeItem('tempDocuments');
        
        // Keep only recent drafts
        const drafts = JSON.parse(localStorage.getItem('cvDrafts') || '[]');
        if (drafts.length > 3) {
          localStorage.setItem('cvDrafts', JSON.stringify(drafts.slice(-3)));
        }
        
        // Clear current draft images from localStorage to save space
        try {
          const currentDraftRaw = localStorage.getItem('recruitment_crm_current_cv_draft');
          if (currentDraftRaw) {
            const currentDraft = JSON.parse(currentDraftRaw);
            if (currentDraft && currentDraft.formData) {
              const lightweightDraft = {
                ...currentDraft,
                formData: {
                  ...currentDraft.formData,
                  profilePhoto: null,
                  fullBodyPhoto: null,
                  logoFile: null,
                  passportDocument: null,
                  idDocument: null,
                  qualificationsCertificates: [],
                  skillsCertificates: [],
                  mediaFiles: []
                }
              };
              localStorage.setItem('recruitment_crm_current_cv_draft', JSON.stringify(lightweightDraft));
            }
          }
        } catch (error) {
          console.error('Error clearing current draft images:', error);
        }
        
        showNotification({
          type: 'success',
          title: 'Storage Cleared',
          message: 'Storage cleared successfully! You can now continue working.'
        });
      } catch (error) {
        console.error('Error clearing storage:', error);
        showNotification({
          type: 'error',
          title: 'Storage Error',
          message: 'Error clearing storage. Please try again.'
        });
      }
    }
  };
  

  
  // Form state with required validation removed for testing
  const [formData, setFormData] = useState({
    companyName: 'NAIM INVESTMENT OFFICE',
    companyNameArabic: 'مكتب الاستثمار نعيم',
    companyLocation: 'P O Box 80249-80100 Mombasa, Kenya',
    contactNumber: '+254720931164',
    logoFile: null,
    position: 'HOUSEMAID',
    positionArabic: 'الوظيفة',
    salary: '…..SAR',
    passportNo: 'BK950859',
    dateIssued: '29/APR/2025',
    dateExpiry: '28/APR/2035',
    placeIssued: 'MOMBASA',
    fullName: 'LEAH SALAMA KAZUNGU',
    fullNameArabic: '',
    address: 'VIPINGO',
    nationality: 'KENYAN',
    religion: 'MUSLIM',
    dateOfBirth: '26/FEB/1992',
    placeOfBirth: 'KILIFI, KEN',
    father: 'HASSAN KOMBO',
    mother: 'UMAZI KOMBO',
    age: '33 Years',
    height: '4\'10"',
    weight: '68.5 kgs',
    civilStatus: 'SINGLE',
    spouse: 'YES',
    numberOfKids: '2 KIDS',
    educationLevel: 'KOLEWA Primary School',
    englishLevel: 'LITTLE',
    arabicLevel: 'FAIR',
    workPosition: 'DOMESTIC WORKER',
    workCountry: 'SAUDI ARABIA',
    workYears: '2 YEARS',
    allSkills: [
      { name: 'ARABIC DISH COOKING', checked: true, isDefault: true },
      { name: 'CLEANING', checked: true, isDefault: true },
      { name: 'WASHING', checked: true, isDefault: true },
      { name: 'IRONING', checked: true, isDefault: true },
      { name: 'BABYSITTING', checked: true, isDefault: true },
      { name: 'CARING ELDERS', checked: true, isDefault: true },
    ],
    remarks: 'COOPERATIVE / MEDIUM BODY BUILD / HARDWORKING / EXPERIENCED',
    // Fields for Candidates page correspondence (Other Information section)
    contact: '',
    email: '',
    emergencyContact: '',
    workCompany: '',
    workCity: '',
    stage: 'Onboarding',
    profilePhoto: null,
    fullBodyPhoto: null,
    // Document uploads
    passportDocument: null as any,
    idDocument: null as any,
    qualificationsCertificates: [] as any[],
    goodConductCertificates: [] as any[],
    otherDocuments: [] as any[],
    // Document zoom levels
    passportZoom: 100,
    idZoom: 100,
    qualificationZooms: [] as number[],
    conductZooms: [] as number[],
    otherZooms: [] as number[],
    // Media uploads
    mediaFiles: [] as any[],
    // Template 1 specific fields
    contractPeriod: '',
    monthlySalary: '',
    // Template 2 specific fields
    customCompanyName: '',
    customCompanyLocation: '',
    salaryExpectation: '',
    // Template 3 specific fields
    referenceNumber: '',
    cvDate: '',
    destination: '',
    nextOfKinName: '',
    kinship: '',
    otherEmergencyInfo: '',
    idNumber: '',
    complexion: '',
    educationPeriod: '',
    additionalEducationInfo: '',
    hobbies: '',
    medicalHistory: '',
    // Template 6 specific fields
    workerName: 'NAIM',
    durationInsurance: '',
    previouslyWorked: '',
    otherExperiences: '',
  });

  const [validationErrors, setValidationErrors] = useState([]);
  
  // Auto-save draft state
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [isAutoSaveEnabled, setIsAutoSaveEnabled] = useState(true);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isFormDirtyRef = useRef(false);
  
  // Focus preservation
  const activeElementRef = useRef<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const scrollPositionRef = useRef<number>(0);
  

  // Helper function to load CV version data into form
  const loadCVVersion = (cv: CandidateCV) => {
    console.log('📄 Loading CV version:', cv.company, cv.template);
    
    const extractedFields = cv.extractedFields;
    setFormData(prev => ({
      ...prev,
      // Personal Information
      fullName: extractedFields.personalInfo?.fullName || '',
      fullNameArabic: extractedFields.personalInfo?.fullNameArabic || '',
      nationality: extractedFields.personalInfo?.nationality || '',
      dateOfBirth: extractedFields.personalInfo?.dateOfBirth || '',
      age: extractedFields.personalInfo?.age || '',
      gender: extractedFields.personalInfo?.gender || '',
      civilStatus: extractedFields.personalInfo?.civilStatus || '',
      height: extractedFields.personalInfo?.height || '',
      weight: extractedFields.personalInfo?.weight || '',
      religion: extractedFields.personalInfo?.religion || '',
      
      // Contact Information
      contact: extractedFields.contactInfo?.phone || '',
      email: extractedFields.contactInfo?.email || '',
      address: extractedFields.contactInfo?.address || '',
      emergencyContact: extractedFields.contactInfo?.emergencyContact?.phone || '',
      
      // Passport Information
      passportNo: extractedFields.passport?.number || '',
      dateExpiry: extractedFields.passport?.expiry || '',
      placeIssued: extractedFields.passport?.placeOfIssue || '',
      
      // Education and Skills
      educationLevel: extractedFields.education?.level || '',
      allSkills: extractedFields.skills || [],
      
      // Family
      father: extractedFields.family?.father || '',
      mother: extractedFields.family?.mother || '',
      
      // Work Preferences
      jobTitle: extractedFields.preferences?.jobTitle || '',
      salary: extractedFields.preferences?.salary || '',
      workCountry: extractedFields.preferences?.location || '',
      
      // Medical
      medicalHistory: extractedFields.medical || '',
      
      // Additional notes
      remarks: `CV Version: ${cv.company} (${cv.template}). Original file: ${cv.originalFileName}`
    }));
    
    setSelectedTemplate(cv.template);
  };
  
  // Helper function to load basic candidate data (no CV version)
  const loadBasicCandidateData = (candidate: any) => {
    console.log('📄 Loading basic candidate data:', candidate.name);
    
    setFormData(prev => ({
      ...prev,
      // Basic candidate information
      fullName: candidate.name || candidate.fullName || '',
      email: candidate.email || candidate.emailAddress || '',
      contact: candidate.phone || candidate.contactNumber || candidate.contact || '',
      emergencyContact: candidate.emergencyContact || '',
      
      // Job and work information
      position: candidate.jobTitle || '',
      workPosition: candidate.workPosition || candidate.jobTitle || '',
      workCompany: candidate.workCompany || candidate.company || '',
      salary: candidate.salary || '',
      workCity: candidate.workCity || candidate.city || '',
      workCountry: candidate.workCountry || candidate.country || '',
      workYears: candidate.workYears || '',
      countryApplyingTo: candidate.countryApplyingTo || '',
      
      // Personal information
      address: candidate.address || '',
      nationality: candidate.nationality || '',
      dateOfBirth: candidate.dateOfBirth || '',
      placeOfBirth: candidate.placeOfBirth || '',
      age: candidate.age || '',
      gender: candidate.gender || '',
      civilStatus: candidate.civilStatus || candidate.maritalStatus || '',
      religion: candidate.religion || '',
      height: candidate.height || '',
      weight: candidate.weight || '',
      complexion: candidate.complexion || '',
      numberOfKids: candidate.numberOfKids || candidate.children || '',
      
      // Family information
      father: candidate.father || candidate.fatherName || '',
      mother: candidate.mother || candidate.motherName || '',
      spouse: candidate.spouse || '',
      
      // Document information
      passportNo: candidate.passportNumber || '',
      idNumber: candidate.idNumber || '',
      dateIssued: candidate.dateIssued || '',
      dateExpiry: candidate.dateExpiry || '',
      placeIssued: candidate.placeIssued || '',
      
      // Education and skills
      educationLevel: candidate.educationLevel || '',
      englishLevel: candidate.englishLevel || '',
      arabicLevel: candidate.arabicLevel || '',
      allSkills: candidate.allSkills || prev.allSkills,
      
      // Additional information
      medicalHistory: candidate.medicalHistory || '',
      hobbies: candidate.hobbies || '',
      stage: candidate.stage || '',
      remarks: candidate.notes || candidate.remarks || ''
    }));
  };

  // Auto-save draft functionality - Enhanced for better persistence
  const autoSaveDraft = useCallback(async () => {
    if (!isAutoSaveEnabled) return;
    
    try {
      // Save to localStorage for immediate persistence
      LocalStorageManager.saveCurrentCVDraft(formData, selectedTemplate);
      setLastSavedAt(new Date());
      isFormDirtyRef.current = false;
      
      console.log('💾 Auto-saved CV draft to localStorage');
      
      // Also save to Firebase for sync across devices (if candidate exists)
      if (currentCandidate && formData.fullName) {
        // Use a consistent draft ID based on candidate ID to prevent duplicates
        const draftId = autosaveDraftIdRef.current;
        
        const draftData: any = {
          name: `${formData.fullName} - Auto-saved Draft`,
          candidateName: formData.fullName,
          candidateId: currentCandidate.id,
          formData: formData,
          template: selectedTemplate,
          description: `Auto-saved draft for ${formData.fullName}`,
          content: JSON.stringify(formData),
          uploadedBy: 'CV Builder Auto-save',
          size: JSON.stringify(formData).length,
          category: 'cv-drafts',
          type: 'cv-draft',
          url: '#',
          uploadedAt: new Date().toISOString()
        };
        
        // Only add ID if we have a valid Firebase ID
        if (draftId && !draftId.startsWith('autosave_')) {
          draftData.id = draftId;
        }
        
        try {
          const savedId = await addCVDraftToFirebase(draftData);
          // Update the ref with the actual Firebase ID for future updates
          if (savedId) {
            autosaveDraftIdRef.current = savedId;
          }
          console.log('✅ Auto-saved to Firebase with ID:', savedId);
        } catch (firebaseError) {
          console.warn('Firebase save failed, but local save succeeded:', firebaseError);
        }
      }
    } catch (error) {
      console.error('Error auto-saving draft:', error);
    }
  }, [formData, selectedTemplate, currentCandidate, isAutoSaveEnabled]);

  // Debounced save function
  const debouncedSave = useCallback(() => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    autoSaveTimerRef.current = setTimeout(() => {
      if (isFormDirtyRef.current) {
        autoSaveDraft();
      }
    }, 3000); // Auto-save after 3 seconds of inactivity
  }, [autoSaveDraft]);

  // Mark form as dirty when data changes
  const markFormDirty = useCallback(() => {
    isFormDirtyRef.current = true;
    debouncedSave();
  }, [debouncedSave]);

  // Date parsing utility function to handle various date formats
  const parseAndFormatDate = (dateString: string): string => {
    if (!dateString || typeof dateString !== 'string') return '';
    
    // Clean the date string
    const cleanDate = dateString.trim();
    if (!cleanDate) return '';
    
    console.log('🔍 Parsing date string:', cleanDate);
    
    // Common date patterns to try
    const datePatterns = [
      // ISO format: 2024-12-31
      /^(\d{4})-(\d{1,2})-(\d{1,2})$/,
      // US format: 12/31/2024 or 12-31-2024
      /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/,
      // European format: 31/12/2024 or 31-12-2024
      /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/,
      // Year first: 2024/12/31
      /^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/,
      // Dot separated: 31.12.2024 or 12.31.2024
      /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/,
      // Dot separated with spaces: 31. 12. 2024 or 07. 01. 1999
      /^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})$/,
      // Space separated: 31 12 2024
      /^(\d{1,2})\s+(\d{1,2})\s+(\d{4})$/,
      // Compact format: 20241231
      /^(\d{4})(\d{2})(\d{2})$/,
      // Compact format with dots: 2024.12.31
      /^(\d{4})\.(\d{1,2})\.(\d{1,2})$/,
    ];
    
    // Month name mapping
    const monthMap: { [key: string]: string } = {
      'JAN': '01', 'JANUARY': '01',
      'FEB': '02', 'FEBRUARY': '02', 
      'MAR': '03', 'MARCH': '03',
      'APR': '04', 'APRIL': '04',
      'MAY': '05',
      'JUN': '06', 'JUNE': '06',
      'JUL': '07', 'JULY': '07',
      'AUG': '08', 'AUGUST': '08',
      'SEP': '09', 'SEPTEMBER': '09',
      'OCT': '10', 'OCTOBER': '10',
      'NOV': '11', 'NOVEMBER': '11',
      'DEC': '12', 'DECEMBER': '12'
    };
    
    // PRIORITY PATTERN: DD MMM YYYY (e.g., "19 JAN 1994", "15 DEC 2011", "14 DEC 2021")
    const ddMmmYyyySpaced = /^(\d{1,2})\s+([A-Za-z]{3,9})\s+(\d{4})$/;
    const spacedMatch = cleanDate.match(ddMmmYyyySpaced);
    if (spacedMatch) {
      const [, dayStr, monthStr, yearStr] = spacedMatch;
      const monthNum = monthMap[monthStr.toUpperCase()];
      if (monthNum) {
        const result = `${yearStr}-${monthNum}-${pad(parseInt(dayStr))}`;
        console.log('✅ Date parsed (DD MMM YYYY):', cleanDate, '→', result);
        return result;
      }
    }
    
    // PRIORITY PATTERN: DD/MMM/YYYY (e.g., "29/APR/2025", "28/APR/2035")
    const ddMmmYyyySlash = /^(\d{1,2})[\/-]([A-Za-z]{3,9})[\/-](\d{4})$/;
    const slashMatch = cleanDate.match(ddMmmYyyySlash);
    if (slashMatch) {
      const [, dayStr, monthStr, yearStr] = slashMatch;
      const monthNum = monthMap[monthStr.toUpperCase()];
      if (monthNum) {
        const result = `${yearStr}-${monthNum}-${pad(parseInt(dayStr))}`;
        console.log('✅ Date parsed (DD/MMM/YYYY):', cleanDate, '→', result);
        return result;
      }
    }
    
    // Skip all the complex date parsing below for now
    for (const pattern of datePatterns) {
      const match = cleanDate.match(pattern);
      if (match) {
        let year, month, day;
        
        if (pattern.source.includes('(\\d{4})(\\d{2})(\\d{2})')) {
          // Compact format: YYYYMMDD
          [, year, month, day] = match;
        } else if (pattern.source.startsWith('^(\\d{4})')) {
          // Year first format: YYYY-MM-DD, YYYY.MM.DD
          [, year, month, day] = match;
        } else {
          // Day/Month/Year or Month/Day/Year formats
          const [, first, second, yearStr] = match;
          
          // If first number > 12, it's likely DD/MM/YYYY
          if (parseInt(first) > 12) {
            day = first;
            month = second;
            year = yearStr;
          } else if (parseInt(second) > 12) {
            // If second number > 12, it's likely MM/DD/YYYY
            month = first;
            day = second;
            year = yearStr;
          } else {
            // Ambiguous - check pattern context or assume US format (MM/DD/YYYY) by default
            // For European contexts (dots), assume DD.MM.YYYY
            if (pattern.source.includes('\\.')) {
              day = first;
              month = second;
              year = yearStr;
            } else {
              // For slashes/dashes, assume MM/DD/YYYY (US format)
              month = first;
              day = second;
              year = yearStr;
            }
          }
        }
        
        // Validate the parsed values
        const monthNum = parseInt(month);
        const dayNum = parseInt(day);
        const yearNum = parseInt(year);
        
        if (monthNum >= 1 && monthNum <= 12 && dayNum >= 1 && dayNum <= 31 && yearNum >= 1900 && yearNum < 2100) {
          // Format as YYYY-MM-DD without timezone conversion
          const result = `${yearNum}-${pad(monthNum)}-${pad(dayNum)}`;
          console.log('✅ Date parsed successfully (manual):', cleanDate, '→', result);
          return result;
        }
      }
    }
    
    // Handle DD/MMM/YYYY format specifically (like "29/APR/2025") to avoid timezone issues
    const ddMmmYyyyPattern = /^(\d{1,2})[\/\-]([A-Za-z]{3})[\/\-](\d{4})$/;
    const ddMmmMatch = cleanDate.match(ddMmmYyyyPattern);
    if (ddMmmMatch) {
      const [, dayStr, monthStr, yearStr] = ddMmmMatch;
      const monthMap: { [key: string]: number } = {
        'JAN': 0, 'FEB': 1, 'MAR': 2, 'APR': 3, 'MAY': 4, 'JUN': 5,
        'JUL': 6, 'AUG': 7, 'SEP': 8, 'OCT': 9, 'NOV': 10, 'DEC': 11
      };
      
      const day = parseInt(dayStr);
      const month = monthMap[monthStr.toUpperCase()];
      const year = parseInt(yearStr);
      
      if (month !== undefined && day >= 1 && day <= 31 && year >= 1900 && year < 2100) {
        // Format as YYYY-MM-DD without timezone conversion
        const result = `${year}-${pad(month + 1)}-${pad(day)}`;
        console.log('✅ Date parsed successfully (DD/MMM/YYYY):', cleanDate, '→', result);
        return result;
      }
    }
    
    // Try parsing month names (e.g., "January 15, 2024", "15 Jan 2024")
    const monthNamePattern = /(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|January|February|March|April|May|June|July|August|September|October|November|December)[a-z]*\s+(\d{4})|(\d{4})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|January|February|March|April|May|June|July|August|September|October|November|December)[a-z]*\s+(\d{1,2})/i;
    const monthMatch = cleanDate.match(monthNamePattern);
    if (monthMatch) {
      const testDate = new Date(cleanDate);
      if (!isNaN(testDate.getTime())) {
        // Use local date formatting to avoid timezone issues
        const yearStr = testDate.getFullYear();
        const monthStr = pad(testDate.getMonth() + 1);
        const dayStr = pad(testDate.getDate());
        const result = `${yearStr}-${monthStr}-${dayStr}`;
        console.log('✅ Date parsed successfully (month name):', cleanDate, '→', result);
        return result;
      }
    }
    
    console.warn('❌ Could not parse date format:', cleanDate);
    return ''; // Return empty string if parsing fails
  };

  // Helper function to pad numbers with leading zeros
  const pad = (num: number): string => num.toString().padStart(2, '0');

  // Helper function to handle "empty" values from n8n
  const cleanEmptyValue = (value: any): string => {
    if (value === "empty" || value === null || value === undefined) return "";
    if (typeof value === 'string' && value.toLowerCase() === "empty") return "";
    return value;
  };

  // CV Upload and Auto-fill functionality - delegates to handleMultipleUploads
  const handleUpload = async (file: File) => {
    await handleMultipleUploads([file]);
  };

  // Handle multiple file uploads
  const handleMultipleUploads = async (files: File[]) => {
    if (files.length === 0) return;

    setCvExtracting(true);
    setExtractionProgress({ current: 0, total: files.length });
    setExtractionResults([]);

    const results: any[] = [];

    try {
      // Show initial notification
      showNotification({
        type: 'info',
        title: 'Processing Documents',
        message: `Starting extraction from ${files.length} document${files.length > 1 ? 's' : ''}...`,
        duration: 3000
      });

      // Process files in parallel with a limit of 3 concurrent uploads
      const batchSize = 3;
      for (let i = 0; i < files.length; i += batchSize) {
        const batch = files.slice(i, Math.min(i + batchSize, files.length));
        
        const batchPromises = batch.map(async (file, batchIndex) => {
          const fileIndex = i + batchIndex;
          const formData = new FormData();
          formData.append("file", file);

          try {
            console.log(`📤 Uploading file ${fileIndex + 1}/${files.length}: ${file.name}`);
            
            const res = await fetch(webhookUrl, {
              method: "POST",
              body: formData,
              mode: 'cors',
              headers: {
                'Accept': 'application/json',
              },
            });

            if (!res.ok) {
              throw new Error(`HTTP error! status: ${res.status}`);
            }

            const responseText = await res.text();
            console.log(`📥 Response for ${file.name}:`, responseText.substring(0, 200) + '...');

            if (!responseText || responseText.trim() === '') {
              throw new Error('No data received from webhook');
            }

            const data = JSON.parse(responseText);
            console.log(`✅ Parsed data for ${file.name}:`, data);

            if (data.success === false) {
              throw new Error(data.error || data.message || 'No data could be extracted from this document');
            }

            if (!data.extractedData || typeof data.extractedData !== 'object') {
              throw new Error('Invalid response format');
            }

            results.push({
              fileName: file.name,
              data: data.extractedData,
              success: true
            });

            // Update progress
            setExtractionProgress(prev => ({ ...prev, current: prev.current + 1 }));

          } catch (error: any) {
            console.error(`❌ Failed to process ${file.name}:`, error);
            results.push({
              fileName: file.name,
              error: error.message,
              success: false
            });
            
            // Update progress even for failures
            setExtractionProgress(prev => ({ ...prev, current: prev.current + 1 }));
          }
        });

        await Promise.all(batchPromises);
      }

      // Merge the extracted data from all successful documents
      const successfulExtractions = results.filter(r => r.success);
      
      if (successfulExtractions.length === 0) {
        throw new Error('No documents were successfully processed');
      }

      // Merge data with priority to non-empty values
      const mergedData = mergeExtractedData(successfulExtractions.map(r => r.data));
      
      // Apply the merged data to the form
      applyExtractedData({ extractedData: mergedData });

      // Show completion notification
      const failedCount = results.filter(r => !r.success).length;
      showNotification({
        type: 'success',
        title: 'Extraction Complete',
        message: `Successfully extracted data from ${successfulExtractions.length} document${successfulExtractions.length > 1 ? 's' : ''}${failedCount > 0 ? `. ${failedCount} document${failedCount > 1 ? 's' : ''} failed.` : '.'}`,
        duration: 8000
      });

      // Store results for potential review
      setExtractionResults(results);

    } catch (err: any) {
      console.error("❌ Multiple upload failed:", err);
      
      showNotification({
        type: 'error',
        title: 'Extraction Failed',
        message: err.message || 'Failed to extract data from documents. Please try again.',
        duration: 8000
      });
    } finally {
      setCvExtracting(false);
      setExtractionProgress({ current: 0, total: 0 });
    }
  };

  // Merge extracted data from multiple documents
  const mergeExtractedData = (dataArray: any[]) => {
    const merged: any = {};
    
    // Define field priorities - later documents can override earlier ones only if they have non-empty values
    dataArray.forEach(data => {
      Object.keys(data).forEach(key => {
        const value = data[key];
        const currentValue = merged[key];
        
        // Skip if the new value is empty or "empty"
        if (!value || value === "empty" || (Array.isArray(value) && value.length === 0)) {
          return;
        }
        
        // If current value is empty or "empty", use the new value
        if (!currentValue || currentValue === "empty" || (Array.isArray(currentValue) && currentValue.length === 0)) {
          merged[key] = value;
          return;
        }
        
        // For arrays, merge unique values
        if (Array.isArray(value) && Array.isArray(currentValue)) {
          const mergedArray = [...currentValue];
          value.forEach(item => {
            if (!mergedArray.some(existing => JSON.stringify(existing) === JSON.stringify(item))) {
              mergedArray.push(item);
            }
          });
          merged[key] = mergedArray;
          return;
        }
        
        // For objects, merge recursively
        if (typeof value === 'object' && typeof currentValue === 'object' && !Array.isArray(value) && !Array.isArray(currentValue)) {
          merged[key] = mergeExtractedData([currentValue, value]);
          return;
        }
        
        // For other types, keep the newer non-empty value
        merged[key] = value;
      });
    });
    
    return merged;
  };

  // Extract the data application logic into a separate function
  const applyExtractedData = (data: any) => {
    try {
      console.log("📊 Applying extracted data:", data);
      
      // Clear the current CV draft to prevent persistence interference
      LocalStorageManager.saveCurrentCVDraft({}, selectedTemplate);
      
      // Create a fresh form with only extracted data, not merging with previous
      setFormData(prev => ({
        // Personal Information (Fields 1-10)
        fullName: cleanEmptyValue(data.extractedData.fullName) || '',
        fullNameArabic: cleanEmptyValue(data.extractedData.fullNameArabic) || '',
        firstName: cleanEmptyValue(data.extractedData.firstName) || '',
        lastName: cleanEmptyValue(data.extractedData.lastName) || '',
        middleName: cleanEmptyValue(data.extractedData.middleName) || '',
        dateOfBirth: parseAndFormatDate(cleanEmptyValue(data.extractedData.dob) || cleanEmptyValue(data.extractedData.dateOfBirth) || cleanEmptyValue(data.extractedData.birthDate) || "") || '',
        age: cleanEmptyValue(data.extractedData.age) || '',
        gender: cleanEmptyValue(data.extractedData.gender) || '',
        civilStatus: cleanEmptyValue(data.extractedData.maritalStatus) || cleanEmptyValue(data.extractedData.civilStatus) || '',
        nationality: cleanEmptyValue(data.extractedData.nationality) || '',
        religion: cleanEmptyValue(data.extractedData.religion) || '',
        height: cleanEmptyValue(data.extractedData.height) || '',
        weight: cleanEmptyValue(data.extractedData.weight) || '',
        
        // Contact Information (Fields 11-15)
        numberOfKids: cleanEmptyValue(data.extractedData.children) || '',
        address: cleanEmptyValue(data.extractedData.address) || cleanEmptyValue(data.extractedData.currentAddress) || '',
        contact: cleanEmptyValue(data.extractedData.phoneNumber) || cleanEmptyValue(data.extractedData.contactNumber) || '',
        email: cleanEmptyValue(data.extractedData.email) || cleanEmptyValue(data.extractedData.emailAddress) || '',
        emergencyContact: typeof data.extractedData.emergencyContact === 'object' 
          ? cleanEmptyValue(data.extractedData.emergencyContact.phoneNumber) || ''
          : cleanEmptyValue(data.extractedData.emergencyContactNumber) || '',
        
        // Passport & Documents (Fields 16-18)
        passportNo: cleanEmptyValue(data.extractedData.passportNumber) || '',
        idNumber: cleanEmptyValue(data.extractedData.idNumber) || '',
        dateIssued: parseAndFormatDate(cleanEmptyValue(data.extractedData.documentIssueDate) || 
                                      cleanEmptyValue(data.extractedData.passportIssueDate) || 
                                      cleanEmptyValue(data.extractedData.issueDate) || "") || '',
        dateExpiry: parseAndFormatDate(cleanEmptyValue(data.extractedData.documentExpiryDate) || 
                                      cleanEmptyValue(data.extractedData.passportExpiry) || 
                                      cleanEmptyValue(data.extractedData.passportExpiryDate) || 
                                      cleanEmptyValue(data.extractedData.expiryDate) || "") || '',
        placeOfBirth: cleanEmptyValue(data.extractedData.placeOfBirth) || '',
        placeIssued: cleanEmptyValue(data.extractedData.placeOfIssue) || cleanEmptyValue(data.extractedData.passportPlaceOfIssue) || '',
        
        // Education & Professional
        educationLevel: cleanEmptyValue(data.extractedData.education) || cleanEmptyValue(data.extractedData.educationLevel) || '',
        workPosition: Array.isArray(data.extractedData.workExperience) && data.extractedData.workExperience.length > 0
          ? (typeof data.extractedData.workExperience[0] === 'object'
              ? cleanEmptyValue(data.extractedData.workExperience[0]?.position) || ''
              : cleanEmptyValue(data.extractedData.workExperience[0]) || '')
          : cleanEmptyValue(data.extractedData.occupation) || cleanEmptyValue(data.extractedData.preferredJobTitle) || '',
        workCompany: Array.isArray(data.extractedData.workExperience) && data.extractedData.workExperience.length > 0 && typeof data.extractedData.workExperience[0] === 'object'
          ? cleanEmptyValue(data.extractedData.workExperience[0]?.company) || ''
          : cleanEmptyValue(data.extractedData.employer) || '',
        workYears: Array.isArray(data.extractedData.workExperience) && data.extractedData.workExperience.length > 0 && typeof data.extractedData.workExperience[0] === 'object'
          ? cleanEmptyValue(data.extractedData.workExperience[0]?.duration) || ''
          : '',
        workCity: Array.isArray(data.extractedData.workExperience) && data.extractedData.workExperience.length > 0 && typeof data.extractedData.workExperience[0] === 'object'
          ? cleanEmptyValue(data.extractedData.workExperience[0]?.city) || ''
          : '',
        workCountry: cleanEmptyValue(data.extractedData.preferredWorkLocation) || '',
        position: cleanEmptyValue(data.extractedData.occupation) || cleanEmptyValue(data.extractedData.preferredJobTitle) || '',
        salary: cleanEmptyValue(data.extractedData.salaryExpectation) || '',
        
        // Family Information
        father: typeof data.extractedData.fatherName === 'object' 
          ? cleanEmptyValue(data.extractedData.fatherName.name) || ''
          : cleanEmptyValue(data.extractedData.fatherName) || cleanEmptyValue(data.extractedData.father) || '',
        mother: typeof data.extractedData.motherName === 'object'
          ? cleanEmptyValue(data.extractedData.motherName.name) || ''
          : cleanEmptyValue(data.extractedData.motherName) || cleanEmptyValue(data.extractedData.mother) || '',
        nextOfKinName: typeof data.extractedData.nextOfKin === 'object'
          ? cleanEmptyValue(data.extractedData.nextOfKin.name) || ''
          : cleanEmptyValue(data.extractedData.nextOfKin) || cleanEmptyValue(data.extractedData.emergencyContactName) || '',
        spouse: typeof data.extractedData.spouseName === 'object'
          ? cleanEmptyValue(data.extractedData.spouseName.name) || ''
          : cleanEmptyValue(data.extractedData.spouseName) || cleanEmptyValue(data.extractedData.spouse) || '',
        
        // Skills (handle as array) - Replace existing skills with extracted ones
        allSkills: Array.isArray(data.extractedData.skills) && data.extractedData.skills.length > 0
          ? data.extractedData.skills.map((skill: string) => ({ 
              name: cleanEmptyValue(skill), 
              checked: true, 
              nameArabic: '' 
            })).filter(skill => skill.name !== "")
          : [],
        
        // Languages
        englishLevel: (() => {
          const languages = data.extractedData.languagesSpoken || data.extractedData.languages;
          if (!languages || !Array.isArray(languages)) return '';
          
          const englishEntry = languages.find((lang: any) => {
            if (typeof lang === 'string') {
              return lang.toLowerCase().includes('english');
            } else if (lang && typeof lang === 'object' && lang.language) {
              return lang.language.toLowerCase().includes('english');
            }
            return false;
          });
          
          if (typeof englishEntry === 'string') {
            return englishEntry.split(' - ')[1] || '';
          } else if (englishEntry?.level) {
            return englishEntry.level;
          }
          return '';
        })(),
        
        arabicLevel: (() => {
          const languages = data.extractedData.languagesSpoken || data.extractedData.languages;
          if (!languages || !Array.isArray(languages)) return '';
          
          const arabicEntry = languages.find((lang: any) => {
            if (typeof lang === 'string') {
              return lang.toLowerCase().includes('arabic');
            } else if (lang && typeof lang === 'object' && lang.language) {
              return lang.language.toLowerCase().includes('arabic');
            }
            return false;
          });
          
          if (typeof arabicEntry === 'string') {
            return arabicEntry.split(' - ')[1] || '';
          } else if (arabicEntry?.level) {
            return arabicEntry.level;
          }
          return '';
        })(),
        
        // Additional Information
        medicalHistory: cleanEmptyValue(data.extractedData.medicalConditions) || prev.medicalHistory || '',
        remarks: data.extractedData.additionalNotes ? 
          `📄 Auto-filled from CV: ${new Date().toLocaleString()}\n${cleanEmptyValue(data.extractedData.additionalNotes)}` : 
          prev.remarks,
        
        // Template-specific fields
        complexion: cleanEmptyValue(data.extractedData.complexion) || prev.complexion,
        kinship: cleanEmptyValue(data.extractedData.kinship) || prev.kinship,
        hobbies: Array.isArray(data.extractedData.hobbies) && data.extractedData.hobbies.length > 0
          ? data.extractedData.hobbies.map(h => cleanEmptyValue(h)).filter(h => h !== "").join(", ")
          : cleanEmptyValue(data.extractedData.hobbies) || prev.hobbies,
        
        // Template 3 specific fields
        cvDate: parseAndFormatDate(data.extractedData.cvDate || "") || prev.cvDate || (() => {
          const today = new Date();
          return `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
        })(),
        destination: cleanEmptyValue(data.extractedData.preferredWorkLocation) || prev.destination,
        
        // Company Information (if extracted)
        companyName: cleanEmptyValue(data.extractedData.targetCompany) || '',
        contactNumber: cleanEmptyValue(data.extractedData.contactNumber) || '',
        
        // Keep all other fields empty for clean start
        companyNameArabic: '',
        companyLocation: '',
        positionArabic: '',
        logoFile: null,
        profilePhoto: null,
        fullBodyPhoto: null,
        passportDocument: null,
        idDocument: null,
        qualificationsCertificates: [],
        goodConductCertificates: [],
        otherDocuments: [],
        mediaFiles: [],
        stage: 'Onboarding',
        otherEmergencyInfo: '',
        educationPeriod: '',
        additionalEducationInfo: '',
        contractPeriod: '',
        monthlySalary: '',
        customCompanyName: '',
        customCompanyLocation: '',
        salaryExpectation: '',
        referenceNumber: '',
        passportZoom: 100,
        idZoom: 100,
        qualificationZooms: [],
        conductZooms: [],
        otherZooms: [],
        workerName: cleanEmptyValue(data.extractedData.firstName) || '',
        durationInsurance: '',
        previouslyWorked: '',
        otherExperiences: ''
      }));
      
      // Mark as dirty to trigger save
      markFormDirty();
    } catch (error: any) {
      console.error("❌ Error applying extracted data:", error);
      throw error;
    }
  };

  const mergeDocumentExtractedData = (prev: any, extractedData: any) => {
    const contactNameValue = (value: any) => {
      if (!value) {
        return "";
      }

      if (typeof value === "object" && !Array.isArray(value)) {
        return cleanEmptyValue(value.name) || "";
      }

      return cleanEmptyValue(value) || "";
    };

    const parsedDateOfBirth =
      parseAndFormatDate(
        cleanEmptyValue(extractedData.dob) ||
        cleanEmptyValue(extractedData.dateOfBirth) ||
        cleanEmptyValue(extractedData.birthDate) ||
        ""
      ) || prev.dateOfBirth;

    const parsedIssueDate =
      parseAndFormatDate(
        cleanEmptyValue(extractedData.documentIssueDate) ||
        cleanEmptyValue(extractedData.passportIssueDate) ||
        cleanEmptyValue(extractedData.issueDate) ||
        cleanEmptyValue(extractedData.dateIssued) ||
        ""
      ) || prev.dateIssued;

    const parsedExpiryDate =
      parseAndFormatDate(
        cleanEmptyValue(extractedData.documentExpiryDate) ||
        cleanEmptyValue(extractedData.passportExpiry) ||
        cleanEmptyValue(extractedData.passportExpiryDate) ||
        cleanEmptyValue(extractedData.expiryDate) ||
        cleanEmptyValue(extractedData.dateExpiry) ||
        ""
      ) || prev.dateExpiry;

    const primaryWorkExperience =
      Array.isArray(extractedData.workExperience) && extractedData.workExperience.length > 0
        ? extractedData.workExperience[0]
        : null;

    const extractedSkills =
      Array.isArray(extractedData.skills) && extractedData.skills.length > 0
        ? extractedData.skills
            .map((skill: any) => ({
              name: cleanEmptyValue(skill) || "",
              checked: true,
              nameArabic: "",
            }))
            .filter((skill: any) => skill.name !== "")
        : prev.allSkills;

    return {
      ...prev,
      fullName: cleanEmptyValue(extractedData.fullName) || prev.fullName,
      fullNameArabic: cleanEmptyValue(extractedData.fullNameArabic) || prev.fullNameArabic,
      firstName: cleanEmptyValue(extractedData.firstName) || prev.firstName,
      lastName: cleanEmptyValue(extractedData.lastName) || prev.lastName,
      middleName: cleanEmptyValue(extractedData.middleName) || prev.middleName,
      passportNo: cleanEmptyValue(extractedData.passportNumber) || prev.passportNo,
      idNumber: cleanEmptyValue(extractedData.idNumber) || prev.idNumber,
      dateIssued: parsedIssueDate,
      dateExpiry: parsedExpiryDate,
      placeIssued:
        cleanEmptyValue(extractedData.placeOfIssue) ||
        cleanEmptyValue(extractedData.passportPlaceOfIssue) ||
        cleanEmptyValue(extractedData.placeIssued) ||
        prev.placeIssued,
      address:
        cleanEmptyValue(extractedData.address) ||
        cleanEmptyValue(extractedData.currentAddress) ||
        prev.address,
      nationality: cleanEmptyValue(extractedData.nationality) || prev.nationality,
      religion: cleanEmptyValue(extractedData.religion) || prev.religion,
      dateOfBirth: parsedDateOfBirth,
      placeOfBirth: cleanEmptyValue(extractedData.placeOfBirth) || prev.placeOfBirth,
      father:
        contactNameValue(extractedData.fatherName) ||
        contactNameValue(extractedData.father) ||
        contactNameValue(extractedData.fathersName) ||
        prev.father,
      mother:
        contactNameValue(extractedData.motherName) ||
        contactNameValue(extractedData.mother) ||
        contactNameValue(extractedData.mothersName) ||
        prev.mother,
      age: cleanEmptyValue(extractedData.age) || prev.age,
      height: cleanEmptyValue(extractedData.height) || prev.height,
      weight: cleanEmptyValue(extractedData.weight) || prev.weight,
      gender: cleanEmptyValue(extractedData.gender) || prev.gender,
      civilStatus:
        cleanEmptyValue(extractedData.maritalStatus) ||
        cleanEmptyValue(extractedData.civilStatus) ||
        prev.civilStatus,
      numberOfKids: cleanEmptyValue(extractedData.children) || prev.numberOfKids,
      contact:
        cleanEmptyValue(extractedData.phoneNumber) ||
        cleanEmptyValue(extractedData.contactNumber) ||
        prev.contact,
      email:
        cleanEmptyValue(extractedData.email) ||
        cleanEmptyValue(extractedData.emailAddress) ||
        prev.email,
      emergencyContact:
        cleanEmptyValue(extractedData.emergencyContact?.phoneNumber) ||
        cleanEmptyValue(extractedData.emergencyContactNumber) ||
        prev.emergencyContact,
      nextOfKinName:
        cleanEmptyValue(extractedData.nextOfKin?.name) ||
        contactNameValue(extractedData.nextOfKin) ||
        cleanEmptyValue(extractedData.emergencyContactName) ||
        prev.nextOfKinName,
      spouse:
        contactNameValue(extractedData.spouseName) ||
        contactNameValue(extractedData.spouse) ||
        prev.spouse,
      educationLevel:
        cleanEmptyValue(extractedData.educationLevel) ||
        cleanEmptyValue(extractedData.education) ||
        prev.educationLevel,
      workPosition:
        (typeof primaryWorkExperience === "object"
          ? cleanEmptyValue(primaryWorkExperience?.position)
          : cleanEmptyValue(primaryWorkExperience)) ||
        cleanEmptyValue(extractedData.occupation) ||
        cleanEmptyValue(extractedData.preferredJobTitle) ||
        prev.workPosition,
      workCompany:
        (typeof primaryWorkExperience === "object"
          ? cleanEmptyValue(primaryWorkExperience?.company)
          : "") ||
        cleanEmptyValue(extractedData.employer) ||
        prev.workCompany,
      workYears:
        (typeof primaryWorkExperience === "object"
          ? cleanEmptyValue(primaryWorkExperience?.duration)
          : "") ||
        prev.workYears,
      workCity:
        (typeof primaryWorkExperience === "object"
          ? cleanEmptyValue(primaryWorkExperience?.city)
          : "") ||
        prev.workCity,
      workCountry:
        cleanEmptyValue(extractedData.preferredWorkLocation) ||
        prev.workCountry,
      position:
        cleanEmptyValue(extractedData.occupation) ||
        cleanEmptyValue(extractedData.preferredJobTitle) ||
        prev.position,
      allSkills: extractedSkills,
      hobbies:
        Array.isArray(extractedData.hobbies) && extractedData.hobbies.length > 0
          ? extractedData.hobbies
              .map((hobby: any) => cleanEmptyValue(hobby))
              .filter((hobby: string) => hobby !== "")
              .join(", ")
          : cleanEmptyValue(extractedData.hobbies) || prev.hobbies,
    };
  };


  // Load candidate data and draft data - Firestore first, then localStorage for drafts
  useEffect(() => {
    const loadData = async () => {
      try {
        // 1. Check if we're in edit-draft or view-cv mode from Documents page (CV draft viewing/editing)
        if ((location.state?.mode === 'edit-draft' || location.state?.mode === 'view-cv') && location.state?.candidate) {
          console.log('📄 Loading CV draft from Documents page');
          console.log('🔍 Location state:', location.state);
          console.log('📊 Candidate data received:', location.state.candidate);
          const candidateData = location.state.candidate;
          const template = location.state.template || 'template2';
          
          // Set the template
          setSelectedTemplate(template);
          
          // Check if candidateData has formData nested inside (from CV draft)
          const formDataToLoad = candidateData.formData || candidateData;
          
          // If we're editing a draft, set the draft ID reference
          if (location.state?.mode === 'edit-draft' && candidateData.id) {
            manualDraftIdRef.current = candidateData.id;
            console.log('📋 Initialized manual draft ID:', candidateData.id);
          }
          
          // Load all the CV draft data into the form
          if (candidateData.formData) {
            // If we have formData directly from the draft, use it as is
            console.log('📋 Loading formData directly from draft');
            setFormData(candidateData.formData);
          } else {
            // Otherwise map the fields from the candidateData structure
            console.log('🔄 Mapping fields from candidate data');
            setFormData(prev => ({
              ...prev,
              // Basic Information
              fullName: candidateData.name || candidateData.fullName || '',
              fullNameArabic: candidateData.fullNameArabic || '',
              firstName: candidateData.firstName || '',
              lastName: candidateData.lastName || '',
              middleName: candidateData.middleName || '',
              
              // Personal Details
              dateOfBirth: candidateData.dateOfBirth || '',
              age: candidateData.age || '',
              gender: candidateData.gender || '',
              civilStatus: candidateData.civilStatus || candidateData.maritalStatus || '',
              nationality: candidateData.nationality || '',
              religion: candidateData.religion || '',
              height: candidateData.height || '',
              weight: candidateData.weight || '',
              
              // Contact Information
              address: candidateData.address || candidateData.city || '',
              contact: candidateData.contact || candidateData.phone || '',
              email: candidateData.email || '',
              emergencyContact: candidateData.emergencyContact || '',
              numberOfKids: candidateData.numberOfKids || candidateData.children || '',
              
              // Documents
              passportNo: candidateData.passportNumber || candidateData.passportNo || '',
              idNumber: candidateData.idNumber || '',
              dateIssued: candidateData.dateIssued || '',
              dateExpiry: candidateData.dateExpiry || '',
              placeOfBirth: candidateData.placeOfBirth || '',
              placeIssued: candidateData.placeIssued || '',
              
              // Professional Information
              position: candidateData.jobTitle || candidateData.position || '',
              positionArabic: candidateData.positionArabic || '',
              salary: candidateData.salary || '',
              workCompany: candidateData.workCompany || candidateData.company || '',
              workPosition: candidateData.workPosition || '',
              workYears: candidateData.workYears || '',
              workCity: candidateData.workCity || '',
              workCountry: candidateData.workCountry || candidateData.countryApplyingTo || '',
              
              // Education
              educationLevel: candidateData.educationLevel || '',
              educationPeriod: candidateData.educationPeriod || '',
              additionalEducationInfo: candidateData.additionalEducationInfo || '',
              
              // Family Information
              father: candidateData.father || '',
              mother: candidateData.mother || '',
              spouse: candidateData.spouse || '',
              nextOfKinName: candidateData.nextOfKinName || '',
              kinship: candidateData.kinship || '',
              
              // Language Skills
              englishLevel: candidateData.englishLevel || '',
              arabicLevel: candidateData.arabicLevel || '',
              
              // Additional Information
              medicalHistory: candidateData.medicalHistory || '',
              remarks: candidateData.notes || candidateData.remarks || '',
              complexion: candidateData.complexion || '',
              hobbies: candidateData.hobbies || '',
              
              // Company Information
              companyName: candidateData.companyName || candidateData.company || '',
              companyNameArabic: candidateData.companyNameArabic || '',
              companyLocation: candidateData.companyLocation || '',
              contactNumber: candidateData.contactNumber || '',
              
              // CV-specific fields
              cvDate: candidateData.cvDate || new Date().toISOString().split('T')[0],
              referenceNumber: candidateData.referenceNumber || '',
              destination: candidateData.destination || '',
              otherEmergencyInfo: candidateData.otherEmergencyInfo || '',
              workerName: candidateData.workerName || candidateData.firstName || candidateData.name || '',
              durationInsurance: candidateData.durationInsurance || candidateData.contractPeriod || '',
              previouslyWorked: candidateData.previouslyWorked || candidateData.workCountry || '',
              otherExperiences: candidateData.otherExperiences || '',
              stage: candidateData.stage || 'CV Processed',
              
              // Skills (if available)
              allSkills: candidateData.allSkills || prev.allSkills || [],
              
              // Any other fields that might exist
              ...Object.keys(candidateData).reduce((acc, key) => {
                // Only include fields that aren't already mapped above
                if (!['id', '_id', 'name', 'fullName', 'email', 'emailAddress', 'phone', 
                      'contactNumber', 'passportNumber', 'jobTitle', 'city', 'country',
                      'countryApplyingTo', 'notes'].includes(key)) {
                  // Check if this key exists in the form structure
                  if (prev.hasOwnProperty(key)) {
                    acc[key] = candidateData[key];
                  }
                }
                return acc;
              }, {})
            }));
          }
          
          console.log('✅ CV draft data loaded successfully');
          
          // Show a notification
          showNotification({
            type: 'success',
            title: location.state?.mode === 'edit-draft' ? 'CV Draft Loaded' : 'CV Loaded',
            message: location.state?.mode === 'edit-draft' 
              ? 'CV draft data has been loaded. You can now continue editing where you left off.'
              : 'CV data has been loaded for viewing.',
            duration: 5000
          });
          
          return; // Exit early, we've loaded the data
        }
        
        // 2. Check if candidate data was passed via navigation (from candidates page)
        const candidateFromNav = location.state?.candidate;
        if (candidateFromNav && !location.state?.mode) {
          console.log('📄 Loading candidate with multi-CV support:', candidateFromNav.name);
          
          // Load candidate with all CV versions
          const candidateWithCVs = await getCandidateWithCVs(candidateFromNav.id || candidateFromNav._id);
          if (candidateWithCVs) {
            setCurrentCandidate(candidateWithCVs);
            setCandidateCVs(candidateWithCVs.cvs || []);
            
            // If candidate has multiple CVs, show selector
            if (candidateWithCVs.cvs && candidateWithCVs.cvs.length > 1) {
              setShowCVSelector(true);
              // Load the most recent CV by default
              setSelectedCVVersion(candidateWithCVs.cvs[0].id);
              loadCVVersion(candidateWithCVs.cvs[0]);
            } else if (candidateWithCVs.cvs && candidateWithCVs.cvs.length === 1) {
              // Load the single CV version
              setSelectedCVVersion(candidateWithCVs.cvs[0].id);
              loadCVVersion(candidateWithCVs.cvs[0]);
            } else {
              // No CVs, load basic candidate data
              loadBasicCandidateData(candidateWithCVs);
            }
          } else {
            // Fallback to basic candidate data
            loadBasicCandidateData(candidateFromNav);
          }
        } else {
          // 2. Check for different viewing modes and handle accordingly
          const cvBuilderMode = localStorage.getItem('cvBuilderMode');
          const savedCandidateData = localStorage.getItem('candidateFormData');
          
          if (savedCandidateData) {
            try {
              const candidateData = JSON.parse(savedCandidateData);
              setFormData(prev => ({
                ...prev,
                ...candidateData
              }));
              
              console.log(`📄 Loaded candidate data in ${cvBuilderMode || 'default'} mode`);
              
              // Handle different modes
              if (cvBuilderMode === 'edit-draft') {
                const currentDraftId = localStorage.getItem('currentDraftId');
                console.log('✏️ Editing draft mode - Draft ID:', currentDraftId);
                // Keep the draft data as is for editing
              } else if (cvBuilderMode === 'view-only') {
                console.log('👁️ View-only mode - CV locked for viewing');
                // Could add read-only state here if needed
              } else if (cvBuilderMode === 'view-cv') {
                const currentCandidateId = localStorage.getItem('currentCandidateId');
                console.log('👁️ View CV mode - Candidate ID:', currentCandidateId);
                // Viewing existing CV for a candidate
              }
              
              // Clear temporary localStorage items after loading
              localStorage.removeItem('candidateFormData');
              localStorage.removeItem('cvBuilderMode');
              localStorage.removeItem('currentDraftId');
              localStorage.removeItem('currentCandidateId');
              
            } catch (error) {
              console.error('Error loading candidate data from localStorage:', error);
            }
          }
        }

        // 3. Load template selection if available
        const savedTemplate = localStorage.getItem('cvBuilderTemplate');
        if (savedTemplate) {
          setSelectedTemplate(savedTemplate);
          localStorage.removeItem('cvBuilderTemplate');
        }

        // 4. Load current draft data - Always load to restore form state
        const loadCurrentDraft = () => {
          try {
            const currentDraft = LocalStorageManager.getCurrentCVDraft();
            if (currentDraft && currentDraft.formData) {
              // If we have candidate data from navigation, merge it with the draft
              if (candidateFromNav || localStorage.getItem('candidateFormData')) {
                console.log('📄 Merging draft with candidate data...');
                // Keep the existing candidate data but restore any additional fields from draft
                setFormData(prev => {
                  // Merge draft data but prioritize navigation data for key fields
                  const mergedData = { ...currentDraft.formData };
                  
                  // Preserve key fields from navigation if they exist
                  if (prev.fullName) mergedData.fullName = prev.fullName;
                  if (prev.passportNo) mergedData.passportNo = prev.passportNo;
                  if (prev.email) mergedData.email = prev.email;
                  if (prev.contact) mergedData.contact = prev.contact;
                  
                  return { ...prev, ...mergedData };
                });
              } else {
                // No navigation data, load the full draft
                setFormData(currentDraft.formData);
                setSelectedTemplate(currentDraft.template || selectedTemplate);
                console.log('✅ Restored CV draft from localStorage');
              }
              
              // Show restoration message
              showNotification({
                type: 'info',
                title: 'Draft Restored',
                message: 'Your previous CV draft has been restored.',
                duration: 3000
              });
            }
          } catch (error) {
            console.error('Error loading CV draft:', error);
          }
        };
        
        // Always attempt to load draft to ensure persistence
        loadCurrentDraft();
      } catch (error) {
        console.error('Error in data loading:', error);
      }
    };

    loadData();
  }, [location.state]);

  // Add CSS to prevent scroll jumping
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      html, body {
        scroll-behavior: auto !important;
      }
      * {
        scroll-behavior: auto !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Removed auto-save functionality
  /*
  // Auto-save setup - monitor form changes with debounce
  useEffect(() => {
    // Auto-save functionality removed
  }, [formData]);
  */

  // Removed auto-save functionality
  /*
  // Save draft before page unload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Auto-save functionality removed
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
  */

  // Removed auto-save before page unload
  /*
  useEffect(() => {
    // Auto-save functionality removed
  }, [formData, selectedTemplate, currentCandidate]);
  */

  // Save data on page unload/refresh (preserves uploads and form data)
  useEffect(() => {
    const saveOnUnload = () => {
      try {
        // Only save text data, not files during unload
        const textOnlyData = {
          companyName: formData.companyName,
          companyNameArabic: formData.companyNameArabic,
          companyLocation: formData.companyLocation,
          contactNumber: formData.contactNumber,
          position: formData.position,
          positionArabic: formData.positionArabic,
          salary: formData.salary,
          passportNo: formData.passportNo,
          dateIssued: formData.dateIssued,
          dateExpiry: formData.dateExpiry,
          placeIssued: formData.placeIssued,
          fullName: formData.fullName,
          fullNameArabic: formData.fullNameArabic,
          address: formData.address,
          nationality: formData.nationality,
          religion: formData.religion,
          dateOfBirth: formData.dateOfBirth,
          placeOfBirth: formData.placeOfBirth,
          father: formData.father,
          mother: formData.mother,
          age: formData.age,
          height: formData.height,
          weight: formData.weight,
          civilStatus: formData.civilStatus,
          spouse: formData.spouse,
          numberOfKids: formData.numberOfKids,
          educationLevel: formData.educationLevel,
          englishLevel: formData.englishLevel,
          arabicLevel: formData.arabicLevel,
          workPosition: formData.workPosition,
          workCountry: formData.workCountry,
          workYears: formData.workYears,
          workerName: formData.workerName,
          durationInsurance: formData.durationInsurance,
          previouslyWorked: formData.previouslyWorked,
          otherExperiences: formData.otherExperiences,
          allSkills: formData.allSkills,
          remarks: formData.remarks,
          // Fields for Candidates page correspondence (Other Information section)
          contact: formData.contact,
          email: formData.email,
          emergencyContact: formData.emergencyContact,
          workCompany: formData.workCompany,
          workCity: formData.workCity,
          stage: formData.stage,
          // Keep file references for persistence
          logoFile: formData.logoFile,
          profilePhoto: formData.profilePhoto,
          fullBodyPhoto: formData.fullBodyPhoto,
          passportDocument: formData.passportDocument,
          idDocument: formData.idDocument,
          qualificationsCertificates: formData.qualificationsCertificates,
          goodConductCertificates: formData.goodConductCertificates,
          otherDocuments: formData.otherDocuments,
          mediaFiles: formData.mediaFiles,
        };
        
        // Save using LocalStorageManager (no await in unload handler to avoid blocking)
        LocalStorageManager.saveCurrentCVDraft(textOnlyData, selectedTemplate);
        console.log('💾 Data saved on page unload');
      } catch (error) {
        console.error('Error saving on unload:', error);
      }
    };

    window.addEventListener('beforeunload', saveOnUnload);
    window.addEventListener('pagehide', saveOnUnload);

    return () => {
      window.removeEventListener('beforeunload', saveOnUnload);
      window.removeEventListener('pagehide', saveOnUnload);
    };
  }, [formData, selectedTemplate]);


  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      // Reset the draft IDs when unmounting
      autosaveDraftIdRef.current = null;
      manualDraftIdRef.current = null;
    };
  }, []);

  // Reset draft IDs when candidate changes
  useEffect(() => {
    // Reset the draft IDs when the current candidate changes
    autosaveDraftIdRef.current = null;
    manualDraftIdRef.current = null;
  }, [currentCandidate?.id]);

  // Handle CV Builder actions from Dashboard and live sync events
  useEffect(() => {
    const handleCVBuilderAction = (e: CustomEvent) => {
      if (e.detail?.action === 'autoSelectFileUpload') {
        // Find and trigger the Quick autofill file upload input
        setTimeout(() => {
          const fileInput = document.getElementById('quick-autofill-upload') as HTMLInputElement;
          if (fileInput) {
            fileInput.focus();
            fileInput.click();
          }
        }, 200);
      }
    };

    // Live sync event handlers
    const handleDataUpdates = (event: CustomEvent) => {
      console.log('📄 CV Builder: Data update event received:', event.type);
      // If candidate data is updated externally, we may need to refresh any candidate selection dropdowns
      // The form data itself doesn't need to change, but candidate-related fields might
    };

    const handleStorageChange = (event: StorageEvent) => {
      console.log('📄 CV Builder: Storage change event received:', event.key);
      // Handle localStorage changes from other tabs/windows
      if (event.key === 'recruitment_crm_candidates' || event.key === null) {
        // Refresh candidate-related data if needed
        console.log('📄 CV Builder: Candidate data updated in storage');
      }
    };

    // Add event listeners
    window.addEventListener('cvBuilderAction', handleCVBuilderAction as EventListener);
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('candidatesUpdated', handleDataUpdates as EventListener);
    window.addEventListener('candidateAdded', handleDataUpdates as EventListener);
    window.addEventListener('candidateUpdated', handleDataUpdates as EventListener);
    window.addEventListener('candidateDeleted', handleDataUpdates as EventListener);
    window.addEventListener('candidateStatusChanged', handleDataUpdates as EventListener);
    
    return () => {
      console.log('📄 CV Builder: Cleaning up event listeners...');
      window.removeEventListener('cvBuilderAction', handleCVBuilderAction as EventListener);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('candidatesUpdated', handleDataUpdates as EventListener);
      window.removeEventListener('candidateAdded', handleDataUpdates as EventListener);
      window.removeEventListener('candidateUpdated', handleDataUpdates as EventListener);
      window.removeEventListener('candidateDeleted', handleDataUpdates as EventListener);
      window.removeEventListener('candidateStatusChanged', handleDataUpdates as EventListener);
    };
  }, []);

  // Function for saving draft
  const saveDraft = async () => {
    try {
      // Auto-save current state using LocalStorageManager
      LocalStorageManager.saveCurrentCVDraft(formData, selectedTemplate);
      
      // Create a consistent draft name (without changing date)
      const candidateName = formData.fullName || 'Unknown Candidate';
      const baseDraftName = `${candidateName} CV - Manual Draft`;
      
      // Use existing manual draft ID if available
      const draftId = manualDraftIdRef.current;
      
      const draftToSave: any = {
        name: baseDraftName,
        candidateName: candidateName,
        candidateId: currentCandidate?.id || formData.fullName || 'unknown',
        formData: formData,
        template: selectedTemplate,
        description: `CV draft for ${candidateName} - Last updated: ${new Date().toLocaleString()}`,
        content: JSON.stringify(formData),
        uploadedBy: 'CV Builder',
        size: JSON.stringify(formData).length,
        category: 'cv-drafts',
        type: 'cv-draft',
        url: '#',
        uploadedAt: new Date().toISOString()
      };
      
      // Only add ID if we have a valid Firebase ID
      if (draftId && !draftId.startsWith('manual_')) {
        draftToSave.id = draftId;
      }
      
      // Save to Firebase for live sync
      try {
        const docId = await addCVDraftToFirebase(draftToSave);
        // Update the ref with the actual Firebase ID for future updates
        if (docId) {
          manualDraftIdRef.current = docId;
        }
        console.log('✅ CV draft saved to Firebase with ID:', docId);
        
        // Also update the local draft with the Firebase ID
        const updatedDraft = { ...draftToSave, id: docId };
        LocalStorageManager.addCVDraft(updatedDraft);
      } catch (firebaseError) {
        console.error('❌ Failed to save to Firebase, saving locally:', firebaseError);
        // Fall back to local storage if Firebase fails
        LocalStorageManager.addCVDraft(draftToSave);
      }
      
      showNotification({
        type: 'success',
        title: 'Draft Saved',
        message: `CV draft saved successfully! You can find it in the Documents page under CVs section.`,
        duration: 5000
      });
      console.log('✅ CV draft saved:', baseDraftName);

      // Dispatch custom event to update Documents page
      window.dispatchEvent(new CustomEvent('cvDraftsUpdated', {
        detail: { message: 'CV draft saved' }
      }));
    } catch (error: any) {
      console.error('❌ Error saving CV draft:', error);
      showNotification({
        type: 'error',
        title: 'Save Failed',
        message: 'Failed to save CV draft. Please try again.',
        duration: 5000
      });
    }
  };

  // Function for saving CV data as candidate
  const saveCandidateFromCV = async () => {
    try {
      // Map CV form data to Candidate interface
      const candidateData = {
        name: formData.fullName || 'Unknown Candidate',
        email: '', // CV builder doesn't collect email, will be empty
        phone: '', // CV builder doesn't collect phone, will be empty
        passportNumber: formData.passportNo,
        jobTitle: formData.position,
        company: formData.companyName,
        salary: parseFloat(formData.salary?.replace(/[^0-9.]/g, '') || '0'),
        countryApplyingTo: formData.workCountry,
        city: formData.address,
        stage: 'Onboarding' as const,
        lastContacted: new Date().toISOString(),
        notes: formData.remarks,
        // Extended fields from CV Builder
        fullNameArabic: formData.fullNameArabic,
        address: formData.address,
        nationality: formData.nationality,
        religion: formData.religion,
        dateOfBirth: formData.dateOfBirth,
        placeOfBirth: formData.placeOfBirth,
        father: formData.father,
        mother: formData.mother,
        age: formData.age,
        height: formData.height,
        weight: formData.weight,
        civilStatus: formData.civilStatus,
        spouse: formData.spouse,
        numberOfKids: formData.numberOfKids,
        educationLevel: formData.educationLevel,
        englishLevel: formData.englishLevel,
        arabicLevel: formData.arabicLevel,
        workPosition: formData.workPosition,
        workCountry: formData.workCountry,
        workYears: formData.workYears,
        dateIssued: formData.dateIssued,
        dateExpiry: formData.dateExpiry,
        placeIssued: formData.placeIssued,
        skills: formData.allSkills.filter(skill => skill.checked).map(skill => skill.name),
        remarks: formData.remarks,
        profilePhoto: formData.profilePhoto || ''
      };
      
      // Add candidate using Firebase service for sync
      const { addCandidate } = await import('../services/candidateService');
      const newCandidateId = await addCandidate(candidateData);

      showNotification({
        type: 'success',
        title: 'Candidate Saved',
        message: `Candidate "${candidateData.name}" has been added to candidates list successfully!`,
        duration: 5000
      });
      console.log('✅ Saved candidate from CV Builder with ID:', newCandidateId);
    } catch (error: any) {
      console.error('❌ Error saving candidate from CV:', error);
      showNotification({
        type: 'error',
        title: 'Save Failed',
        message: 'Failed to save candidate. Please try again.',
        duration: 5000
      });
    }
  };

  // OPTIMIZED: Function to handle input changes with minimal flicker
  // Handle document autofill upload

  const handleInputChange = useCallback((field: keyof typeof formData, value: string) => {
    setFormData(prev => {
      // Skip state updates if nothing changed to reduce extra rerenders on mobile.
      if (prev[field] === value) return prev;
      return {
        ...prev,
        [field]: value,
      };
    });

    // Mark form as dirty to trigger auto-save.
    markFormDirty();

    // Clear validation errors only when needed.
    setValidationErrors(prev => (prev.length > 0 ? [] : prev));
  }, [markFormDirty]);

  // Function to handle file changes
  const handleFileChange = (field: keyof typeof formData, file: File | null) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target && e.target.result) {
          const result = e.target.result;
          
          setFormData(prev => {
            const newFormData = {
              ...prev,
              [field]: result,
            };
            
            // Trigger auto-save for file changes
            markFormDirty();
            
            return newFormData;
          });
          
          // Clear validation errors when required files are uploaded
          if (validationErrors.length > 0) {
            setValidationErrors([]);
          }
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Function to handle file deletion
  const handleFileDelete = (field: keyof typeof formData) => {
    setFormData(prev => {
      const newFormData = {
        ...prev,
        [field]: null
      };
      
      // Trigger auto-save for file deletions
      markFormDirty();
      
      return newFormData;
    });
  };

  // Function to handle document uploads (single files)
  const handleDocumentUpload = async (field: 'passportDocument' | 'idDocument', file: File | null) => {
    if (file) {
      // First, read the file for local storage
      const reader = new FileReader();
      reader.onload = async (e) => {
        if (e.target && e.target.result) {
          const result = e.target.result;
          
          setFormData(prev => {
            const newFormData = {
              ...prev,
              [field]: {
                name: file.name,
                type: file.type,
                data: result,
                size: file.size
              },
              [`${field === 'passportDocument' ? 'passport' : 'id'}Zoom`]: 100
            };
            
            return newFormData;
          });

          // Send file to n8n for extraction
          try {
            setUploading(true);
            const formDataUpload = new FormData();
            formDataUpload.append('file', file);
            
            // Get candidate ID from local storage or generate temporary one
            const candidateId = localStorage.getItem('currentCandidateId') || 'temp-' + Date.now();
            
            console.log("📤 Uploading document to webhook:", webhookUrl);
            
            const response = await axios.post(
              webhookUrl,
              formDataUpload,
              {
                headers: {
                  // Let axios set the Content-Type with boundary for FormData
                  'Accept': 'application/json'
                },
                params: {
                  candidateId: candidateId
                },
                withCredentials: false,
                timeout: 30000 // 30 second timeout
              }
            );

            console.log("📥 Document upload response:", response.data);

            // Handle response - check if it's empty
            if (!response.data || Object.keys(response.data).length === 0) {
              console.warn("⚠️ Empty response from webhook for document upload");
              showNotification({
                type: 'warning',
                title: 'Document Uploaded',
                message: 'Document uploaded but no data was extracted. Please check the n8n workflow configuration.',
                duration: 5000
              });
            } else if (response.data.success === false) {
              showNotification({
                type: 'warning',
                title: 'No Data Extracted',
                message: response.data.error || response.data.message || 'Document uploaded but no data could be extracted from this file.',
                duration: 6000
              });
            } else if (response.data.extractedData) {
              setFormData(prev => mergeDocumentExtractedData(prev, response.data.extractedData));

              showNotification({
                type: 'success',
                title: 'Document Processed',
                message: 'Document uploaded and available fields were auto-filled successfully.',
                duration: 5000
              });
            } else {
              showNotification({
                type: 'warning',
                title: 'Document Uploaded',
                message: 'Document uploaded but the extraction response was incomplete.',
                duration: 5000
              });
            }
          } catch (error: any) {
            console.error('❌ Error processing document:', error);
            
            let errorMessage =
              error.response?.data?.error ||
              error.response?.data?.message ||
              'Failed to process document. Please try again.';
            
            if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
              errorMessage = 'Unable to connect to the extraction service. Please check your internet connection.';
            } else if (error.response?.status === 0) {
              errorMessage = 'CORS error: The n8n webhook needs to be configured to accept requests from your domain.';
            }
            
            showNotification({
              type: 'error',
              title: 'Processing Failed',
              message: errorMessage,
              duration: 5000
            });
          } finally {
            setUploading(false);
          }
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Function to handle multiple document uploads (arrays)
  const handleMultipleDocumentUpload = (field: 'qualificationsCertificates' | 'goodConductCertificates' | 'otherDocuments', files: FileList | null) => {
    if (files) {
      const fileArray = Array.from(files);
      const processedFiles: any[] = [];
      let processed = 0;

      fileArray.forEach((file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          if (e.target && e.target.result) {
            processedFiles.push({
              name: file.name,
              type: file.type,
              data: e.target.result,
              size: file.size
            });
            processed++;
            
            if (processed === fileArray.length) {
              setFormData(prev => {
                const zoomField = field === 'qualificationsCertificates' ? 'qualificationZooms' : 
                                field === 'goodConductCertificates' ? 'conductZooms' : 'otherZooms';
                const newZooms = new Array(processedFiles.length).fill(100);
                const newFormData = {
                  ...prev,
                  [field]: [...(prev[field] as any[]), ...processedFiles],
                  [zoomField]: [...(prev[zoomField] as number[]), ...newZooms]
                };
                
                
                return newFormData;
              });
            }
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  // Function to handle camera capture
  const handleCameraCapture = async (field: 'passportDocument' | 'idDocument' | 'profilePhoto' | 'fullBodyPhoto' | string) => {
    try {
      // Create a file input with camera capture
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.capture = 'environment'; // Use back camera by default
      
      input.onchange = (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          // Process the captured image
          if (field === 'passportDocument' || field === 'idDocument') {
            handleDocumentUpload(field as 'passportDocument' | 'idDocument', file);
          } else if (field === 'profilePhoto' || field === 'fullBodyPhoto') {
            handleFileChange(field as 'profilePhoto' | 'fullBodyPhoto', file);
          } else {
            // Handle other document types
            const fileList = new DataTransfer();
            fileList.items.add(file);
            handleMultipleDocumentUpload(field as any, fileList.files);
          }
        }
      };
      
      input.click();
    } catch (error) {
      console.error('Camera capture error:', error);
      alert('Camera capture failed. Please try using the file upload option.');
    }
  };

  // Function to handle media uploads
  const handleMediaUpload = (files: FileList | null) => {
    if (files) {
      const fileArray = Array.from(files);
      const processedFiles: any[] = [];
      let processed = 0;

      fileArray.forEach((file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          if (e.target && e.target.result) {
            processedFiles.push({
              name: file.name,
              type: file.type,
              data: e.target.result,
              size: file.size
            });
            processed++;
            
            if (processed === fileArray.length) {
              setFormData(prev => {
                const newFormData = {
                  ...prev,
                  mediaFiles: [...prev.mediaFiles, ...processedFiles],
                };
                
                
                return newFormData;
              });
            }
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  // Function to remove uploaded documents
  const removeDocument = (field: 'qualificationsCertificates' | 'goodConductCertificates' | 'otherDocuments' | 'mediaFiles', index: number) => {
    setFormData(prev => {
      const zoomField = field === 'qualificationsCertificates' ? 'qualificationZooms' : 
                      field === 'goodConductCertificates' ? 'conductZooms' : 
                      field === 'otherDocuments' ? 'otherZooms' : null;
      
      const updates: any = {
        ...prev,
        [field]: (prev[field] as any[]).filter((_, i) => i !== index),
      };
      
      if (zoomField) {
        updates[zoomField] = (prev[zoomField] as number[]).filter((_, i) => i !== index);
      }
      
      
      return updates;
    });
  };

  // Function to handle zoom level changes
  const handleZoomChange = (type: 'passport' | 'id', zoom: number) => {
    setFormData(prev => {
      const newFormData = {
        ...prev,
        [`${type}Zoom`]: zoom
      };
      
      
      return newFormData;
    });
  };

  const handleMultipleZoomChange = (field: 'qualificationZooms' | 'conductZooms' | 'otherZooms', index: number, zoom: number) => {
    setFormData(prev => {
      const newFormData = {
        ...prev,
        [field]: (prev[field] as number[]).map((z, i) => i === index ? zoom : z)
      };
      
      
      return newFormData;
    });
  };

  // Enhanced English to Arabic translation function with comprehensive job-specific skills
  const getArabicTranslation = (englishSkill: string): string => {
    if (!englishSkill || typeof englishSkill !== 'string') {
      return '';
    }

    const allTranslations: { [key: string]: string } = {
      // Default skills
      'ARABIC DISH COOKING': 'طبخ',
      'CLEANING': 'تنظيف',
      'WASHING': 'غسيل',
      'IRONING': 'كي الملابس',
      'BABYSITTING': 'رعاية الأطفال',
      'CARING ELDERS': 'رعاية كبار سن',
      
      // HOUSEMAID SKILLS
      'COOKING': 'طبخ',
      'KITCHEN WORK': 'العمل في المطبخ',
      'LAUNDRY': 'غسيل الملابس',
      'WASHING CLOTHES': 'غسيل الملابس',
      'FOLDING CLOTHES': 'طي الملابس',
      'DISHWASHING': 'غسيل الأطباق',
      'HOUSE CLEANING': 'تنظيف المنزل',
      'FLOOR CLEANING': 'تنظيف الأرضيات',
      'WINDOW CLEANING': 'تنظيف النوافذ',
      'BATHROOM CLEANING': 'تنظيف الحمام',
      'DUSTING': 'تنظيف الغبار',
      'VACUUMING': 'استخدام المكنسة الكهربائية',
      'MOPPING': 'مسح الأرضيات',
      'SWEEPING': 'كنس',
      'TIDYING': 'ترتيب',
      'BED MAKING': 'ترتيب الأسرة',
      'GROCERY SHOPPING': 'شراء البقالة',
      
      // WAITER/WAITRESS SKILLS
      'CUSTOMER SERVICE': 'خدمة العملاء',
      'ORDER TAKING': 'أخذ الطلبات',
      'SERVING FOOD': 'تقديم الطعام',
      'TABLE SETTING': 'ترتيب المائدة',
      'MENU KNOWLEDGE': 'معرفة قائمة الطعام',
      'CASH HANDLING': 'التعامل مع النقود',
      'FOOD SAFETY': 'سلامة الغذاء',
      'MULTITASKING': 'تعدد المهام',
      'WINE SERVICE': 'خدمة النبيذ',
      'RESTAURANT ETIQUETTE': 'آداب المطعم',
      'HOSPITALITY': 'الضيافة',
      
      // BARISTA SKILLS
      'COFFEE MAKING': 'صنع القهوة',
      'ESPRESSO PREPARATION': 'تحضير الإسبريسو',
      'LATTE ART': 'فن اللاتيه',
      'MILK STEAMING': 'تبخير الحليب',
      'COFFEE MACHINE OPERATION': 'تشغيل آلة القهوة',
      'BEVERAGE PREPARATION': 'تحضير المشروبات',
      'INVENTORY CONTROL': 'مراقبة المخزون',
      'POS SYSTEM': 'نظام نقاط البيع',
      
      // DRIVER SKILLS  
      'DRIVING': 'القيادة',
      'DEFENSIVE DRIVING': 'القيادة الدفاعية',
      'VEHICLE MAINTENANCE': 'صيانة المركبات',
      'ROUTE PLANNING': 'تخطيط المسارات',
      'GPS NAVIGATION': 'الملاحة بالـ GPS',
      'TRAFFIC RULES': 'قوانين المرور',
      'PARKING': 'الوقوف',
      'FUEL EFFICIENCY': 'كفاءة الوقود',
      'CARGO LOADING': 'تحميل البضائع',
      'DELIVERY': 'التوصيل',
      
      // TRUCK DRIVER SKILLS
      'HEAVY VEHICLE OPERATION': 'قيادة المركبات الثقيلة',
      'CARGO SECURING': 'تأمين البضائع',
      'LONG DISTANCE DRIVING': 'القيادة لمسافات طويلة',
      'TRAILER BACKING': 'الرجوع بالمقطورة',
      'LOAD CALCULATION': 'حساب الأحمال',
      'LOGBOOK MAINTENANCE': 'حفظ سجل الرحلات',
      'DOT REGULATIONS': 'لوائح وزارة النقل',
      'HAZMAT HANDLING': 'التعامل مع المواد الخطرة',
      
      // SECURITY SKILLS
      'SURVEILLANCE': 'المراقبة',
      'ACCESS CONTROL': 'مراقبة الدخول',
      'PATROL': 'الدورية',
      'INCIDENT REPORTING': 'كتابة التقارير',
      'CROWD CONTROL': 'مراقبة الحشود',
      'ALARM SYSTEMS': 'أنظمة الإنذار',
      'CCTV MONITORING': 'مراقبة الكاميرات',
      'CONFLICT RESOLUTION': 'حل النزاعات',
      'PHYSICAL FITNESS': 'اللياقة البدنية',
      
      // CAREGIVER SKILLS
      'PATIENT CARE': 'رعاية المرضى',
      'MEDICATION ADMINISTRATION': 'إعطاء الأدوية',
      'VITAL SIGNS MONITORING': 'مراقبة العلامات الحيوية',
      'MOBILITY ASSISTANCE': 'مساعدة في الحركة',
      'PERSONAL HYGIENE ASSISTANCE': 'مساعدة في النظافة الشخصية',
      'MEAL ASSISTANCE': 'مساعدة في تناول الطعام',
      'COMPANIONSHIP': 'المرافقة',
      'EMOTIONAL SUPPORT': 'الدعم العاطفي',
      
      // CLEANER SKILLS
      'COMMERCIAL CLEANING': 'التنظيف التجاري',
      'SANITIZATION': 'التعقيم',
      'CHEMICAL HANDLING': 'التعامل مع المواد الكيميائية',
      'EQUIPMENT OPERATION': 'تشغيل المعدات',
      'WASTE MANAGEMENT': 'إدارة النفايات',
      'DEEP CLEANING': 'التنظيف العميق',
      'CARPET CLEANING': 'تنظيف السجاد',
      'PRESSURE WASHING': 'غسيل الضغط العالي',
      
      // EMERGENCY SERVICES SKILLS
      'FIREFIGHTING': 'مكافحة الحرائق',
      'EMERGENCY RESPONSE': 'الاستجابة للطوارئ',
      'RESCUE OPERATIONS': 'عمليات الإنقاذ',
      'FIRST AID': 'الإسعافات الأولية',
      'CPR': 'الإنعاش القلبي الرئوي',
      'DISPATCH': 'الإرسال',
      'RADIO COMMUNICATION': 'الاتصال اللاسلكي',
      'CREW MANAGEMENT': 'إدارة الطاقم',
      'HAZARD ASSESSMENT': 'تقييم المخاطر',
      
      // NURSING SKILLS
      'PATIENT ASSESSMENT': 'تقييم المرضى',
      'WOUND CARE': 'رعاية الجروح',
      'IV THERAPY': 'العلاج الوريدي',
      'MEDICATION MANAGEMENT': 'إدارة الأدوية',
      'INFECTION CONTROL': 'مكافحة العدوى',
      'MEDICAL DOCUMENTATION': 'التوثيق الطبي',
      'EMERGENCY PROCEDURES': 'إجراءات الطوارئ',
      'PATIENT EDUCATION': 'تثقيف المرضى',
      
      // TEACHING SKILLS
      'LESSON PLANNING': 'تخطيط الدروس',
      'CLASSROOM MANAGEMENT': 'إدارة الفصل',
      'CURRICULUM DEVELOPMENT': 'تطوير المناهج',
      'STUDENT ASSESSMENT': 'تقييم الطلاب',
      'EDUCATIONAL TECHNOLOGY': 'التكنولوجيا التعليمية',
      'PARENT COMMUNICATION': 'التواصل مع الأولياء',
      'SPECIAL NEEDS SUPPORT': 'دعم ذوي الاحتياجات الخاصة',
      'TUTORING': 'التدريس الخصوصي',
      
      // WELDING SKILLS
      'ARC WELDING': 'اللحام بالقوس',
      'MIG WELDING': 'لحام MIG',
      'TIG WELDING': 'لحام TIG',
      'STAINLESS STEEL WELDING': 'لحام الستانلس ستيل',
      'BLUEPRINT READING': 'قراءة المخططات',
      'METAL CUTTING': 'قطع المعادن',
      'GRINDING': 'الطحن',
      'SAFETY PROCEDURES': 'إجراءات السلامة',
      
      // MECHANIC SKILLS
      'ENGINE REPAIR': 'إصلاح المحركات',
      'DIESEL ENGINE MAINTENANCE': 'صيانة محركات الديزل',
      'HYDRAULIC SYSTEMS': 'الأنظمة الهيدروليكية',
      'ELECTRICAL SYSTEMS': 'الأنظمة الكهربائية',
      'DIAGNOSTIC TOOLS': 'أدوات التشخيص',
      'BRAKE REPAIR': 'إصلاح المكابح',
      'TRANSMISSION REPAIR': 'إصلاح ناقل الحركة',
      'TIRE INSTALLATION': 'تركيب الإطارات',
      'AUTOMOTIVE PAINTING': 'دهان السيارات',
      'BODY REPAIR': 'إصلاح هيكل السيارة',
      'DENTING': 'إصلاح الخدوش',
      
      // CNC MACHINE OPERATOR SKILLS
      'CNC PROGRAMMING': 'برمجة CNC',
      'MACHINE SETUP': 'إعداد الآلات',
      'QUALITY CONTROL': 'مراقبة الجودة',
      'PRECISION MEASUREMENT': 'القياس الدقيق',
      'TOOL CHANGING': 'تغيير الأدوات',
      'G-CODE': 'كود G',
      'CAD SOFTWARE': 'برامج CAD',
      
      // FABRICATION SKILLS
      'METAL FABRICATION': 'تصنيع المعادن',
      'STEEL STRUCTURE': 'هياكل الصلب',
      'PIPE FITTING': 'تركيب الأنابيب',
      'ASSEMBLY': 'التجميع',
      'RIGGING': 'الرفع والنقل',
      'ERECTION': 'التركيب',
      'FOREMAN DUTIES': 'مهام رئيس العمال',
      'TEAM SUPERVISION': 'إشراف الفريق',
      'PROJECT COORDINATION': 'تنسيق المشاريع',
      'DRAFTING': 'الرسم الهندسي',
      
      // PLANT TECHNICIAN SKILLS
      'PROCESS MONITORING': 'مراقبة العمليات',
      'EQUIPMENT MAINTENANCE': 'صيانة المعدات',
      'SAFETY PROTOCOLS': 'بروتوكولات السلامة',
      'INSTRUMENTATION': 'القياسات والأجهزة',
      'TROUBLESHOOTING': 'استكشاف الأعطال',
      'GAS PLANT OPERATIONS': 'عمليات محطة الغاز',
      'POWER PLANT OPERATIONS': 'عمليات محطة الكهرباء',
      'WATER TREATMENT': 'معالجة المياه',
      'STEEL PLANT OPERATIONS': 'عمليات مصنع الصلب',
      
      // CRANE OPERATOR SKILLS
      'CRANE OPERATION': 'تشغيل الرافعة',
      'LOAD CALCULATIONS': 'حساب الأحمال',
      'SIGNAL COMMUNICATION': 'التواصل بالإشارات',
      'SAFETY INSPECTIONS': 'فحص السلامة',
      'RIGGING KNOWLEDGE': 'معرفة الرفع',
      'MOBILE CRANE OPERATION': 'تشغيل الرافعة المتنقلة',
      
      // AC TECHNICIAN SKILLS
      'HVAC SYSTEMS': 'أنظمة التكييف والتهوية',
      'REFRIGERATION': 'التبريد',
      'ELECTRICAL WIRING': 'الأسلاك الكهربائية',
      'SYSTEM INSTALLATION': 'تركيب الأنظمة',
      'PREVENTIVE MAINTENANCE': 'الصيانة الوقائية',
      'DUCT WORK': 'أعمال القنوات',
      'CENTRAL AC REPAIR': 'إصلاح التكييف المركزي',
      
      // HELPER SKILLS
      'MANUAL LABOR': 'العمل اليدوي',
      'TOOL HANDLING': 'التعامل مع الأدوات',
      'MATERIAL HANDLING': 'التعامل مع المواد',
      'SITE CLEANUP': 'تنظيف الموقع',
      'BASIC CONSTRUCTION': 'البناء الأساسي',
      'PAINTING ASSISTANCE': 'مساعدة في الدهان',
      'LOADING AND UNLOADING': 'التحميل والتفريغ',
      
      // PERSONAL SKILLS
      'TIME MANAGEMENT': 'إدارة الوقت',
      'COMMUNICATION': 'التواصل',
      'PATIENCE': 'الصبر',
      'RELIABILITY': 'الموثوقية',
      'PUNCTUALITY': 'الالتزام بالمواعيد',
      'FLEXIBILITY': 'المرونة',
      'ATTENTION TO DETAIL': 'الانتباه للتفاصيل',
      'PROBLEM SOLVING': 'حل المشاكل',
      'ADAPTABILITY': 'القدرة على التكيف',
      'TEAMWORK': 'العمل الجماعي',
      'LEADERSHIP': 'القيادة',
      'ORGANIZATION': 'التنظيم',
      'DECISION MAKING': 'اتخاذ القرارات',
      
      // PHYSICAL ABILITIES  
      'LIFTING HEAVY OBJECTS': 'رفع الأشياء الثقيلة',
      'STANDING FOR LONG PERIODS': 'الوقوف لفترات طويلة',
      'STRENGTH': 'القوة',
      'ENDURANCE': 'التحمل',
      'AGILITY': 'الرشاقة',
      'HAND-EYE COORDINATION': 'التنسيق بين اليد والعين',
      'FINE MOTOR SKILLS': 'المهارات الحركية الدقيقة',
      
      // TECHNOLOGY SKILLS
      'COMPUTER SKILLS': 'مهارات الكمبيوتر',
      'PHONE OPERATION': 'تشغيل الهاتف',
      'BASIC TECHNOLOGY': 'التكنولوجيا الأساسية',
      'INTERNET USAGE': 'استخدام الإنترنت',
      'EMAIL': 'البريد الإلكتروني',
      'SOCIAL MEDIA': 'وسائل التواصل الاجتماعي',
      'SOFTWARE OPERATION': 'تشغيل البرامج',
      
      // LANGUAGE SKILLS
      'ENGLISH SPEAKING': 'التحدث بالإنجليزية',
      'ARABIC SPEAKING': 'التحدث بالعربية',
      'BASIC ENGLISH': 'الإنجليزية الأساسية',
      'BASIC ARABIC': 'العربية الأساسية',
      'TRANSLATION': 'الترجمة',
      'INTERPRETATION': 'الترجمة الفورية',
      'MULTILINGUAL': 'متعدد اللغات',
      
      // SAFETY SKILLS
      'SAFETY AWARENESS': 'الوعي بالسلامة',
      'PERSONAL PROTECTIVE EQUIPMENT': 'معدات الحماية الشخصية',
      'HAZARD IDENTIFICATION': 'تحديد المخاطر',
      'ACCIDENT PREVENTION': 'منع الحوادث',
      'EMERGENCY EVACUATION': 'الإخلاء في الطوارئ',
      
      // ADMINISTRATIVE
      'INVENTORY MANAGEMENT': 'إدارة المخزون',
      'BUDGET AWARENESS': 'الوعي بالميزانية',
      'SCHEDULING': 'الجدولة',
      'RECORD KEEPING': 'حفظ السجلات',
      'PLANNING': 'التخطيط',
      'COORDINATION': 'التنسيق',
      'DOCUMENTATION': 'التوثيق',
      
      // FOOD AND COOKING
      'MEAL PREPARATION': 'إعداد الوجبات',
      'BAKING': 'الخبز',
      'GRILLING': 'الشواء',
      'FOOD PRESERVATION': 'حفظ الطعام',
      'NUTRITION KNOWLEDGE': 'معرفة التغذية',
      'KITCHEN SAFETY': 'سلامة المطبخ',
      'FOOD HANDLING': 'التعامل مع الطعام',
      
      // CHILD CARE SKILLS
      'CHILD CARE': 'رعاية الأطفال',
      'BABY CARE': 'رعاية الرضع',
      'FEEDING CHILDREN': 'إطعام الأطفال',
      'PLAYING WITH CHILDREN': 'اللعب مع الأطفال',
      'HELPING WITH HOMEWORK': 'مساعدة في الواجبات المدرسية',
      'BATHING CHILDREN': 'استحمام الأطفال',
      'CHANGING DIAPERS': 'تغيير الحفاضات',
      'PUTTING CHILDREN TO SLEEP': 'تنويم الأطفال',
      'READING TO CHILDREN': 'القراءة للأطفال',
      
      // ELDER CARE SKILLS
      'ELDER CARE': 'رعاية كبار السن',
      'ELDERLY ASSISTANCE': 'مساعدة كبار السن',
      'MEDICATION REMINDERS': 'تذكير بالأدوية',
      
      // OTHER COMMON SKILLS
      'SWIMMING': 'السباحة',
      'MASSAGE': 'التدليك',
      'HAIRDRESSING': 'تصفيف الشعر',
      'MANICURE': 'العناية بالأظافر',
      'MAKEUP': 'المكياج',
      'PHOTOGRAPHY': 'التصوير',
      'WRITING': 'الكتابة',
      'READING': 'القراءة',
      'MUSIC': 'الموسيقى',
      'SINGING': 'الغناء',
      'DANCING': 'الرقص',
      'STORYTELLING': 'رواية القصص',
      'GAMES': 'الألعاب',
      'SPORTS': 'الرياضة',

      // From getSimpleTranslation
      'SCAFFOLDING': 'السقالات',
      'CONCRETE WORK': 'أعمال الخرسانة',
      'PLASTERING': 'التجصيص',
      'TILING': 'أعمال البلاط',
      'FLOORING': 'أعمال الأرضيات',
      'ROOFING': 'أعمال الأسقف',
      'MASONRY': 'أعمال البناء',
      'CARPENTRY': 'النجارة',
      'PLUMBING': 'السباكة',
      'ELECTRICIAN WORK': 'أعمال الكهرباء',
      'PNEUMATIC SYSTEMS': 'الأنظمة الهوائية',
      'LUBRICATION': 'التشحيم',
      'ALIGNMENT': 'المحاذاة',
      'CALIBRATION': 'المعايرة',
      'VIBRATION ANALYSIS': 'تحليل الاهتزاز',
      'THERMAL ANALYSIS': 'التحليل الحراري',
      'STERILIZATION': 'التعقيم',
      'ANESTHESIA': 'التخدير',
      'SURGERY ASSISTANCE': 'مساعدة الجراحة',
      'LABORATORY WORK': 'العمل المخبري',
      'RADIOLOGY': 'الأشعة',
      'PHYSIOTHERAPY': 'العلاج الطبيعي',
      'AUTOMATION': 'الأتمتة',
      'ROBOTICS': 'الروبوتات',
      'MICROPROCESSORS': 'المعالجات الدقيقة',
      'FIBER OPTICS': 'الألياف البصرية',
      'TELECOMMUNICATIONS': 'الاتصالات',
      'NETWORKING': 'الشبكات',
      'WINE PAIRING': 'تنسيق النبيذ',
      'COCKTAIL MIXING': 'خلط الكوكتيل',
      'FOOD PRESENTATION': 'تقديم الطعام',
      'INVENTORY ROTATION': 'تدوير المخزون',
      'COST CONTROL': 'مراقبة التكاليف',
      'HAZMAT CLEANUP': 'تنظيف المواد الخطرة',
      'BIOHAZARD CLEANING': 'تنظيف المخاطر البيولوجية',
      'INDUSTRIAL CLEANING': 'التنظيف الصناعي',
      'WINDOW WASHING': 'غسيل النوافذ',
      'FORKLIFT OPERATION': 'تشغيل الرافعة الشوكية',
      'BULLDOZER OPERATION': 'تشغيل الجرافة',
      'EXCAVATOR OPERATION': 'تشغيل الحفارة',
      'DUMP TRUCK DRIVING': 'قيادة شاحنة القلاب',
      'FARMING': 'الزراعة',
      'IRRIGATION': 'الري',
      'HARVESTING': 'الحصاد',
      'LIVESTOCK CARE': 'رعاية الماشية',
      'CROP MANAGEMENT': 'إدارة المحاصيل',
      'MARITIME OPERATIONS': 'العمليات البحرية',
      'DECK OPERATIONS': 'عمليات السطح',
      'NAVIGATION': 'الملاحة البحرية',
      'ANCHOR HANDLING': 'التعامل مع المراسي',
      'ROPE WORK': 'أعمال الحبال',
      'CONCIERGE SERVICES': 'خدمات الكونسيرج',
      'EVENT PLANNING': 'تخطيط الفعاليات',
      'TOUR GUIDING': 'الإرشاد السياحي',
      'RESERVATION MANAGEMENT': 'إدارة الحجوزات',
      'FACIAL TREATMENTS': 'علاجات الوجه',
      'BODY TREATMENTS': 'علاجات الجسم',
      'AROMATHERAPY': 'العلاج بالروائح',
      'REFLEXOLOGY': 'علم المنعكسات',
      'ACUPUNCTURE': 'الوخز بالإبر',
      'QUALITY ASSURANCE': 'ضمان الجودة',
      'INSPECTION': 'التفتيش',
      'TESTING': 'الاختبار',
      'CERTIFICATION': 'الشهادات',
      'COMPLIANCE': 'الامتثال',
      'WASTE SORTING': 'فرز النفايات',
      'RECYCLING': 'إعادة التدوير',
      'ENVIRONMENTAL MONITORING': 'المراقبة البيئية',
      'POLLUTION CONTROL': 'مكافحة التلوث',
      'BOMB DETECTION': 'كشف المتفجرات',
      'K9 HANDLING': 'التعامل مع الكلاب البوليسية',
      'METAL DETECTION': 'كشف المعادن',
      'BACKGROUND CHECKS': 'فحص الخلفيات',
      'RISK ASSESSMENT': 'تقييم المخاطر',
    };

    const upperSkill = englishSkill.toUpperCase().trim();

    // 1. Exact match
    if (allTranslations[upperSkill]) {
      return allTranslations[upperSkill];
    }

    // 2. Partial match for compound skills
    const words = upperSkill.split(/[\s\-_]+/);
    const foundTranslations: string[] = [];
    for (const word of words) {
      if (allTranslations[word]) {
        foundTranslations.push(allTranslations[word]);
      }
    }
    if (foundTranslations.length > 0) {
      return foundTranslations.join(' ');
    }

    // 3. Fallback for untranslated skills
    return `مهارة ${englishSkill.toLowerCase()}`;
  };

  // Function to toggle skills (memoized to prevent re-renders)
  const toggleSkill = useCallback((index: number) => {
    setFormData(prev => {
      const newFormData = {
        ...prev,
        allSkills: prev.allSkills.map((skill, i) => 
          i === index ? { ...skill, checked: !skill.checked } : skill
        )
      };
      
      // Auto-save functionality removed
      // markFormDirty();
      
      return newFormData;
    });
  }, [selectedTemplate]);

  // Function to add new skill (memoized to prevent re-renders)
  const addNewSkill = useCallback(() => {
    if (newSkillName.trim() !== '') {
      const newSkill = {
        name: newSkillName.trim().toUpperCase(),
        checked: true, // Automatically check the new skill
        isDefault: false
      };
      
      setFormData(prev => {
        const newFormData = {
          ...prev,
          allSkills: [...prev.allSkills, newSkill]
        };
        
        // Mark form as dirty to trigger auto-save
        markFormDirty();
        
        return newFormData;
      });
      
      setNewSkillName('');
    }
  }, [newSkillName, selectedTemplate]);

  // Function to remove skill (memoized to prevent re-renders)
  const removeSkill = useCallback((index: number) => {
    setFormData(prev => {
      const newFormData = {
        ...prev,
        allSkills: prev.allSkills.filter((skill, i) => i !== index)
      };
      
      // Auto-save functionality removed
      // markFormDirty();
      
      return newFormData;
    });
  }, [selectedTemplate]);

  const normalizeSkillName = (skillName: string) => skillName.toUpperCase().replace(/[^A-Z0-9]/g, '');

  const template6SkillRows = [
    { key: 'elderlyCare', english: 'Elderly Care', arabic: 'رعاية كبار السن', aliases: ['CARING ELDERS', 'ELDERLY CARE', 'ELDERLYCARE'] },
    { key: 'childCare', english: 'Child Care', arabic: 'رعاية الأطفال', aliases: ['BABYSITTING', 'CHILD CARE', 'CHILDCARE'] },
    { key: 'housework', english: 'Housework', arabic: 'أعمال المنزل', aliases: ['CLEANING', 'HOUSEWORK', 'HOUSE WORK'] },
    { key: 'cooking', english: 'Cooking', arabic: 'الطبخ', aliases: ['COOKING', 'ARABIC DISH COOKING', 'ARABICCOOKING'] },
    { key: 'sewing', english: 'Sewing', arabic: 'الخياطة', aliases: ['SEWING'] },
    { key: 'computerUse', english: 'Computer Use', arabic: 'استخدام الكمبيوتر', aliases: ['COMPUTER USE', 'COMPUTER', 'COMPUTERUSE'] }
  ] as const;

  const hasTemplate6Skill = (aliases: readonly string[]) => {
    const normalizedAliases = aliases.map(normalizeSkillName);
    return formData.allSkills.some(
      skill => normalizedAliases.includes(normalizeSkillName(skill.name)) && skill.checked
    );
  };

  const setTemplate6Skill = (aliases: readonly string[], checked: boolean, fallbackName: string) => {
    const normalizedAliases = aliases.map(normalizeSkillName);
    setFormData(prev => {
      let found = false;
      const updatedSkills = prev.allSkills.map(skill => {
        if (normalizedAliases.includes(normalizeSkillName(skill.name))) {
          found = true;
          return { ...skill, checked };
        }
        return skill;
      });

      if (!found) {
        updatedSkills.push({
          name: fallbackName.toUpperCase(),
          checked,
          isDefault: false
        });
      }

      return {
        ...prev,
        allSkills: updatedSkills
      };
    });
    markFormDirty();
  };

  // Function to render PDF pages separately for multi-page documents
  const renderPDFPages = (src: string, title: string, zoom: number, maxPages: number = 3) => {
    // For multi-page PDFs, we'll create separate page containers
    // This is a simplified approach focusing on most common case of 1-3 pages
    const pages = [];
    for (let i = 1; i <= maxPages; i++) {
      pages.push(
        <div key={i} className="document-container page-break" style={{marginBottom: i < maxPages ? '20px' : '0'}}>
          <iframe 
            src={`${src}#page=${i}`}
            className="w-full"
            title={`${title} - Page ${i}`}
            style={{
              border: 'none', 
              margin: 0, 
              padding: 0, 
              display: 'block',
              height: `${(1122 * zoom) / 100}px`,
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top left',
              width: `${100 / (zoom / 100)}%`
            }}
          />
        </div>
      );
    }
    return pages;
  };

  // Function to preview CV
  const previewCV = () => {
    setPreviewMode(true);
  };

  // Function to download and print CV
  const downloadAndPrintCV = async () => {
    try {
      // Clear storage proactively before attempting to save
      try {
        localStorage.removeItem('uploadedDocuments');
        localStorage.removeItem('tempDocuments');
      } catch (clearError) {
        console.warn('Could not clear old documents:', clearError);
      }
      
      const printContent = printRef.current;
      if (!printContent) {
        alert('No content to download/print');
        return;
      }

      // Save the CV as a completed document
      const candidateName = formData.fullName || 'Unknown Candidate';
      const cvDocument = {
        id: `cv-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: `${candidateName} CV - ${new Date().toLocaleDateString()}`,
        type: 'application/pdf',
        size: 150000, // Approximate size
        uploadedAt: new Date().toISOString(),
        uploadedBy: 'CV Builder',
        url: '#cv-completed',
        category: 'cv-builder-cvs',
        description: `Completed CV for ${candidateName}`,
        formData: formData,
        template: selectedTemplate
      };
      
      // Skip saving to localStorage during print to avoid quota errors
      // The CV data is already saved as draft

      const printWindow = window.open('', '_blank', 'width=1200,height=800');
      if (!printWindow) {
        alert('Please allow pop-ups to download/print PDF');
        return;
      }

      // No document attachments section since passport copy and certificates upload were removed

      // Generate the print content
      let templateContent = '';
      
      // Copy the current template HTML to the print window
      templateContent = (printContent as HTMLElement).innerHTML;

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>CV - ${formData.fullName || 'Draft'}</title>
          <meta charset="utf-8">
          <style>
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
              box-sizing: border-box !important;
            }
            
            @page {
              size: A4;
              margin: 1cm;
            }
            
            body {
              margin: 0;
              padding: 0;
              background: white !important;
              font-family: Arial, sans-serif !important;
              font-size: 12px !important;
              line-height: 1.2 !important;
            }
            
            .cv-container {
              width: 100% !important;
              background: white !important;
              padding: 0 !important;
              margin: 0 !important;
              font-family: Arial, sans-serif !important;
              font-weight: bold !important;
              border: 1px solid #000 !important;
            }
            
            table {
              border-collapse: collapse !important;
              width: 100% !important;
              font-family: Arial, sans-serif !important;
            }
            
            td, th {
              border: 1px solid #000 !important;
              padding: 4px !important;
              font-weight: bold !important;
              vertical-align: middle !important;
              font-family: Arial, sans-serif !important;
              font-size: 12px !important;
            }
            
            .header-blue {
              background-color: #00BFFF !important;
              background: #00BFFF !important;
            }
            
            .header-green {
              background-color: #DDE9D5 !important;
              background: #DDE9D5 !important;
            }
            
            .header-yellow {
              background-color: #ffeb3b !important;
              background: #ffeb3b !important;
            }
            
            .header-gray {
              background-color: #e0e0e0 !important;
              background: #e0e0e0 !important;
            }
            
            .text-red {
              color: #FF0000 !important;
            }
            
            img {
              max-width: 100% !important;
              height: auto !important;
              display: block !important;
            }
            
            .page-break {
              page-break-before: always !important;
            }
            
            .document-container {
              width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
              border: none !important;
            }
            
            .document-container iframe {
              width: 100% !important;
              border: none !important;
              margin: 0 !important;
              padding: 0 !important;
              display: block !important;
              background: white !important;
            }
            
            .document-container img {
              width: 100% !important;
              height: auto !important;
              border: none !important;
              margin: 0 !important;
              padding: 0 !important;
              display: block !important;
              background: white !important;
            }
            
            .no-print {
              display: block !important;
            }
            
            @media print {
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              .cv-container {
                max-height: 297mm !important;
                page-break-after: avoid !important;
                overflow: hidden !important;
              }
              .page-break {
                page-break-before: always !important;
              }
              .document-container {
                page-break-inside: avoid !important;
              }
              .no-print {
                display: none !important;
              }
              .zoom-controls {
                display: none !important;
              }
              iframe {
                height: 100vh !important;
                width: 100% !important;
                border: none !important;
                transform: none !important;
              }
              img {
                max-width: 100% !important;
                height: auto !important;
                border: none !important;
                transform: none !important;
                object-fit: contain !important;
              }
            }
          </style>
        </head>
        <body>
          ${templateContent}
        </body>
        </html>
      `);
      
      printWindow.document.close();
      
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
        }, 1000);
      };
      
    } catch (error) {
      console.error('PDF generation error:', error);
      const err = error as any;
      console.error('Error details:', {
        name: err?.name,
        message: err?.message,
        stack: err?.stack
      });
      
      // Handle specific error types
      if (err?.name === 'QuotaExceededError') {
        // Clear some localStorage data and show helpful message
        try {
          localStorage.removeItem('uploadedDocuments');
          localStorage.removeItem('tempDocuments');
          // Clear old CV drafts if any
          const drafts = JSON.parse(localStorage.getItem('cvDrafts') || '[]');
          if (drafts.length > 5) {
            localStorage.setItem('cvDrafts', JSON.stringify(drafts.slice(-5)));
          }
          alert('Storage quota exceeded. We\'ve cleared some old data. Please try again.');
        } catch (clearError) {
          console.error('Error clearing storage:', clearError);
          alert('Storage is full. Please clear your browser data and try again.');
        }
      } else if (err.message && err.message.includes('blocked')) {
        alert('Pop-up blocker prevented opening the print window. Please allow pop-ups for this site and try again.');
      } else {
        alert('Error generating PDF. Please try again. If the problem persists, try clearing your browser cache.');
      }
    }
  };

  
  // Template 2 - NAIM INVESTMENT LIMITED
  const Template2Preview = () => (
    <div className="cv-container" style={{ 
      fontFamily: 'Arial, sans-serif', 
      fontSize: '12px', 
      fontWeight: 'bold',
      width: '100%',
      maxWidth: '800px',
      margin: '0 auto',
      border: '1px solid #000'
    }}>
      {/* Header */}
      <div style={{ 
        textAlign: 'center', 
        padding: '10px', 
        borderBottom: '1px solid #000'
      }}>
        <h1 style={{ 
          fontSize: '20px', 
          fontWeight: 'bold', 
          margin: '0 0 5px 0',
          color: '#25A4DB'
        }}>
          NAIM INVESTMENT LIMITED
        </h1>
        <h2 style={{ 
          fontSize: '16px', 
          fontWeight: 'bold', 
          margin: '0',
          display: 'flex',
          justifyContent: 'center'
        }}>
          HOUSEMAID APPLICATION FORM - 
          <span style={{ marginRight: '8px' }}></span>
          <span style={{ textAlign: 'right', color: 'red' }}>بيانات طلب الخادمة</span>
        </h2>
      </div>
      
      {/* Main Content */}
      <div style={{ display: 'flex' }}>
        {/* Left side - Main table */}
        <div style={{ width: '75%' }}>
          {/* First section */}
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td style={{ 
                  padding: '5px', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  width: '33%',
                  fontWeight: 'normal'
                }}>
                  POST APPLIED FOR
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'center', 
                  fontWeight: 'bold', 
                  border: '1px solid #000'
                }}>
                  {formData.position}
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'right', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  color: 'red'
                }}>
                  الوظيفة المطلوبة
                </td>
              </tr>
              <tr>
                <td style={{ 
                  padding: '5px', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  fontWeight: 'normal'
                }}>
                  CONTRACT PERIOD
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'center', 
                  fontWeight: 'bold', 
                  border: '1px solid #000'
                }}>
                  {formData.contractPeriod || formData.workYears}
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'right', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  color: 'red'
                }}>
                  مدة العقد
                </td>
              </tr>
              <tr>
                <td style={{ 
                  padding: '5px', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  fontWeight: 'normal'
                }}>
                  MONTHLY SALARY
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'center', 
                  fontWeight: 'bold', 
                  border: '1px solid #000'
                }}>
                  {formData.monthlySalary || formData.salary}
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'right', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  color: 'red'
                }}>
                  الراتب الشهري
                </td>
              </tr>
            </tbody>
          </table>
          
          {/* Personal Information Section */}
          <div style={{ 
            textAlign: 'center', 
            padding: '4px', 
            fontWeight: 'bold',
            backgroundColor: '#DDE9D5',
            borderLeft: '1px solid #000',
            borderRight: '1px solid #000',
            borderBottom: '1px solid #000'
          }}>
            <span style={{ color: 'red' }}>PERSONAL INFORMATION</span> <span style={{ color: 'red' }}>المعلومات الشخصية</span>
          </div>
          
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td style={{ 
                  padding: '5px', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  width: '33%',
                  fontWeight: 'normal'
                }}>
                  Name
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'center', 
                  fontWeight: 'bold', 
                  border: '1px solid #000'
                }}>
                  {formData.fullName}
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'right', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  color: 'red'
                }}>
                  اسم
                </td>
              </tr>
              <tr>
                <td style={{ 
                  padding: '5px', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  fontWeight: 'normal'
                }}>
                  Nationality
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'center', 
                  fontWeight: 'bold', 
                  border: '1px solid #000'
                }}>
                  {formData.nationality}
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'right', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  color: 'red'
                }}>
                  الجنسية
                </td>
              </tr>
              <tr>
                <td style={{ 
                  padding: '5px', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  fontWeight: 'normal'
                }}>
                  Date of Birth
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'center', 
                  fontWeight: 'bold', 
                  border: '1px solid #000'
                }}>
                  {formData.dateOfBirth}
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'right', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  color: 'red'
                }}>
                  العمر
                </td>
              </tr>
              <tr>
                <td style={{ 
                  padding: '5px', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  fontWeight: 'normal'
                }}>
                  Civil Status
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'center', 
                  fontWeight: 'bold', 
                  border: '1px solid #000'
                }}>
                  {formData.civilStatus}
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'right', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  color: 'red'
                }}>
                  الحالة الاجتماعية
                </td>
              </tr>
              <tr>
                <td style={{ 
                  padding: '5px', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  fontWeight: 'normal'
                }}>
                  Living town
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'center', 
                  fontWeight: 'bold', 
                  border: '1px solid #000'
                }}>
                  {formData.address}
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'right', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  color: 'red'
                }}>
                  مكان السكن
                </td>
              </tr>
              <tr>
                <td style={{ 
                  padding: '5px', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  fontWeight: 'normal'
                }}>
                  Weight
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'center', 
                  fontWeight: 'bold', 
                  border: '1px solid #000'
                }}>
                  {formData.weight}
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'right', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  color: 'red'
                }}>
                  الوزن
                </td>
              </tr>
              <tr>
                <td style={{ 
                  padding: '5px', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  fontWeight: 'normal'
                }}>
                  Height
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'center', 
                  fontWeight: 'bold', 
                  border: '1px solid #000'
                }}>
                  {formData.height}
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'right', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  color: 'red'
                }}>
                  الطول
                </td>
              </tr>
              <tr>
                <td style={{ 
                  padding: '5px', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  fontWeight: 'normal'
                }}>
                  Religion
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'center', 
                  fontWeight: 'bold', 
                  border: '1px solid #000'
                }}>
                  {formData.religion}
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'right', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  color: 'red'
                }}>
                  الديانة
                </td>
              </tr>
              <tr>
                <td style={{ 
                  padding: '5px', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  fontWeight: 'normal'
                }}>
                  Highest Education
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'center', 
                  fontWeight: 'bold', 
                  border: '1px solid #000'
                }}>
                  {formData.educationLevel}
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'right', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  color: 'red'
                }}>
                  أعلى تعليم
                </td>
              </tr>
            </tbody>
          </table>
          
          {/* Passport Details Section */}
          <div style={{ 
            textAlign: 'center', 
            padding: '4px', 
            fontWeight: 'bold',
            backgroundColor: '#DDE9D5',
            borderLeft: '1px solid #000',
            borderRight: '1px solid #000',
            borderBottom: '1px solid #000'
          }}>
            <span style={{ color: 'red' }}>PASSPORT DETAILS</span> <span style={{ color: 'red' }}>بيانات جواز السفر</span>
          </div>
          
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td style={{ 
                  padding: '5px', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  width: '33%',
                  fontWeight: 'normal'
                }}>
                  Passport No
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'center', 
                  fontWeight: 'bold', 
                  border: '1px solid #000'
                }}>
                  {formData.passportNo}
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'right', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  color: 'red'
                }}>
                  رقم جواز السفر
                </td>
              </tr>
              <tr>
                <td style={{ 
                  padding: '5px', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  fontWeight: 'normal'
                }}>
                  Date of Issue
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'center', 
                  fontWeight: 'bold', 
                  border: '1px solid #000'
                }}>
                  {formData.dateIssued}
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'right', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  color: 'red'
                }}>
                  تاريخ الإصدار
                </td>
              </tr>
              <tr>
                <td style={{ 
                  padding: '5px', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  fontWeight: 'normal'
                }}>
                  Date of Expiry
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'center', 
                  fontWeight: 'bold', 
                  border: '1px solid #000'
                }}>
                  {formData.dateExpiry}
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'right', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  color: 'red'
                }}>
                  تاريخ الإنتهاء
                </td>
              </tr>
              <tr>
                <td style={{ 
                  padding: '5px', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  fontWeight: 'normal'
                }}>
                  Place of Issue
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'center', 
                  fontWeight: 'bold', 
                  border: '1px solid #000'
                }}>
                  {formData.placeIssued}
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'right', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  color: 'red'
                }}>
                  مكان الإصدار
                </td>
              </tr>
            </tbody>
          </table>
          
          {/* Language Skills Section */}
          <div style={{ 
            textAlign: 'center', 
            padding: '4px', 
            fontWeight: 'bold',
            backgroundColor: '#DDE9D5',
            borderLeft: '1px solid #000',
            borderRight: '1px solid #000',
            borderBottom: '1px solid #000'
          }}>
            <span style={{ color: 'red' }}>SPOKEN LANGUAGE</span> <span style={{ color: 'red' }}>اللغة المتحدثة</span>
          </div>
          
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td style={{ 
                  padding: '5px', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  width: '33%',
                  fontWeight: 'normal'
                }}>
                  English
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'center', 
                  fontWeight: 'bold', 
                  border: '1px solid #000'
                }}>
                  {formData.englishLevel}
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'right', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  color: 'red'
                }}>
                  إنجليزي
                </td>
              </tr>
              <tr>
                <td style={{ 
                  padding: '5px', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  fontWeight: 'normal'
                }}>
                  Arabic
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'center', 
                  fontWeight: 'bold', 
                  border: '1px solid #000'
                }}>
                  {formData.arabicLevel}
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'right', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  color: 'red'
                }}>
                  عربي
                </td>
              </tr>
            </tbody>
          </table>
          
          {/* Work Experience Section */}
          <div style={{ 
            textAlign: 'center', 
            padding: '4px', 
            fontWeight: 'bold',
            backgroundColor: '#DDE9D5',
            borderLeft: '1px solid #000',
            borderRight: '1px solid #000',
            borderBottom: '1px solid #000'
          }}>
            <span style={{ color: 'red' }}>WORK EXPERIENCE</span> <span style={{ color: 'red' }}>خبرة العمل</span>
          </div>
          
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ 
                  padding: '5px', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  fontWeight: 'normal'
                }}>
                  Designation <span style={{ color: 'red' }}>المهنة</span>
                </th>
                <th style={{ 
                  padding: '5px', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  fontWeight: 'normal'
                }}>
                  Country <span style={{ color: 'red' }}>البلد</span>
                </th>
                <th style={{ 
                  padding: '5px', 
                  backgroundColor: '#f2f2f2', 
                  border: '1px solid #000',
                  fontWeight: 'normal'
                }}>
                  Period <span style={{ color: 'red' }}>الفترة</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'center', 
                  fontWeight: 'bold', 
                  border: '1px solid #000',
                  backgroundColor: '#fffde7'
                }}>
                  {formData.workPosition}
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'center', 
                  fontWeight: 'bold', 
                  border: '1px solid #000',
                  backgroundColor: '#fffde7'
                }}>
                  {formData.workCountry}
                </td>
                <td style={{ 
                  padding: '5px', 
                  textAlign: 'center', 
                  fontWeight: 'bold', 
                  border: '1px solid #000',
                  backgroundColor: '#fffde7'
                }}>
                  {formData.workYears}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        {/* Right side - Images */}
        <div style={{ width: '25%' }}>
          {/* Profile image box */}
          <div style={{ 
            height: '33%', 
            border: '1px solid #000',
            padding: '5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {formData.profilePhoto ? (
              <img 
                src={formData.profilePhoto} 
                alt="Profile" 
                style={{ 
                  maxHeight: '100%', 
                  maxWidth: '100%',
                  objectFit: 'contain',
                  objectPosition: 'center'
                }} 
              />
            ) : (
              <div style={{ 
                backgroundColor: '#f2f2f2', 
                height: '150px', 
                width: '120px', 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#777'
              }}>
                Profile Photo
              </div>
            )}
          </div>
          
          {/* Full body image box */}
          <div style={{ 
            height: '67%', 
            border: '1px solid #000',
            padding: '5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {formData.fullBodyPhoto ? (
              <img 
                src={formData.fullBodyPhoto} 
                alt="Full Body" 
                style={{ 
                  maxHeight: '100%', 
                  maxWidth: '100%',
                  objectFit: 'contain',
                  objectPosition: 'center'
                }} 
              />
            ) : (
              <div style={{ 
                backgroundColor: '#f2f2f2', 
                height: '300px', 
                width: '160px', 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#777'
              }}>
                Full Body Photo
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Duties Section */}
      <div style={{ marginTop: '15px' }}>
        <div style={{ 
          textAlign: 'center', 
          padding: '5px', 
          color: 'red', 
          fontWeight: 'bold', 
          fontSize: '16px'
        }}>
          <span style={{ color: 'red' }}>DUTIES</span> <span style={{ color: 'red' }}>الواجبات</span>
        </div>
        
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ 
                border: '1px solid #000', 
                padding: '5px', 
                backgroundColor: '#f2f2f2',
                width: '20%',
                fontWeight: 'normal'
              }}>
                Cooking /ARABIC FOODS<br/>
                <span style={{ color: 'red' }}>الطبخ</span>
              </th>
              <th style={{ 
                border: '1px solid #000', 
                padding: '5px', 
                backgroundColor: '#f2f2f2',
                width: '20%',
                fontWeight: 'normal'
              }}>
                Cleaning<br/>
                <span style={{ color: 'red' }}>النظافة</span>
              </th>
              <th style={{ 
                border: '1px solid #000', 
                padding: '5px', 
                backgroundColor: '#f2f2f2',
                width: '20%',
                fontWeight: 'normal'
              }}>
                Washing<br/>
                <span style={{ color: 'red' }}>الغسيل</span>
              </th>
              <th style={{ 
                border: '1px solid #000', 
                padding: '5px', 
                backgroundColor: '#f2f2f2',
                width: '20%',
                fontWeight: 'normal'
              }}>
                Baby-sitting / Elderly Care<br/>
                <span style={{ color: 'red' }}>تربية الأطفال</span>
              </th>
              <th style={{ 
                border: '1px solid #000', 
                padding: '5px', 
                backgroundColor: '#f2f2f2',
                width: '20%',
                fontWeight: 'normal'
              }}>
                Ironing<br/>
                <span style={{ color: 'red' }}>كي الملابس</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ 
                border: '1px solid #000', 
                padding: '5px', 
                textAlign: 'center',
                color: '#25A4DB'
              }}>
                {formData.allSkills.find(s => s.name === 'ARABIC DISH COOKING')?.checked ? 'YES' : 'NO'}
              </td>
              <td style={{ 
                border: '1px solid #000', 
                padding: '5px', 
                textAlign: 'center',
                color: '#25A4DB'
              }}>
                {formData.allSkills.find(s => s.name === 'CLEANING')?.checked ? 'YES' : 'NO'}
              </td>
              <td style={{ 
                border: '1px solid #000', 
                padding: '5px', 
                textAlign: 'center',
                color: '#25A4DB'
              }}>
                {formData.allSkills.find(s => s.name === 'WASHING')?.checked ? 'YES' : 'NO'}
              </td>
              <td style={{ 
                border: '1px solid #000', 
                padding: '5px', 
                textAlign: 'center',
                color: '#25A4DB'
              }}>
                {formData.allSkills.find(s => s.name === 'BABYSITTING')?.checked ? 'YES' : 'NO'} / 
                {formData.allSkills.find(s => s.name === 'CARING ELDERS')?.checked ? 'YES' : 'NO'}
              </td>
              <td style={{ 
                border: '1px solid #000', 
                padding: '5px', 
                textAlign: 'center',
                color: '#25A4DB'
              }}>
                {formData.allSkills.find(s => s.name === 'IRONING')?.checked ? 'YES' : 'NO'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      {/* Additional Skills Section - for newly added skills */}
      {(() => {
        const additionalSkills = formData.allSkills.filter(skill => 
          !['ARABIC DISH COOKING', 'CLEANING', 'WASHING', 'BABYSITTING', 'CARING ELDERS', 'IRONING'].includes(skill.name) && 
          skill.checked
        );
        
        return additionalSkills.length > 0 ? (
          <div style={{ 
            marginTop: '15px', 
            borderTop: '1px solid #000', 
            padding: '8px'
          }}>
            <div style={{ 
              textAlign: 'center', 
              padding: '5px', 
              color: 'red', 
              fontWeight: 'bold', 
              fontSize: '16px'
            }}>
              <span style={{ color: 'red' }}>ADDITIONAL DUTIES</span> <span style={{ color: 'red' }}>واجبات إضافية</span>
            </div>
            
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '5px' }}>
              <tbody>
                {additionalSkills.map((skill, index) => (
                  <tr key={index}>
                    <td style={{ 
                      border: '1px solid #000', 
                      padding: '5px',
                      backgroundColor: '#f2f2f2',
                      fontWeight: 'normal',
                      width: '70%'
                    }}>
                      {skill.name}<br/>
                      <span style={{ color: 'red' }}>{getArabicTranslation(skill.name)}</span>
                    </td>
                    <td style={{ 
                      border: '1px solid #000', 
                      padding: '5px', 
                      textAlign: 'center',
                      color: '#25A4DB',
                      fontWeight: 'bold',
                      width: '30%'
                    }}>
                      YES
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null;
      })()}
      
      {/* Remarks Section */}
      <div style={{ 
        marginTop: '15px', 
        borderTop: '1px solid #000', 
        padding: '8px'
      }}>
        <div style={{ fontWeight: 'bold' }}>
          <span style={{ color: 'red' }}>REMARKS</span> <span style={{ color: 'red' }}>ملاحظات</span>
        </div>
        <div style={{ marginTop: '8px' }}>{formData.remarks}</div>
      </div>
    </div>
  );

  // Template 3 - Custom Template (Tailored to match CV Template 2 from provided code)
  const Template3Preview = () => (
    <div className="cv-container" style={{ 
      fontFamily: 'Arial, sans-serif', 
      fontSize: '12px', 
      fontWeight: 'bold',
      width: '100%',
      maxWidth: '800px',
      margin: '0 auto',
      border: '1px solid #000'
    }}>
      {/* Header */}
      <div style={{ 
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'white'
      }}>
        {/* Logo */}
        <div style={{ 
          width: '20%', 
          padding: '15px'
        }}>
          {formData.logoFile ? (
            <img 
              src={formData.logoFile} 
              alt="Logo" 
              style={{ 
                maxWidth: '100%',
                maxHeight: '90px'
              }}
            />
          ) : (
            <div style={{ 
              backgroundColor: '#f2f2f2', 
              height: '90px', 
              width: '90px', 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#777'
            }}>
              Logo
            </div>
          )}
        </div>
        
        {/* Agency Information */}
        <div style={{ 
          width: '80%', 
          padding: '15px',
          backgroundColor: '#66ccff',
          color: 'white'
        }}>
          <h1 style={{ 
            fontSize: '22px', 
            fontWeight: 'bold', 
            color: 'red', 
            textAlign: 'center',
            margin: '0 0 5px 0'
          }}>
            {formData.companyName}
          </h1>
          <p style={{ 
            textAlign: 'center', 
            color: 'black', 
            fontWeight: 'bold',
            margin: '0 0 3px 0'
          }}>
            {formData.companyLocation}
          </p>
          <p style={{ 
            textAlign: 'center', 
            color: 'black', 
            fontWeight: 'bold',
            margin: '0'
          }}>
            CONTACT NUMBER: {formData.contactNumber}
          </p>
        </div>
      </div>
      
      {/* Position and Salary */}
      <div style={{ display: 'flex' }}>
        <div style={{ width: '67%' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '5px',
                  backgroundColor: '#24fcfc',
                  fontWeight: 'bold',
                  textAlign: 'center'
                }}>
                  POSITION APPLYING FOR <span style={{ color: 'red' }}>المنصب المتقدم له</span>
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '5px',
                  backgroundColor: '#24fcfc',
                  fontWeight: 'bold',
                  textAlign: 'center'
                }}>
                  {formData.position}
                </td>
              </tr>
              <tr>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '5px',
                  fontWeight: 'bold',
                  textAlign: 'center'
                }} colSpan={2}>
                  SALARY: {formData.salary}
                </td>
              </tr>
            </tbody>
          </table>
          
          {/* Passport Details */}
          <div style={{ 
            backgroundColor: '#24fcfc', 
            border: '1px solid #000',
            padding: '5px',
            fontWeight: 'bold',
            textAlign: 'center'
          }}>
            PASSPORT DETAILS <span style={{ color: 'red' }}>بيانات جواز السفر</span>
          </div>
          
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '5px',
                  backgroundColor: '#66ccff',
                  fontWeight: 'bold'
                }}>
                  PASSPORT NO.
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '5px',
                  backgroundColor: 'white',
                  fontWeight: 'bold',
                  textAlign: 'center'
                }}>
                  {formData.passportNo}
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '5px',
                  backgroundColor: '#66ccff',
                  color: 'red',
                  textAlign: 'right'
                }}>
                  رقم جواز السفر
                </td>
              </tr>
              <tr>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '5px',
                  backgroundColor: '#66ccff',
                  fontWeight: 'bold'
                }}>
                  DATE ISSUED
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '5px',
                  backgroundColor: 'white',
                  fontWeight: 'bold',
                  textAlign: 'center'
                }}>
                  {formData.dateIssued}
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '5px',
                  backgroundColor: '#66ccff',
                  color: 'red',
                  textAlign: 'right'
                }}>
                  تاريخ الإصدار
                </td>
              </tr>
              <tr>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '5px',
                  backgroundColor: '#66ccff',
                  fontWeight: 'bold'
                }}>
                  DATE OF EXPIRY
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '5px',
                  backgroundColor: 'white',
                  fontWeight: 'bold',
                  textAlign: 'center'
                }}>
                  {formData.dateExpiry}
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '5px',
                  backgroundColor: '#66ccff',
                  color: 'red',
                  textAlign: 'right'
                }}>
                  تاريخ الانتهاء
                </td>
              </tr>
              <tr>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '5px',
                  backgroundColor: '#66ccff',
                  fontWeight: 'bold'
                }}>
                  PLACE ISSUED
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '5px',
                  backgroundColor: 'white',
                  fontWeight: 'bold',
                  textAlign: 'center'
                }}>
                  {formData.placeIssued}
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '5px',
                  backgroundColor: '#66ccff',
                  color: 'red',
                  textAlign: 'right'
                }}>
                  مكان الإصدار
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        {/* Profile Photo */}
        <div style={{ 
          width: '33%', 
          padding: '5px',
          backgroundColor: '#66ccff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {formData.profilePhoto ? (
            <img 
              src={formData.profilePhoto} 
              alt="Profile" 
              style={{ 
                height: '160px',
                width: '130px',
                objectFit: 'contain',
                objectPosition: 'center',
                backgroundColor: '#fff'
              }}
            />
          ) : (
            <div style={{ 
              backgroundColor: 'white', 
              height: '160px', 
              width: '130px', 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#777'
            }}>
              Profile Photo
            </div>
          )}
        </div>
      </div>
      
      {/* Full Name */}
      <div style={{ 
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#24fcfc',
        padding: '8px'
      }}>
        <div style={{ 
          width: '33%',
          textAlign: 'center',
          fontWeight: 'bold'
        }}>
          NAME IN FULL / <span style={{ color: 'red' }}>الاسم الكامل</span>
        </div>
        <div style={{ 
          width: '67%',
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: '16px'
        }}>
          {formData.fullName}
        </div>
      </div>
      
      {/* Personal Information */}
      <div style={{ 
        backgroundColor: '#66ccff',
        padding: '5px',
        textAlign: 'center',
        fontWeight: 'bold'
      }}>
        PERSONAL INFORMATION <span style={{ color: 'red' }}>معلومات شخصية</span>
      </div>
      
      {/* Personal Information Table */}
      <div style={{ display: 'flex' }}>
        <div style={{ width: '67%' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  backgroundColor: '#66ccff',
                  fontWeight: 'bold',
                  width: '25%'
                }}>
                  ADDRESS
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  {formData.address}
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  backgroundColor: '#66ccff',
                  color: 'red',
                  textAlign: 'right',
                  width: '15%'
                }}>
                  عنوان
                </td>
              </tr>
              <tr>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  backgroundColor: '#66ccff',
                  fontWeight: 'bold'
                }}>
                  NATIONALITY
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  {formData.nationality}
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  backgroundColor: '#66ccff',
                  color: 'red',
                  textAlign: 'right'
                }}>
                  جنسية
                </td>
              </tr>
              <tr>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  backgroundColor: '#66ccff',
                  fontWeight: 'bold'
                }}>
                  RELIGION
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  {formData.religion}
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  backgroundColor: '#66ccff',
                  color: 'red',
                  textAlign: 'right'
                }}>
                  دين
                </td>
              </tr>
              <tr>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  backgroundColor: '#66ccff',
                  fontWeight: 'bold'
                }}>
                  DATE OF BIRTH
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  {formData.dateOfBirth}
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  backgroundColor: '#66ccff',
                  color: 'red',
                  textAlign: 'right'
                }}>
                  تاريخ الميلاد
                </td>
              </tr>
              <tr>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  backgroundColor: '#66ccff',
                  fontWeight: 'bold'
                }}>
                  PLACE OF BIRTH
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  {formData.placeOfBirth}
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  backgroundColor: '#66ccff',
                  color: 'red',
                  textAlign: 'right'
                }}>
                  مكان الميلاد
                </td>
              </tr>
              <tr>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  backgroundColor: '#66ccff',
                  fontWeight: 'bold'
                }}>
                  FATHER
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  {formData.father}
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  backgroundColor: '#66ccff',
                  color: 'red',
                  textAlign: 'right'
                }}>
                  اسم الأب
                </td>
              </tr>
              <tr>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  backgroundColor: '#66ccff',
                  fontWeight: 'bold'
                }}>
                  MOTHER
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  {formData.mother}
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  backgroundColor: '#66ccff',
                  color: 'red',
                  textAlign: 'right'
                }}>
                  اسم الأم
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div style={{ width: '33%' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', height: '100%' }}>
            <tbody>
              <tr>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  backgroundColor: '#66ccff',
                  fontWeight: 'bold',
                  width: '50%'
                }}>
                  AGE
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  {formData.age}
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  backgroundColor: '#66ccff',
                  color: 'red',
                  textAlign: 'right'
                }}>
                  عمر
                </td>
              </tr>
              <tr>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  backgroundColor: '#66ccff',
                  fontWeight: 'bold'
                }}>
                  HEIGHT
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  {formData.height}
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  backgroundColor: '#66ccff',
                  color: 'red',
                  textAlign: 'right'
                }}>
                  ارتفاع
                </td>
              </tr>
              <tr>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  backgroundColor: '#66ccff',
                  fontWeight: 'bold'
                }}>
                  WEIGHT
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  {formData.weight}
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  backgroundColor: '#66ccff',
                  color: 'red',
                  textAlign: 'right'
                }}>
                  وزن
                </td>
              </tr>
              <tr>
                <td
                  style={{
                    border: '1px solid #000',
                    padding: '4px',
                    backgroundColor: '#66ccff',
                    fontWeight: 'bold'
                  }}
                  rowSpan={2}
                >
                  CIVIL STATUS
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  {formData.civilStatus}
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  backgroundColor: '#66ccff',
                  color: 'red',
                  textAlign: 'right'
                }} rowSpan={2}>
                  الأحوال المدنية
                </td>
              </tr>
              <tr>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                </td>
              </tr>
              <tr>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  backgroundColor: '#66ccff',
                  fontWeight: 'bold'
                }}>
                  SPOUSE
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  {formData.spouse}
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  backgroundColor: '#66ccff',
                  color: 'red',
                  textAlign: 'right'
                }}>
                  زوج
                </td>
              </tr>
              <tr>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  backgroundColor: '#66ccff',
                  fontWeight: 'bold'
                }}>
                  NO. OF KIDS
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  {formData.numberOfKids}
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  backgroundColor: '#66ccff',
                  color: 'red',
                  textAlign: 'right'
                }}>
                  عدد الأطفال
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Education & Languages */}
      <div style={{ display: 'flex' }}>
        <div style={{ 
          width: '67%',
          backgroundColor: '#66ccff',
          padding: '5px',
          textAlign: 'center',
          fontWeight: 'bold'
        }}>
          LANGUAGES LEVEL <span style={{ color: 'red' }}>لغات</span>
        </div>
        <div style={{ 
          width: '33%',
          padding: '5px',
          textAlign: 'center',
          fontWeight: 'bold',
          backgroundColor: '#66ccff'
        }}>
          EDUCATION LEVEL <span style={{ color: 'red' }}>التحصيل العلمي</span>
        </div>
      </div>
      
      <div style={{ display: 'flex' }}>
        <div style={{ width: '67%' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  width: '25%'
                }}>
                  ENGLISH
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center'
                }}>
                  {formData.englishLevel}
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  width: '25%'
                }}>
                  ARABIC
                </td>
                <td style={{ 
                  border: '1px solid #000', 
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center'
                }}>
                  {formData.arabicLevel}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div style={{ width: '33%' }}>
          <div style={{ 
            border: '1px solid #000', 
            padding: '4px',
            fontWeight: 'bold',
            textAlign: 'center',
            height: '100%'
          }}>
            {formData.educationLevel}
          </div>
        </div>
      </div>
      
      {/* Work Experience */}
      <div style={{ 
        backgroundColor: '#66ccff',
        padding: '5px',
        textAlign: 'center',
        fontWeight: 'bold',
        marginTop: '8px'
      }}>
        WORK EXPERIENCE <span style={{ color: 'red' }}>خبرة العمل</span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            <td style={{ 
              border: '1px solid #000', 
              padding: '4px',
              fontWeight: 'bold',
              textAlign: 'center',
              width: '33%'
            }}>
              POSITION <span style={{ color: 'red' }}>المنصب</span>
            </td>
            <td style={{ 
              border: '1px solid #000', 
              padding: '4px',
              fontWeight: 'bold',
              textAlign: 'center',
              width: '33%'
            }}>
              COUNTRY <span style={{ color: 'red' }}>البلد</span>
            </td>
            <td style={{ 
              border: '1px solid #000', 
              padding: '4px',
              fontWeight: 'bold',
              textAlign: 'center',
              width: '33%'
            }}>
              YEAR <span style={{ color: 'red' }}>السنة</span>
            </td>
          </tr>
          <tr>
            <td style={{ 
              border: '1px solid #000', 
              padding: '4px',
              fontWeight: 'bold',
              textAlign: 'center',
              backgroundColor: '#fffde7'
            }}>
              {formData.workPosition}
            </td>
            <td style={{ 
              border: '1px solid #000', 
              padding: '4px',
              fontWeight: 'bold',
              textAlign: 'center',
              backgroundColor: '#fffde7'
            }}>
              {formData.workCountry}
            </td>
            <td style={{ 
              border: '1px solid #000', 
              padding: '4px',
              fontWeight: 'bold',
              textAlign: 'center',
              backgroundColor: '#fffde7'
            }}>
              {formData.workYears}
            </td>
          </tr>
        </tbody>
      </table>
      
      {/* Skills and Full Body Photo */}
      <div style={{ display: 'flex', marginTop: '8px' }}>
        <div style={{ width: '50%' }}>
          <div style={{ 
            backgroundColor: '#66ccff',
            padding: '5px',
            textAlign: 'center',
            fontWeight: 'bold'
          }}>
            SKILLS <span style={{ color: 'red' }}>مهارات</span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {formData.allSkills.map((skill, index) => (
                <tr key={index}>
                  <td style={{ 
                    border: '1px solid #000', 
                    padding: '4px',
                    fontWeight: 'bold'
                  }}>
                    {skill.name}
                  </td>
                  <td style={{ 
                    border: '1px solid #000', 
                    padding: '4px',
                    fontWeight: 'bold',
                    textAlign: 'center'
                  }}>
                    {skill.checked ? 'YES' : 'NO'}
                  </td>
                  <td style={{ 
                    border: '1px solid #000', 
                    padding: '4px',
                    color: 'red',
                    textAlign: 'right'
                  }}>
                    {getArabicTranslation(skill.name)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Full body image */}
        <div style={{ 
          width: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '15px'
        }}>
          {formData.fullBodyPhoto ? (
            <img 
              src={formData.fullBodyPhoto} 
              alt="Full Body" 
              style={{ 
                maxHeight: '300px',
                maxWidth: '100%',
                objectFit: 'contain',
                objectPosition: 'center'
              }}
            />
          ) : (
            <div style={{ 
              backgroundColor: '#f2f2f2', 
              height: '300px', 
              width: '200px', 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#777'
            }}>
              Full Body Photo
            </div>
          )}
        </div>
      </div>
      
      {/* Remarks */}
      <div style={{ display: 'flex' }}>
        <div style={{ 
          width: '20%',
          backgroundColor: '#66ccff',
          padding: '5px',
          fontWeight: 'bold'
        }}>
          REMARKS <span style={{ color: 'red' }}>ملاحظات</span>
        </div>
        <div style={{ 
          width: '80%',
          padding: '5px',
          fontWeight: 'bold'
        }}>
          {formData.remarks}
        </div>
      </div>
    </div>
  );

  // Template 4 - Naim Investments CV Template
  const Template4Preview = () => {
    // Function to handle profile photo upload
    const handleProfilePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFileChange('profilePhoto', e.target.files ? e.target.files[0] : null);
    };

    // Function to handle full body photo upload
    const handleFullBodyPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFileChange('fullBodyPhoto', e.target.files ? e.target.files[0] : null);
    };

    // Function to handle logo upload
    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFileChange('logoFile', e.target.files ? e.target.files[0] : null);
    };
    
    return (
      <div style={{ 
        fontFamily: 'Arial, sans-serif', 
        fontSize: '11px', 
        fontWeight: 'bold',
        width: '100%',
        maxWidth: '800px',
        maxHeight: '1050px',
        overflow: 'hidden',
        margin: '0 auto',
        border: '1px solid #000',
        backgroundColor: 'white',
      }}>
        {/* Top section with logo and title */}
        <div style={{ 
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#e8f4f8',
          padding: '4px 15px',
          height: '70px',
        }}>
          {/* Logo */}
          <div style={{ width: '60px', height: '40px', position: 'relative' }}>
            {formData.logoFile ? (
              <img 
                src={formData.logoFile} 
                alt="Company Logo" 
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            ) : (
              <div style={{
                width: '60px',
                height: '40px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px dashed #ccc',
                backgroundColor: '#f9f9f9',
                borderRadius: '4px',
                cursor: previewMode ? 'default' : 'pointer'
              }}
              onClick={() => !previewMode && document.getElementById('logo-upload-t4')?.click()}
              >
                <div style={{ fontSize: '8px', textAlign: 'center' }}>
                  {previewMode ? 'Logo' : 'Click to add logo'}
                </div>
              </div>
            )}
            {!previewMode && (
              <input 
                type="file" 
                id="logo-upload-t4"
                accept="image/*" 
                onChange={handleLogoChange}
                style={{ display: 'none' }}
              />
            )}
          </div>
          
          {/* Title */}
          <div style={{ 
            flex: 1, 
            textAlign: 'center', 
            color: '#1a4c7c', 
            fontFamily: 'Cambria, serif',
            fontStyle: 'italic',
            fontSize: '36px',
            fontWeight: 'bold',
            paddingLeft: '20px'
          }}>
            Naim Investments
          </div>
        </div>

        {/* Company Address */}
        <div style={{ 
          textAlign: 'center', 
          padding: '3px', 
          borderTop: '1px solid #000',
          borderBottom: '1px solid #000',
          fontSize: '10px',
          fontWeight: 'bold',
        }}>
          {formData.companyLocation}. Tel/Fax: +254 41 2317883, Mobile: {formData.contactNumber}
        </div>

        {/* Full Name Header Row */}
        <div style={{ 
          display: 'flex', 
          backgroundColor: '#800000', 
          color: 'white',
          padding: '3px',
          fontWeight: 'bold',
          borderBottom: '1px solid #000',
        }}>
          <div style={{ 
            width: '120px', 
            textAlign: 'center',
            fontWeight: 'bold',
            borderRight: '1px solid white',
            backgroundColor: '#800000',
            color: 'white',
          }}>
            NAME IN FULL
          </div>
          <div style={{ 
            flex: 1, 
            textAlign: 'left',
            paddingLeft: '10px',
            fontWeight: 'bold',
            backgroundColor: 'white',
            color: 'black'
          }}>
            {formData.fullName}
            <span style={{ marginLeft: '20px', color: 'black' }}>
              Tel No: {formData.contact}
            </span>
          </div>
          <div style={{ 
            width: '120px', 
            textAlign: 'center',
            fontWeight: 'bold',
            backgroundColor: '#800000',
            color: '#e6be8a',
            borderLeft: '1px solid white',
          }}>
            الاسم الكامل
          </div>
        </div>

        {/* Next of Kin */}
        <div style={{ 
          display: 'flex', 
          borderBottom: '1px solid #000',
          backgroundColor: 'white',
        }}>
          <div style={{ 
            width: '150px', 
            padding: '3px',
            fontWeight: 'bold',
            fontSize: '10px',
          }}>
            NEXT OF KIN:
          </div>
          <div style={{ 
            flex: 1, 
            padding: '3px',
            fontWeight: 'bold',
            fontSize: '10px',
          }}>
            {formData.nextOfKinName || formData.emergencyContact || 'MERCY KAZUNGU'}
          </div>
          <div style={{ 
            width: '300px', 
            padding: '3px',
            fontWeight: 'bold',
            fontSize: '10px',
          }}>
            TEL NO: {formData.emergencyContact || '0701400955'}
          </div>
        </div>

        {/* Kinship */}
        <div style={{ 
          display: 'flex', 
          borderBottom: '1px solid #000',
          backgroundColor: 'white',
        }}>
          <div style={{ 
            width: '150px', 
            padding: '3px',
            fontWeight: 'bold',
            fontSize: '10px',
          }}>
            KINSHIP:
          </div>
          <div style={{ 
            width: '100px', 
            padding: '3px',
            fontWeight: 'bold',
            fontSize: '10px',
          }}>
            {formData.kinship || 'SISTER'}
          </div>
          <div style={{ 
            flex: 1, 
            padding: '3px',
            fontWeight: 'bold',
            fontSize: '10px',
          }}>
            {formData.otherEmergencyInfo || '0728773569 (WILLIAM-Cuz, BRO)'}
          </div>
        </div>

        {/* Main table with profile photo and details */}
        <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
          {/* Left column - Profile photo */}
          <div style={{ 
            width: '25%', 
            borderRight: '1px solid #000',
            padding: '10px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
            <div style={{
              position: 'relative',
              width: '110px',
              maxHeight: '110px',
              cursor: previewMode ? 'default' : 'pointer',
            }}
            onClick={() => !previewMode && document.getElementById('profile-upload-t4')?.click()}
            >
              {formData.profilePhoto ? (
                <img 
                  src={formData.profilePhoto} 
                  alt="Profile Photo" 
                  style={{ 
                    width: '100%',
                    maxHeight: '110px',
                    objectFit: 'contain',
                  }}
                />
              ) : (
                <div style={{
                  width: '110px',
                  maxHeight: '110px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px dashed #ccc',
                  backgroundColor: '#f9f9f9',
                }}>
                  Profile Photo
                </div>
              )}
              {!previewMode && (
                <div style={{
                  position: 'absolute',
                  bottom: '5px',
                  right: '5px',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  padding: '2px 5px',
                  borderRadius: '3px',
                  fontSize: '8px',
                }}>
                  Click to change
                </div>
              )}
            </div>
            {!previewMode && (
              <input 
                type="file" 
                id="profile-upload-t4"
                accept="image/*" 
                onChange={handleProfilePhotoChange}
                style={{ display: 'none' }}
              />
            )}
          </div>

          {/* Right column - REF.NO, DATE, TO, etc. */}
          <div style={{ width: '75%' }}>
            {/* First row: REF.NO/DATE/TO */}
            <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
              <div style={{
                width: '20%',
                backgroundColor: '#800000',
                color: 'white',
                padding: '5px',
                fontWeight: 'bold',
                textAlign: 'center',
                borderRight: '1px solid #000',
              }}>
                REF.<br/>NO
              </div>
              <div style={{
                width: '10%',
                padding: '5px',
                fontWeight: 'bold',
                textAlign: 'center',
                borderRight: '1px solid #000',
              }}>
                {formData.referenceNumber || '001'}
              </div>
              <div style={{
                width: '15%',
                backgroundColor: '#800000',
                color: 'white',
                padding: '5px',
                fontWeight: 'bold',
                textAlign: 'center',
                borderRight: '1px solid #000',
              }}>
                DATE
              </div>
              <div style={{
                width: '20%',
                padding: '5px',
                fontWeight: 'bold',
                textAlign: 'center',
                borderRight: '1px solid #000',
              }}>
                {formData.cvDate || '18/JUL/2025'}
              </div>
              <div style={{
                width: '15%',
                backgroundColor: '#800000',
                color: 'white',
                padding: '5px',
                fontWeight: 'bold',
                textAlign: 'center',
                borderRight: '1px solid #000',
              }}>
                TO
              </div>
              <div style={{
                width: '20%',
                padding: '5px',
                fontWeight: 'bold',
                textAlign: 'center',
              }}>
                {formData.destination || formData.workCountry || 'SAUDI ARABIA'}
              </div>
            </div>

            {/* Second row: BROKER/TEL */}
            <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
              <div style={{
                width: '20%',
                padding: '5px',
                fontWeight: 'bold',
                borderRight: '1px solid #000',
              }}>
                BROKER:
              </div>
              <div style={{
                width: '45%',
                padding: '5px',
                fontWeight: 'bold',
                borderRight: '1px solid #000',
              }}>
                {formData.companyName}
              </div>
              <div style={{
                width: '15%',
                padding: '5px',
                fontWeight: 'bold',
                textAlign: 'right',
                borderRight: '1px solid #000',
              }}>
                Tel:
              </div>
              <div style={{
                width: '20%',
                padding: '5px',
                fontWeight: 'bold',
                textAlign: 'center',
              }}>
                {formData.contactNumber}
              </div>
            </div>

            {/* Third row: Post applied for */}
            <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
              <div style={{
                width: '20%',
                backgroundColor: '#e6be8a',
                padding: '5px',
                fontWeight: 'bold',
                borderRight: '1px solid #000',
              }}>
                Post<br/>applied<br/>for
              </div>
              <div style={{
                width: '60%',
                padding: '5px',
                fontWeight: 'bold',
                textAlign: 'center',
                borderRight: '1px solid #000',
              }}>
                {formData.position}<br/>
                {formData.destination || formData.workCountry || 'KSA'} (CLIENT)
              </div>
              <div style={{
                width: '20%',
                backgroundColor: '#e6be8a',
                padding: '5px',
                fontWeight: 'bold',
                textAlign: 'right',
              }}>
                الوظيفة
              </div>
            </div>

            {/* Fourth row: Monthly Salary */}
            <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
              <div style={{
                width: '20%',
                backgroundColor: '#e6be8a',
                padding: '5px',
                fontWeight: 'bold',
                borderRight: '1px solid #000',
              }}>
                Monthly<br/>Salary
              </div>
              <div style={{
                width: '60%',
                padding: '5px',
                fontWeight: 'bold',
                textAlign: 'center',
                borderRight: '1px solid #000',
              }}>
                {formData.salary}
              </div>
              <div style={{
                width: '20%',
                backgroundColor: '#e6be8a',
                padding: '5px',
                fontWeight: 'bold',
                textAlign: 'right',
              }}>
                الراتب الشهري
              </div>
            </div>

            {/* Fifth row: Contract period */}
            <div style={{ display: 'flex' }}>
              <div style={{
                width: '20%',
                backgroundColor: '#e6be8a',
                padding: '5px',
                fontWeight: 'bold',
                borderRight: '1px solid #000',
              }}>
                Contract<br/>period
              </div>
              <div style={{
                width: '60%',
                padding: '5px',
                fontWeight: 'bold',
                textAlign: 'center',
                borderRight: '1px solid #000',
              }}>
                {formData.workYears}
              </div>
              <div style={{
                width: '20%',
                backgroundColor: '#e6be8a',
                padding: '5px',
                fontWeight: 'bold',
                textAlign: 'right',
              }}>
                مدة العقد
              </div>
            </div>
          </div>
        </div>

        {/* Main information tables section */}
        <div style={{ display: 'flex' }}>
          {/* Left Side - Applicant Details */}
          <div style={{ width: '50%', borderRight: '1px solid #000' }}>
            {/* Headers */}
            <div style={{
              display: 'flex',
              borderBottom: '1px solid #000',
              backgroundColor: 'white',
            }}>
              <div style={{
                width: '50%',
                padding: '4px',
                fontWeight: 'bold',
                textAlign: 'center',
              }}>
                APPLICANT DETAILS
              </div>
              <div style={{
                width: '50%',
                padding: '4px',
                fontWeight: 'bold',
                textAlign: 'right',
              }}>
                بيانات صاحب العمل
              </div>
            </div>

            {/* Applicant Details Rows */}
            <div style={{ borderBottom: '1px solid #000' }}>
              <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
                <div style={{
                  width: '30%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  borderRight: '1px solid #000',
                }}>
                  Nationality
                </div>
                <div style={{
                  width: '45%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRight: '1px solid #000',
                }}>
                  {formData.nationality}
                </div>
                <div style={{
                  width: '25%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'right',
                  fontSize: '9px',
                }}>
                  الجنسية
                </div>
              </div>

              <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
                <div style={{
                  width: '30%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  borderRight: '1px solid #000',
                }}>
                  Religion
                </div>
                <div style={{
                  width: '45%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRight: '1px solid #000',
                }}>
                  {formData.religion}
                </div>
                <div style={{
                  width: '25%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'right',
                }}>
                  الديانة
                </div>
              </div>

              <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
                <div style={{
                  width: '30%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  borderRight: '1px solid #000',
                }}>
                  Date of Birth
                </div>
                <div style={{
                  width: '45%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRight: '1px solid #000',
                }}>
                  {formData.dateOfBirth}
                </div>
                <div style={{
                  width: '25%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'right',
                }}>
                  تاريخ الميلاد
                </div>
              </div>

              <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
                <div style={{
                  width: '30%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  borderRight: '1px solid #000',
                }}>
                  Age
                </div>
                <div style={{
                  width: '45%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRight: '1px solid #000',
                }}>
                  {formData.age}
                </div>
                <div style={{
                  width: '25%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'right',
                }}>
                  العمر
                </div>
              </div>

              <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
                <div style={{
                  width: '30%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  borderRight: '1px solid #000',
                }}>
                  Place of birth
                </div>
                <div style={{
                  width: '45%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRight: '1px solid #000',
                }}>
                  {formData.placeOfBirth}
                </div>
                <div style={{
                  width: '25%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'right',
                }}>
                  مكان الميلاد
                </div>
              </div>

              <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
                <div style={{
                  width: '30%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  borderRight: '1px solid #000',
                }}>
                  Living Area
                </div>
                <div style={{
                  width: '45%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRight: '1px solid #000',
                }}>
                  {formData.address}
                </div>
                <div style={{
                  width: '25%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'right',
                }}>
                  مكان السكن
                </div>
              </div>

              <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
                <div style={{
                  width: '30%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  borderRight: '1px solid #000',
                }}>
                  Marital Status
                </div>
                <div style={{
                  width: '45%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRight: '1px solid #000',
                }}>
                  {formData.civilStatus}
                </div>
                <div style={{
                  width: '25%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'right',
                }}>
                  الحالة الاجتماعية
                </div>
              </div>

              <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
                <div style={{
                  width: '30%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  borderRight: '1px solid #000',
                }}>
                  Children
                </div>
                <div style={{
                  width: '70%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                }}>
                  {formData.numberOfKids}
                </div>
              </div>

              <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
                <div style={{
                  width: '30%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  borderRight: '1px solid #000',
                }}>
                  Weight
                </div>
                <div style={{
                  width: '45%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRight: '1px solid #000',
                }}>
                  {formData.weight}
                </div>
                <div style={{
                  width: '25%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'right',
                }}>
                  الوزن
                </div>
              </div>

              <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
                <div style={{
                  width: '30%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  borderRight: '1px solid #000',
                }}>
                  Height
                </div>
                <div style={{
                  width: '45%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRight: '1px solid #000',
                }}>
                  {formData.height}
                </div>
                <div style={{
                  width: '25%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'right',
                }}>
                  الطول
                </div>
              </div>

              <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
                <div style={{
                  width: '30%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  borderRight: '1px solid #000',
                }}>
                  Complexion
                </div>
                <div style={{
                  width: '45%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRight: '1px solid #000',
                }}>
                  N/A
                </div>
                <div style={{
                  width: '25%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'right',
                }}>
                  لون البشرة
                </div>
              </div>
            </div>

            {/* Educational Qualifications */}
            <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
              <div style={{
                width: '40%',
                backgroundColor: '#e6be8a',
                padding: '5px',
                fontWeight: 'bold',
                borderRight: '1px solid #000',
              }}>
                Educational<br/>Qualifications
              </div>
              <div style={{
                width: '35%',
                padding: '5px',
                borderRight: '1px solid #000',
              }}>
                <div style={{ color: '#0066cc', fontWeight: 'bold' }}>{formData.educationLevel || 'KOLEWA Primary School'}</div>
                <div style={{ fontWeight: 'bold' }}>1994-2007</div>
                <div style={{ fontWeight: 'bold' }}>N/A</div>
              </div>
              <div style={{
                width: '25%',
                backgroundColor: '#e6be8a',
                padding: '5px',
                fontWeight: 'bold',
                textAlign: 'right',
              }}>
                الدرجة العلمية
              </div>
            </div>

            {/* Languages Section */}
            <div style={{ borderBottom: '1px solid #000' }}>
              <div style={{ 
                display: 'flex', 
                padding: '4px', 
                fontWeight: 'bold', 
                borderBottom: '1px solid #000',
                backgroundColor: 'white',
              }}>
                <div style={{
                  width: '50%',
                  textAlign: 'center',
                }}>
                  LANGUAGES
                </div>
                <div style={{
                  width: '50%',
                  textAlign: 'right',
                }}>
                  إجادة اللغات
                </div>
              </div>

              {/* Language Headers */}
              <div style={{ display: 'flex', borderBottom: '1px solid #000', backgroundColor: '#800000', color: 'white' }}>
                <div style={{
                  width: '25%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRight: '1px solid #000',
                }}>
                  ENGLISH
                </div>
                <div style={{
                  width: '25%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'right',
                  borderRight: '1px solid #000',
                }}>
                  إنجليزي
                </div>
                <div style={{
                  width: '25%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRight: '1px solid #000',
                }}>
                  ARABIC
                </div>
                <div style={{
                  width: '25%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'right',
                }}>
                  عربي
                </div>
              </div>

              {/* POOR */}
              <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
                <div style={{
                  width: '25%',
                  padding: '4px',
                  fontWeight: 'bold',
                  backgroundColor: '#e6be8a',
                  borderRight: '1px solid #000',
                }}>
                  POOR
                </div>
                <div style={{
                  width: '25%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRight: '1px solid #000',
                }}>
                  {formData.englishLevel === 'POOR' ? '✓' : ''}
                </div>
                <div style={{
                  width: '25%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRight: '1px solid #000',
                }}>
                  {formData.arabicLevel === 'POOR' ? '✓' : ''}
                </div>
                <div style={{
                  width: '25%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'right',
                  backgroundColor: '#e6be8a',
                }}>
                  لا شيء
                </div>
              </div>

              {/* LITTLE */}
              <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
                <div style={{
                  width: '25%',
                  padding: '4px',
                  fontWeight: 'bold',
                  backgroundColor: '#e6be8a',
                  borderRight: '1px solid #000',
                }}>
                  LITTLE
                </div>
                <div style={{
                  width: '25%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRight: '1px solid #000',
                }}>
                  {formData.englishLevel === 'LITTLE' && (
                    <div style={{ 
                      width: '24px', 
                      height: '24px', 
                      borderRadius: '50%', 
                      backgroundColor: 'green',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold',
                      margin: '0 auto'
                    }}>
                      ✓
                    </div>
                  )}
                </div>
                <div style={{
                  width: '25%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRight: '1px solid #000',
                }}>
                  {formData.arabicLevel === 'LITTLE' ? '✓' : ''}
                </div>
                <div style={{
                  width: '25%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'right',
                  backgroundColor: '#e6be8a',
                }}>
                  قليل
                </div>
              </div>

              {/* FAIR */}
              <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
                <div style={{
                  width: '25%',
                  padding: '4px',
                  fontWeight: 'bold',
                  backgroundColor: '#e6be8a',
                  borderRight: '1px solid #000',
                }}>
                  FAIR
                </div>
                <div style={{
                  width: '25%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRight: '1px solid #000',
                }}>
                  {formData.englishLevel === 'FAIR' ? '✓' : ''}
                </div>
                <div style={{
                  width: '25%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRight: '1px solid #000',
                }}>
                  {formData.arabicLevel === 'FAIR' && (
                    <div style={{ 
                      width: '24px', 
                      height: '24px', 
                      borderRadius: '50%', 
                      backgroundColor: 'green',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold',
                      margin: '0 auto'
                    }}>
                      ✓
                    </div>
                  )}
                </div>
                <div style={{
                  width: '25%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'right',
                  backgroundColor: '#e6be8a',
                }}>
                  وسط
                </div>
              </div>

              {/* FLUENT */}
              <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
                <div style={{
                  width: '25%',
                  padding: '4px',
                  fontWeight: 'bold',
                  backgroundColor: '#e6be8a',
                  borderRight: '1px solid #000',
                }}>
                  FLUENT
                </div>
                <div style={{
                  width: '25%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRight: '1px solid #000',
                }}>
                  {formData.englishLevel === 'FLUENT' ? '✓' : ''}
                </div>
                <div style={{
                  width: '25%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRight: '1px solid #000',
                }}>
                  {formData.arabicLevel === 'FLUENT' ? '✓' : ''}
                </div>
                <div style={{
                  width: '25%',
                  padding: '4px',
                  fontWeight: 'bold',
                }}>
                  {/* Empty cell */}
                </div>
              </div>
            </div>

            {/* Previous Employment */}
            <div style={{ borderBottom: '1px solid #000' }}>
              <div style={{ 
                display: 'flex', 
                borderBottom: '1px solid #000',
                backgroundColor: 'white',
              }}>
                <div style={{
                  width: '100%',
                  padding: '4px',
                  fontWeight: 'bold',
                  color: 'black',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}>
                  <span>PREVIOUS EMPLOYMENT ABROAD</span>
                  <span>الخبرة خارج البلاد</span>
                </div>
              </div>

              {/* Country */}
              <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
                <div style={{
                  width: '30%',
                  padding: '4px',
                  fontWeight: 'bold',
                  backgroundColor: '#800000',
                  color: 'white',
                  borderRight: '1px solid #000',
                }}>
                  COUNTRY
                </div>
                <div style={{
                  width: '45%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRight: '1px solid #000',
                }}>
                  {formData.workCountry}
                </div>
                <div style={{
                  width: '25%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'right',
                  backgroundColor: '#800000',
                  color: 'white',
                }}>
                  البلد
                </div>
              </div>

              {/* Period */}
              <div style={{ display: 'flex' }}>
                <div style={{
                  width: '30%',
                  padding: '4px',
                  fontWeight: 'bold',
                  backgroundColor: '#e6be8a',
                  borderRight: '1px solid #000',
                }}>
                  PERIOD
                </div>
                <div style={{
                  width: '45%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRight: '1px solid #000',
                }}>
                  {formData.workYears}
                </div>
                <div style={{
                  width: '25%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'right',
                  backgroundColor: '#e6be8a',
                }}>
                  المدة
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Passport Details and Full Photo */}
          <div style={{ width: '50%' }}>
            {/* Headers */}
            <div style={{
              display: 'flex',
              borderBottom: '1px solid #000',
              backgroundColor: 'white',
            }}>
              <div style={{
                width: '50%',
                padding: '4px',
                fontWeight: 'bold',
                textAlign: 'center',
              }}>
                PASSPORT DETAILS
              </div>
              <div style={{
                width: '50%',
                padding: '4px',
                fontWeight: 'bold',
                textAlign: 'right',
              }}>
                بيانات جواز السفر
              </div>
            </div>

            {/* Passport Details Rows */}
            <div style={{ borderBottom: '1px solid #000' }}>
              <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
                <div style={{
                  width: '30%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  borderRight: '1px solid #000',
                }}>
                  Passport No.
                </div>
                <div style={{
                  width: '45%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRight: '1px solid #000',
                }}>
                  {formData.passportNo}
                </div>
                <div style={{
                  width: '25%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'right',
                }}>
                  رقم الجواز
                </div>
              </div>

              <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
                <div style={{
                  width: '30%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  borderRight: '1px solid #000',
                }}>
                  Date of Issue
                </div>
                <div style={{
                  width: '45%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRight: '1px solid #000',
                }}>
                  {formData.dateIssued}
                </div>
                <div style={{
                  width: '25%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'right',
                }}>
                  تاريخ الاصدار
                </div>
              </div>

              <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
                <div style={{
                  width: '30%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  borderRight: '1px solid #000',
                }}>
                  Place of<br/>Issue
                </div>
                <div style={{
                  width: '45%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRight: '1px solid #000',
                }}>
                  {formData.placeIssued}
                </div>
                <div style={{
                  width: '25%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'right',
                }}>
                  مكان الاصدار
                </div>
              </div>

              <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
                <div style={{
                  width: '30%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  borderRight: '1px solid #000',
                }}>
                  Date of Exp.
                </div>
                <div style={{
                  width: '45%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRight: '1px solid #000',
                }}>
                  {formData.dateExpiry}
                </div>
                <div style={{
                  width: '25%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'right',
                }}>
                  تاريخ الانتهاء
                </div>
              </div>

              <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
                <div style={{
                  width: '30%',
                  backgroundColor: '#e6be8a',
                  padding: '4px',
                  fontWeight: 'bold',
                  borderRight: '1px solid #000',
                }}>
                  ID Number:
                </div>
                <div style={{
                  width: '70%',
                  padding: '4px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                }}>
                  30132445
                </div>
              </div>
            </div>

            {/* Full Body Photo */}
            <div style={{ 
              backgroundColor: 'white',
              padding: '4px',
              fontWeight: 'bold',
              textAlign: 'center',
              borderBottom: '1px solid #000',
            }}>
              PHOTO
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              padding: '10px',
              maxHeight: '280px',
              borderBottom: '1px solid #000',
              position: 'relative',
              cursor: previewMode ? 'default' : 'pointer',
            }}
            onClick={() => !previewMode && document.getElementById('full-body-upload-t4')?.click()}
            >
              {formData.fullBodyPhoto ? (
                <img 
                  src={formData.fullBodyPhoto} 
                  alt="Full Body Photo" 
                  style={{ 
                    maxHeight: '280px',
                    maxWidth: '100%',
                    objectFit: 'contain',
                  }}
                />
              ) : (
                <div style={{
                  maxHeight: '280px',
                  width: '80%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px dashed #ccc',
                  backgroundColor: '#f9f9f9',
                }}>
                  Full Body Photo
                </div>
              )}
              {!previewMode && (
                <div style={{
                  position: 'absolute',
                  bottom: '10px',
                  right: '10px',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '10px',
                }}>
                  Click to change
                </div>
              )}
            </div>
            {!previewMode && (
              <input 
                type="file" 
                id="full-body-upload-t4"
                accept="image/*" 
                onChange={handleFullBodyPhotoChange}
                style={{ display: 'none' }}
              />
            )}

            {/* Sickness text */}
            <div style={{ 
              padding: '4px',
              fontWeight: 'bold',
            }}>
              CASE OF OLD SICKNESS : N/A
            </div>
          </div>
        </div>

        {/* Work Experience Section */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #000',
          backgroundColor: 'white',
        }}>
          <div style={{
            width: '100%',
            padding: '4px',
            fontWeight: 'bold',
            display: 'flex',
          }}>
            <span style={{ marginRight: '50px' }}>WORK EXPERIENCE</span>
            <span>خبرة العمل</span>
          </div>
        </div>

        {/* Skills Headers */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #000',
        }}>
          <div style={{
            width: '33.33%',
            padding: '4px',
            fontWeight: 'bold',
            textAlign: 'center',
            borderRight: '1px solid #000',
            backgroundColor: '#e6be8a',
          }}>
            <div style={{ 
              fontWeight: 'bold',
              padding: '4px',
              textAlign: 'center',
            }}>
              Baby Sitting
            </div>
            <div style={{ 
              textAlign: 'center', 
              fontSize: '10px',
              padding: '4px',
            }}>
              جليسة أطفال
            </div>
          </div>
          <div style={{
            width: '33.33%',
            padding: '4px',
            fontWeight: 'bold',
            textAlign: 'center',
            borderRight: '1px solid #000',
            backgroundColor: '#e6be8a',
          }}>
            <div style={{ 
              fontWeight: 'bold',
              padding: '4px',
              textAlign: 'center',
            }}>
              Cleaning
            </div>
            <div style={{ 
              textAlign: 'center', 
              fontSize: '10px',
              padding: '4px',
            }}>
              التنظيف
            </div>
          </div>
          <div style={{
            width: '33.33%',
            padding: '4px',
            fontWeight: 'bold',
            textAlign: 'center',
            backgroundColor: '#e6be8a',
          }}>
            <div style={{ 
              fontWeight: 'bold',
              padding: '4px',
              textAlign: 'center',
            }}>
              Washing
            </div>
            <div style={{ 
              textAlign: 'center', 
              fontSize: '10px',
              padding: '4px',
            }}>
              الغسيل
            </div>
          </div>
        </div>

        {/* Skills Checkmarks */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #000',
        }}>
          <div style={{
            width: '33.33%',
            padding: '12px',
            fontWeight: 'bold',
            textAlign: 'center',
            borderRight: '1px solid #000',
          }}>
            {formData.allSkills.find(s => s.name === 'BABYSITTING' && s.checked) ? '✓' : ''}
          </div>
          <div style={{
            width: '33.33%',
            padding: '12px',
            fontWeight: 'bold',
            textAlign: 'center',
            borderRight: '1px solid #000',
          }}>
            {formData.allSkills.find(s => s.name === 'CLEANING' && s.checked) ? '✓' : ''}
          </div>
          <div style={{
            width: '33.33%',
            padding: '12px',
            fontWeight: 'bold',
            textAlign: 'center',
          }}>
            {formData.allSkills.find(s => s.name === 'WASHING' && s.checked) ? '✓' : ''}
          </div>
        </div>

        {/* Hobbies Section */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #000',
        }}>
          <div style={{
            width: '33.33%',
            fontWeight: 'bold',
            textAlign: 'center',
            backgroundColor: '#e6be8a',
            padding: '4px',
            borderRight: '1px solid #000',
          }}>
            Hobbies
          </div>
          <div style={{
            width: '66.67%',
            padding: '4px',
            fontWeight: 'bold',
            backgroundColor: 'white',
            fontSize: '9px',
          }}>
            1.COOKING  2.GOING OUT  3.HOUSE CHORES
          </div>
        </div>

        {/* Skills Row */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #000',
        }}>
          <div style={{
            width: '33.33%',
            padding: '4px',
            fontWeight: 'bold',
            textAlign: 'center',
            borderRight: '1px solid #000',
            backgroundColor: '#e6be8a',
          }}>
            SKILLS
          </div>
          <div style={{
            width: '66.67%',
            padding: '4px',
            fontWeight: 'bold',
            fontSize: '9px',
          }}>
            {formData.remarks || 'COOPERATIVE / MEDIUM BODY BUILD / HARDWORKING / EXPERIENCED'}
          </div>
        </div>
      </div>
    );
  };

  // Template 5 - NAIM INVESTMENTS LIMITED (Arabic Style)
  const Template5Preview = () => {
    // Function to handle profile photo upload
    const handleProfilePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFileChange('profilePhoto', e.target.files ? e.target.files[0] : null);
    };

    // Function to handle full body photo upload
    const handleFullBodyPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFileChange('fullBodyPhoto', e.target.files ? e.target.files[0] : null);
    };

    // Function to handle logo upload
    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFileChange('logoFile', e.target.files ? e.target.files[0] : null);
    };

    return (
      <div style={{ 
        fontFamily: 'Arial, sans-serif',
        background: 'white',
        padding: '15px',
        boxShadow: '0 0 20px rgba(0,0,0,0.1)',
        maxWidth: '800px',
        margin: '0 auto'
      }}>
        {/* Header with Logo, Title and Profile Photo */}
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '15px' }}>
          {/* Logo Upload Area */}
          <div style={{ width: '120px', marginLeft: '10px' }}>
            <div style={{ width: '120px', height: '90px', position: 'relative' }}>
              {formData.logoFile ? (
                <img 
                  src={typeof formData.logoFile === 'string' ? formData.logoFile : URL.createObjectURL(formData.logoFile)}
                  alt="Company Logo" 
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              ) : (
                <div style={{
                  width: '120px',
                  height: '90px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px dashed #ccc',
                  backgroundColor: '#f9f9f9',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                onClick={() => document.getElementById('logo-upload-template5')?.click()}
                >
                  <div style={{ fontSize: '10px', textAlign: 'center' }}>
                    Click to add logo
                  </div>
                </div>
              )}
              <input 
                type="file" 
                id="logo-upload-template5"
                accept="image/*" 
                onChange={handleLogoChange}
                style={{ display: 'none' }}
              />
            </div>
          </div>

          {/* Title Section */}
          <div style={{ flex: 1, padding: '0 20px' }}>
            <div style={{ textAlign: 'center', color: 'black', fontSize: '22px', fontWeight: 'bold', marginBottom: '5px' }}>
              NAIM INVESTMENTS LIMITED
            </div>
            <div style={{ textAlign: 'center', fontSize: '10px', color: '#666', lineHeight: '1.4' }}>
              <div style={{ display: 'inline-block', textAlign: 'left' }}>
                <div>Email: admin@naiminvestments.com</div>
                <div>Website: www.naiminvestments.com</div>
                <div>Contact: +254720931164 or +254799659792</div>
              </div>
            </div>
            
            {/* APPLICATION FOR EMPLOYMENT Title */}
            <div style={{ textAlign: 'center', marginTop: '15px' }}>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: 'black', marginBottom: '5px' }}>
                APPLICATION FOR EMPLOYMENT
              </div>
              <div style={{ fontSize: '14px', color: 'black', marginBottom: '3px' }}>
                استمارة طلب عمل
              </div>
              <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'black' }}>
                NAIM AGENCY-KENYA
              </div>
            </div>
          </div>

          {/* Profile Photo */}
          <div style={{
            width: '120px',
            height: '140px',
            padding: '2px',
            cursor: 'pointer'
          }}
          onClick={() => document.getElementById('profile-upload-template5')?.click()}>
            {formData.profilePhoto ? (
              <img 
                src={typeof formData.profilePhoto === 'string' ? formData.profilePhoto : URL.createObjectURL(formData.profilePhoto)}
                alt="Profile" 
                style={{ 
                  width: '100%', 
                  height: '100%',
                  objectFit: 'contain',
                  objectPosition: 'center',
                  backgroundColor: '#f5f5f5'
                }}
              />
            ) : (
              <div style={{
                background: '#f5f5f5',
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#999',
                fontSize: '12px',
                textAlign: 'center'
              }}>
                <div>
                  <svg width="40" height="40" fill="#ccc" viewBox="0 0 24 24">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                  <br />Profile Photo
                </div>
              </div>
            )}
            <input 
              type="file" 
              id="profile-upload-template5"
              style={{ display: 'none' }}
              accept="image/*"
              onChange={handleProfilePhotoChange}
            />
          </div>
        </div>

        {/* Name and Age Bar */}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
          <tr>
            <td style={{ 
              background: '#f0f0f0', 
              width: '70%', 
              padding: '6px',
              border: '1px solid #000',
              fontWeight: 'bold',
              fontSize: '11px',
              color: 'black'
            }}>
              NAME: {formData.fullName || 'SCHOLAR MJENI BENDERA'}
            </td>
            <td style={{ 
              background: '#f0f0f0', 
              padding: '6px',
              border: '1px solid #000',
              fontWeight: 'bold',
              fontSize: '11px',
              color: 'black'
            }}>
              AGE: {formData.age || '22'} YEARS
            </td>
          </tr>
        </table>

        {/* Main Content with Full Body Photo and Tables */}
        <div style={{ display: 'flex', gap: '0' }}>
          {/* Left Side - Full Body Photo with Ref/Religion table above */}
          <div style={{ width: '35%' }}>
            {/* Basic Info Table positioned above full body photo */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '0' }}>
              <tr>
                <td style={{ 
                  background: '#e0e0e0', 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>Ref</td>
                <td style={{ 
                  background: '#e0e0e0', 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>Religion</td>
                <td style={{ 
                  background: '#e0e0e0', 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>Height</td>
                <td style={{ 
                  background: '#e0e0e0', 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>Weight</td>
              </tr>
              <tr>
                <td style={{ 
                  textAlign: 'center',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>{formData.referenceNumber || 'GDAL09/S'}</td>
                <td style={{ 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>{formData.religion || 'CHRISTIAN'}</td>
                <td style={{ 
                  textAlign: 'center',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>{formData.height || '5.4 FT'}</td>
                <td style={{ 
                  textAlign: 'center',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>{formData.weight || '54 KGs'}</td>
              </tr>
            </table>
            
            {/* Full Body Photo */}
            <div style={{
              width: '100%',
              height: '380px',
              border: '2px solid #000',
              padding: '3px',
              cursor: 'pointer'
            }}
            onClick={() => document.getElementById('fullbody-upload-template5')?.click()}>
              {formData.fullBodyPhoto ? (
                <img 
                  src={typeof formData.fullBodyPhoto === 'string' ? formData.fullBodyPhoto : URL.createObjectURL(formData.fullBodyPhoto)}
                  alt="Full Body" 
                  style={{ 
                    width: '100%', 
                    height: '100%',
                    objectFit: 'contain',
                    objectPosition: 'center',
                    backgroundColor: '#f5f5f5'
                  }}
                />
              ) : (
                <div style={{
                  background: '#f5f5f5',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#999',
                  fontSize: '12px',
                  textAlign: 'center'
                }}>
                  <div>
                    <svg width="60" height="80" fill="#ccc" viewBox="0 0 24 24">
                      <path d="M12 2C13.1 2 14 2.9 14 4S13.1 6 12 6 10 5.1 10 4 10.9 2 12 2M12.5 7H11.5C10.7 7 10 7.7 10 8.5V11H8V22H11V18H13V22H16V11H14V8.5C14 7.7 13.3 7 12.5 7Z"/>
                    </svg>
                    <br />Full Body Photo
                  </div>
                </div>
              )}
              <input 
                type="file" 
                id="fullbody-upload-template5"
                style={{ display: 'none' }}
                accept="image/*"
                onChange={handleFullBodyPhotoChange}
              />
            </div>
          </div>

          {/* Right Side - Position and Details Tables */}
          <div style={{ width: '65%', marginLeft: '5px' }}>
            {/* Position Applied Table */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '0' }}>
              <tr>
                <td style={{ 
                  background: '#e0e0e0', 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  width: '35%',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>Position Applied</td>
                <td style={{ 
                  background: '#e0e0e0', 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }} colSpan={2}>{formData.position || 'HOUSEMAID'}</td>
              </tr>
              <tr>
                <td style={{ 
                  background: '#e0e0e0', 
                  textAlign: 'center',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>Monthly Salary</td>
                <td style={{ 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>{formData.salary || '900 SAR'}</td>
                <td style={{ 
                  background: '#e0e0e0', 
                  textAlign: 'right',
                  width: '30%',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>الراتب الشهري</td>
              </tr>
              <tr>
                <td style={{ 
                  background: '#e0e0e0', 
                  textAlign: 'center',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>Contract Period</td>
                <td style={{ 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>{formData.contractPeriod || '2 YEARS'}</td>
                <td style={{ 
                  background: '#e0e0e0', 
                  textAlign: 'right',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>مدة العقد</td>
              </tr>
              <tr>
                <td style={{ 
                  background: '#e0e0e0', 
                  textAlign: 'center',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>Passport No.</td>
                <td style={{ 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>{formData.passportNo || 'AK1834339'}</td>
                <td style={{ 
                  background: '#e0e0e0', 
                  textAlign: 'right',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>رقم جواز السفر</td>
              </tr>
            </table>

            {/* Details of Application Header with Arabic */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0' }}>
              <tr>
                <td style={{ 
                  background: '#e0e0e0', 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  color: 'black',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px'
                }} colSpan={3}>
                  Details of Application &nbsp;&nbsp;&nbsp;&nbsp; تفاصيل الطلب
                </td>
              </tr>
              <tr>
                <td style={{ 
                  background: '#e0e0e0',
                  width: '30%',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>Nationality</td>
                <td style={{ 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>{formData.nationality || 'KENYAN'}</td>
                <td style={{ 
                  background: '#e0e0e0', 
                  textAlign: 'right',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>الجنسية</td>
              </tr>
              <tr>
                <td style={{ 
                  background: '#e0e0e0',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>Resident</td>
                <td style={{ 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>{formData.address || 'MOMBASA'}</td>
                <td style={{ 
                  background: '#e0e0e0', 
                  textAlign: 'right',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>مكان السكن</td>
              </tr>
              <tr>
                <td style={{ 
                  background: '#e0e0e0',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>Date of Birth</td>
                <td style={{ 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>{formData.dateOfBirth || '24/MAY/2003'}</td>
                <td style={{ 
                  background: '#e0e0e0', 
                  textAlign: 'right',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>تاريخ الميلاد</td>
              </tr>
              <tr>
                <td style={{ 
                  background: '#e0e0e0',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>Place of Birth</td>
                <td style={{ 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>{formData.placeOfBirth || 'KWALE, KEN'}</td>
                <td style={{ 
                  background: '#e0e0e0', 
                  textAlign: 'right',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>مكان الميلاد</td>
              </tr>
              <tr>
                <td style={{ 
                  background: '#e0e0e0',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>Marital Status</td>
                <td style={{ 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>{formData.civilStatus || 'SINGLE'}</td>
                <td style={{ 
                  background: '#e0e0e0', 
                  textAlign: 'right',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>الحالة الاجتماعية</td>
              </tr>
              <tr>
                <td style={{ 
                  background: '#e0e0e0',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>No. of Children</td>
                <td style={{ 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>{formData.numberOfKids || '0'}</td>
                <td style={{ 
                  background: '#e0e0e0', 
                  textAlign: 'right',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>عدد الأطفال</td>
              </tr>
            </table>

            {/* Languages & Education */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0' }}>
              <tr>
                <td style={{ 
                  background: '#e0e0e0', 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  color: 'black',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px'
                }} colSpan={3}>
                  Languages & Education &nbsp;&nbsp;&nbsp;&nbsp; اللغة والتعليم
                </td>
              </tr>
              <tr>
                <td style={{ 
                  background: '#e0e0e0',
                  width: '30%',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>English</td>
                <td style={{ 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>{formData.englishLevel || 'GOOD'}</td>
                <td style={{ 
                  background: '#e0e0e0', 
                  textAlign: 'right',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>الإنجليزية</td>
              </tr>
              <tr>
                <td style={{ 
                  background: '#e0e0e0',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>Arabic</td>
                <td style={{ 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>{formData.arabicLevel || 'NO'}</td>
                <td style={{ 
                  background: '#e0e0e0', 
                  textAlign: 'right',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>العربية</td>
              </tr>
              <tr>
                <td style={{ 
                  background: '#e0e0e0',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>Education</td>
                <td style={{ 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>{formData.educationLevel || 'HIGH SCHOOL LEVEL'}</td>
                <td style={{ 
                  background: '#e0e0e0', 
                  textAlign: 'right',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>المستوى التعليمي</td>
              </tr>
            </table>

            {/* Previous Employment Abroad */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0' }}>
              <tr>
                <td style={{ 
                  background: '#e0e0e0', 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  color: 'black',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px'
                }} colSpan={3}>
                  Previous Employment Abroad &nbsp;&nbsp;&nbsp;&nbsp; خبرة خارج البلاد
                </td>
              </tr>
              <tr>
                <td style={{ 
                  background: '#e0e0e0', 
                  textAlign: 'center',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>Period</td>
                <td style={{ 
                  background: '#e0e0e0', 
                  textAlign: 'center',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>Position</td>
                <td style={{ 
                  background: '#e0e0e0', 
                  textAlign: 'center',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>City, Country</td>
              </tr>
              <tr>
                <td style={{ 
                  textAlign: 'center',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>{formData.workYears || 'N/A'}</td>
                <td style={{ 
                  textAlign: 'center',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>{formData.workPosition || 'N/A'}</td>
                <td style={{ 
                  textAlign: 'center',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>{formData.workCountry || 'N/A'}</td>
              </tr>
            </table>

            {/* Inside Country Employment */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0' }}>
              <tr>
                <td style={{ 
                  background: '#e0e0e0', 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }} colSpan={3}>
                  Inside Country Employment
                </td>
              </tr>
              <tr>
                <td style={{ 
                  background: '#FFEB3B', 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>{formData.workYears || '2 YEARS'}</td>
                <td style={{ 
                  background: '#FFEB3B', 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>{formData.position || 'HOUSE MAID'}</td>
                <td style={{ 
                  background: '#FFEB3B', 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  border: '1px solid #000',
                  padding: '4px',
                  fontSize: '11px',
                  color: 'black'
                }}>KENYA</td>
              </tr>
            </table>
          </div>
        </div>

        {/* Skills & Experience */}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
          <tr>
            <td style={{ 
              background: '#e0e0e0', 
              textAlign: 'center', 
              fontWeight: 'bold',
              color: 'black',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px'
            }} colSpan={6}>
              Skills & Experience &nbsp;&nbsp;&nbsp;&nbsp; خبرة العمل
            </td>
          </tr>
          <tr>
            <td style={{ 
              background: '#e0e0e0',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>Ironing</td>
            <td style={{ 
              textAlign: 'center', 
              fontWeight: 'bold',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>{formData.allSkills?.find(s => s.name === 'IRONING')?.checked ? 'YES' : 'YES'}</td>
            <td style={{ 
              background: '#e0e0e0', 
              textAlign: 'right',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>الكوي</td>
            <td style={{ 
              background: '#e0e0e0',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>Baby Sitting</td>
            <td style={{ 
              textAlign: 'center', 
              fontWeight: 'bold',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>{formData.allSkills?.find(s => s.name === 'BABYSITTING')?.checked ? 'YES' : 'YES'}</td>
            <td style={{ 
              background: '#e0e0e0', 
              textAlign: 'right',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>عناية الرضيع</td>
          </tr>
          <tr>
            <td style={{ 
              background: '#e0e0e0',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>Cooking</td>
            <td style={{ 
              textAlign: 'center', 
              fontWeight: 'bold',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>{formData.allSkills?.find(s => s.name === 'COOKING')?.checked ? 'YES' : 'YES'}</td>
            <td style={{ 
              background: '#e0e0e0', 
              textAlign: 'right',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>الطبخ</td>
            <td style={{ 
              background: '#e0e0e0',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>Children Care</td>
            <td style={{ 
              textAlign: 'center', 
              fontWeight: 'bold',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>{formData.allSkills?.find(s => s.name === 'CHILDCARE')?.checked ? 'YES' : 'YES'}</td>
            <td style={{ 
              background: '#e0e0e0', 
              textAlign: 'right',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>عناية الأطفال</td>
          </tr>
          <tr>
            <td style={{ 
              background: '#e0e0e0',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>Arabic Cooking</td>
            <td style={{ 
              textAlign: 'center', 
              fontWeight: 'bold',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>{formData.allSkills?.find(s => s.name === 'ARABIC_COOKING')?.checked ? 'YES' : 'NO'}</td>
            <td style={{ 
              background: '#e0e0e0', 
              textAlign: 'right',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>الطبخ العربي</td>
            <td style={{ 
              background: '#e0e0e0',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>Tutoring</td>
            <td style={{ 
              textAlign: 'center', 
              fontWeight: 'bold',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>{formData.allSkills?.find(s => s.name === 'TUTORING')?.checked ? 'YES' : 'NO'}</td>
            <td style={{ 
              background: '#e0e0e0', 
              textAlign: 'right',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>تعليم الأطفال</td>
          </tr>
          <tr>
            <td style={{ 
              background: '#e0e0e0',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>Sewing</td>
            <td style={{ 
              textAlign: 'center', 
              fontWeight: 'bold',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>{formData.allSkills?.find(s => s.name === 'SEWING')?.checked ? 'YES' : 'NO'}</td>
            <td style={{ 
              background: '#e0e0e0', 
              textAlign: 'right',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>الخياطة</td>
            <td style={{ 
              background: '#e0e0e0',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>Cleaning</td>
            <td style={{ 
              textAlign: 'center', 
              fontWeight: 'bold',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>{formData.allSkills?.find(s => s.name === 'CLEANING')?.checked ? 'YES' : 'YES'}</td>
            <td style={{ 
              background: '#e0e0e0', 
              textAlign: 'right',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>التنظيف</td>
          </tr>
          <tr>
            <td style={{ 
              background: '#e0e0e0',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>Other Skills</td>
            <td style={{ 
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }} colSpan={2}></td>
            <td style={{ 
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }} colSpan={2}></td>
            <td style={{ 
              background: '#e0e0e0', 
              textAlign: 'right',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>خبرات أخرى</td>
          </tr>
        </table>

        {/* Remarks */}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '5px' }}>
          <tr>
            <td style={{ 
              background: '#e0e0e0', 
              fontWeight: 'bold',
              width: '15%',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>Remarks</td>
            <td style={{ 
              fontWeight: 'bold',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>{formData.remarks || 'GOOD. HIGHLY DISCIPLINE.'}</td>
            <td style={{ 
              background: '#e0e0e0', 
              textAlign: 'right', 
              fontWeight: 'bold',
              width: '15%',
              border: '1px solid #000',
              padding: '4px',
              fontSize: '11px',
              color: 'black'
            }}>ملاحظات</td>
          </tr>
        </table>
      </div>
    );
  };

  // Template 6 - Domestic Helper (Almelhem layout)
  const Template6Preview = () => {
    const workerDisplayName = (formData.workerName || formData.fullName || 'NAIM').toUpperCase();
    const workerNameBadgeFontSize =
      workerDisplayName.length > 40
        ? '8px'
        : workerDisplayName.length > 34
          ? '9px'
          : workerDisplayName.length > 28
            ? '10px'
            : workerDisplayName.length > 22
              ? '11px'
              : '13px';
    const workerNameBadgeLetterSpacing =
      workerDisplayName.length > 34 ? '0px' : workerDisplayName.length > 24 ? '0.4px' : '1px';
    const profilePhotoSrc = typeof formData.profilePhoto === 'string' ? formData.profilePhoto : '';
    const fullBodyPhotoSrc = typeof formData.fullBodyPhoto === 'string' ? formData.fullBodyPhoto : '';
    const alMelhemLogoSrc = '/assets/al-melhem-logo.png';
    const experienceText = formData.otherExperiences || formData.remarks || '';

    const personalRows = [
      ['full name', 'اسم العاملة', formData.fullName],
      ['Nationality', 'الجنسية', formData.nationality],
      ['Religion', 'الديانة', formData.religion],
      ['Age', 'العمر', formData.age],
      ['Date of birth', 'تاريخ الميلاد', formData.dateOfBirth],
      ['Weight', 'الوزن', formData.weight],
      ['Height', 'الطول', formData.height],
      ['Number of children', 'عدد الأطفال', formData.numberOfKids],
      ['Marital Status', 'الحالة الاجتماعية', formData.civilStatus],
      ['Passport number', 'رقم الجواز', formData.passportNo]
    ] as const;

    const educationRows = [
      ['Education Level', 'مستوى التعليم', formData.educationLevel],
      ['English Language', 'اللغة الإنجليزية', formData.englishLevel],
      ['Arabic Language', 'اللغة العربية', formData.arabicLevel]
    ] as const;

    const workRows = [
      ['Previously Worked', 'سبق لها العمل', formData.previouslyWorked || formData.workCountry],
      ['Duration ( years )', 'المدة ( سنوات )', formData.workYears],
      ['City', 'المدينة', formData.workCity]
    ] as const;

    const rowContainerStyle: React.CSSProperties = {
      borderRadius: '999px',
      background: '#e8e8e8',
      height: '22px',
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr',
      alignItems: 'center',
      padding: '0 8px',
      color: '#d7a42a',
      fontSize: '10px',
      fontWeight: 800
    };

    const renderArabicLabel = (text: string, fontSize: string = '10px') => (
      <span
        style={{
          display: 'inline-flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: '3px',
          fontSize,
          direction: 'ltr'
        }}
      >
        <span>:</span>
        <span style={{ direction: 'rtl' }}>{text}</span>
        <span>●</span>
      </span>
    );

    return (
      <div style={{ background: '#f3f3f3', padding: '12px' }}>
        <section
          className="print-page"
          style={{
            position: 'relative',
            height: '1024px',
            width: '768px',
            overflow: 'hidden',
            background: '#fbf7f0',
            boxShadow: '0 16px 34px rgba(0,0,0,0.18)',
            margin: '0 auto'
          }}
        >
          <div style={{ position: 'absolute', left: '32px', top: '34px', fontSize: '24px', fontWeight: 900, letterSpacing: '7px', color: '#d8a82b' }}>
            DOMESTIC HELPER
          </div>

          <div
            style={{
              position: 'absolute',
              left: '61px',
              top: '106px',
              fontSize: '12px',
              fontWeight: 800,
              letterSpacing: '1px',
              color: '#5a5a5a',
              zIndex: 3,
              background: '#fbf7f0',
              paddingRight: '8px'
            }}
          >
            Worker data :
          </div>
          <div
            style={{
              position: 'absolute',
              left: '172px',
              top: '84px',
              height: '44px',
              width: '304px',
              overflow: 'hidden',
              borderRadius: '6px',
              background: 'linear-gradient(180deg, #9a9a9a 0%, #eeeeee 66%, #ffc400 100%)',
              boxShadow: 'inset 0 1px 8px rgba(0,0,0,0.2)',
              color: 'white',
              fontSize: workerNameBadgeFontSize,
              fontWeight: 900,
              letterSpacing: workerNameBadgeLetterSpacing,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              padding: '0 10px',
              whiteSpace: 'nowrap'
            }}
          >
            {workerDisplayName}
          </div>

          <div style={{ position: 'absolute', right: '44px', top: '16px', width: '356px', height: '148px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <img
              src={alMelhemLogoSrc}
              alt="Al Melhem logo"
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          </div>

          <div
            style={{
              position: 'absolute',
              left: '50px',
              top: '135px',
              height: '452px',
              width: '273px',
              borderRadius: '6px',
              background: '#e8e8e8',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#bdbdbd',
              fontSize: '14px',
              fontWeight: 700
            }}
          >
            {fullBodyPhotoSrc ? (
              <img src={fullBodyPhotoSrc} alt="Full body" style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center', background: '#e8e8e8' }} />
            ) : (
              'FULL BODY PHOTO'
            )}
          </div>

          <div
            style={{
              position: 'absolute',
              left: '376px',
              top: '186px',
              display: 'flex',
              height: '88px',
              width: '103px',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#e8e8e8',
              overflow: 'hidden',
              color: '#bdbdbd',
              fontSize: '10px',
              fontWeight: 700,
              textAlign: 'center'
            }}
          >
            {profilePhotoSrc ? (
              <img src={profilePhotoSrc} alt="Passport size" style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center', background: '#e8e8e8' }} />
            ) : (
              'PASSPORT SIZE PHOTO'
            )}
          </div>

          <div style={{ position: 'absolute', left: '345px', top: '326px', width: '405px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {personalRows.map(([left, right, value]) => (
              <div key={`${left}-${right}`} style={rowContainerStyle}>
                <span>{`• ${left}`}</span>
                <span style={{ textAlign: 'center', color: '#555' }}>{value || ''}</span>
                <span style={{ textAlign: 'right' }}>{renderArabicLabel(right)}</span>
              </div>
            ))}
          </div>

          <div style={{ position: 'absolute', left: '82px', top: '598px', fontSize: '12px', fontWeight: 900, letterSpacing: '1px', color: '#555' }}>
            Contract duration and
            <br />
            Monthly Salary :
          </div>
          <div style={{ position: 'absolute', right: '44px', top: '598px', width: '210px', display: 'flex', justifyContent: 'flex-end' }}>
            <img
              src="/assets/contract-arabic-text.png"
              alt="مدة العقد والراتب الشهري"
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          </div>

          <div style={{ position: 'absolute', left: '61px', top: '637px', width: '317px', ...rowContainerStyle, gridTemplateColumns: '1.35fr 0.8fr 1fr', height: '20px', fontSize: '9px' }}>
            <span style={{ whiteSpace: 'nowrap' }}>• Duration of insurance</span>
            <span style={{ textAlign: 'center', color: '#555' }}>{formData.durationInsurance || formData.contractPeriod || formData.workYears || ''}</span>
            <span style={{ textAlign: 'right' }}>{renderArabicLabel('مدة التأمين', '9px')}</span>
          </div>
          <div style={{ position: 'absolute', left: '396px', top: '637px', width: '318px', ...rowContainerStyle, gridTemplateColumns: '1.1fr 0.9fr 1fr', height: '20px', fontSize: '9px' }}>
            <span>• Month salary</span>
            <span style={{ textAlign: 'center', color: '#555' }}>{formData.monthlySalary || formData.salary || ''}</span>
            <span style={{ textAlign: 'right' }}>{renderArabicLabel('الراتب الشهري', '9px')}</span>
          </div>

          <div style={{ position: 'absolute', left: '82px', top: '676px', fontSize: '12px', fontWeight: 900, letterSpacing: '1px', color: '#555' }}>
            Educational level and
            <br />
            language :
          </div>
          <div style={{ position: 'absolute', right: '44px', top: '676px', width: '210px', display: 'flex', justifyContent: 'flex-end' }}>
            <img
              src="/assets/educational-level-arabic-text.png"
              alt="مستوى التعليمي واللغة"
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          </div>

          <div
            style={{
              position: 'absolute',
              left: '61px',
              top: '721px',
              width: '653px',
              borderRadius: '8px',
              background: '#e8e8e8',
              padding: '4px 10px',
              display: 'flex',
              flexDirection: 'column',
              gap: '3px'
            }}
          >
            {educationRows.map(([left, right, value]) => (
              <div key={`${left}-${right}`} style={{ ...rowContainerStyle, background: 'transparent', padding: 0 }}>
                <span>{`• ${left}`}</span>
                <span style={{ textAlign: 'center', color: '#555' }}>{value || ''}</span>
                <span style={{ textAlign: 'right' }}>{renderArabicLabel(right)}</span>
              </div>
            ))}
          </div>

          <div style={{ position: 'absolute', left: '82px', top: '821px', fontSize: '12px', fontWeight: 900, letterSpacing: '1px', color: '#555' }}>
            Work experience :
          </div>
          <div style={{ position: 'absolute', right: '34px', top: '797px', width: '140px', display: 'flex', justifyContent: 'flex-end' }}>
            <img
              src="/assets/work-experience-arabic-text.png"
              alt="خبرة العمل"
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          </div>

          <div
            style={{
              position: 'absolute',
              left: '60px',
              top: '859px',
              width: '654px',
              borderRadius: '8px',
              background: '#e8e8e8',
              padding: '6px 10px',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px'
            }}
          >
            {workRows.map(([left, right, value]) => (
              <div key={`${left}-${right}`} style={{ ...rowContainerStyle, background: 'transparent', padding: 0 }}>
                <span>{`• ${left}`}</span>
                <span style={{ textAlign: 'center', color: '#555' }}>{value || ''}</span>
                <span style={{ textAlign: 'right' }}>{renderArabicLabel(right)}</span>
              </div>
            ))}
          </div>
        </section>

        <section
          className="print-page page-break"
          style={{
            position: 'relative',
            height: '1024px',
            width: '768px',
            overflow: 'hidden',
            background: '#fbf7f0',
            boxShadow: '0 16px 34px rgba(0,0,0,0.18)',
            margin: '12px auto 0 auto'
          }}
        >
          <div style={{ position: 'absolute', left: '62px', top: '116px', fontSize: '12px', fontWeight: 900, letterSpacing: '1px', color: '#555' }}>
            Skills and experience :
          </div>
          <div style={{ position: 'absolute', right: '44px', top: '113px', width: '172px', display: 'flex', justifyContent: 'flex-end' }}>
            <img
              src="/assets/skills-and-experience-arabic-text.png"
              alt="المهارات والخبرات"
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          </div>

          <div style={{ position: 'absolute', left: '51px', top: '162px', height: '143px', width: '659px', borderRadius: '8px', background: '#e8e8e8', padding: '8px 12px' }}>
            {template6SkillRows.map((row) => {
              const checked = hasTemplate6Skill(row.aliases);
              return (
                <div
                  key={row.key}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 34px 1fr',
                    alignItems: 'center',
                    minHeight: '22px',
                    color: '#d7a42a',
                    fontWeight: 900,
                    fontSize: '10px'
                  }}
                >
                  <span>{`• ${row.english}`}</span>
                  <span style={{ textAlign: 'center', fontSize: '13px', color: '#777' }}>{checked ? '✔' : ''}</span>
                  <span style={{ textAlign: 'right', fontSize: '13px' }}>{renderArabicLabel(row.arabic, '13px')}</span>
                </div>
              );
            })}
          </div>

          <div style={{ position: 'absolute', left: '62px', top: '322px', fontSize: '12px', fontWeight: 900, letterSpacing: '1px', color: '#555' }}>
            Other experiences :
          </div>
          <div style={{ position: 'absolute', right: '44px', top: '304px', width: '150px', display: 'flex', justifyContent: 'flex-end' }}>
            <img
              src="/assets/other-experiences-arabic-text.png"
              alt="خيارات أخرى"
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          </div>
          <div
            style={{
              position: 'absolute',
              left: '54px',
              top: '359px',
              height: '25px',
              width: '659px',
              borderRadius: '999px',
              background: '#e8e8e8',
              padding: '4px 20px',
              fontSize: '10px',
              fontWeight: 700,
              color: '#555',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis'
            }}
          >
            {experienceText}
          </div>

        </section>
      </div>
    );
  };

  // Main CV Form
  const CVForm = () => (
    <div ref={containerRef} className="min-h-screen bg-cream-warm" style={{backgroundColor: '#f5f5dc', scrollBehavior: 'auto'}}>
      <div className={`${recruitmentTheme.components.card} p-4 shadow-lg`}>
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <h1 className="text-2xl font-bold text-[#8b6914]">Professional CV Builder - Saudi Recruitment</h1>
          <div className="flex items-center space-x-4">
            {/* Auto-save Status */}
            <div className="flex items-center space-x-2">
              {lastSavedAt && (
                <span className="text-sm text-gray-600 flex items-center">
                  <Check className="w-4 h-4 mr-1 text-green-600" />
                  Auto-saved {new Date(lastSavedAt).toLocaleTimeString()}
                </span>
              )}
              {isFormDirtyRef.current && !lastSavedAt && (
                <span className="text-sm text-gray-500 flex items-center">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-600 mr-2"></div>
                  Saving...
                </span>
              )}
            </div>
            
            {/* Auto-save Toggle */}
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isAutoSaveEnabled}
                onChange={(e) => setIsAutoSaveEnabled(e.target.checked)}
                className="sr-only"
              />
              <div className={`relative w-11 h-6 rounded-full transition-colors ${isAutoSaveEnabled ? 'bg-green-500' : 'bg-gray-300'}`}>
                <div className={`absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full transition-transform ${isAutoSaveEnabled ? 'transform translate-x-5' : ''}`} />
              </div>
              <span className="ml-2 text-sm text-gray-700">Auto-save</span>
            </label>
            
            {/* Action Buttons */}
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={saveDraft}
                className="flex items-center px-4 py-2 bg-[#8b6914] text-white rounded hover:bg-[#7a5a11]"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Draft
              </button>
              <button
                type="button"
                onClick={previewCV}
                className="flex items-center px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview CV
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl p-6 mx-auto">
        {/* Multi-CV Selector */}
        {showCVSelector && candidateCVs.length > 0 && (
          <div className="p-6 mb-6 bg-white rounded-lg shadow-md border-l-4 border-blue-500">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-[#8b6914]">
                Multiple CV Versions Found for {currentCandidate?.fullName || currentCandidate?.name}
              </h2>
              <span className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-full">
                {candidateCVs.length} CV{candidateCVs.length > 1 ? 's' : ''} Available
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {candidateCVs.map((cv) => (
                <div
                  key={cv.id}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedCVVersion === cv.id
                      ? 'border-[#8b6914] bg-[#f5f5dc] shadow-md'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onClick={() => {
                    setSelectedCVVersion(cv.id);
                    loadCVVersion(cv);
                  }}
                >
                  <div className="font-semibold text-[#8b6914] mb-2 truncate">{cv.company}</div>
                  <div className="text-sm text-gray-600 mb-2">Template: {cv.template}</div>
                  <div className="text-xs text-gray-500 mb-2">
                    Uploaded: {new Date(cv.uploadedAt).toLocaleDateString()}
                  </div>
                  <div className="text-xs text-gray-400 truncate">
                    File: {cv.originalFileName}
                  </div>
                  {selectedCVVersion === cv.id && (
                    <div className="mt-2 text-xs text-[#8b6914] font-medium">
                      ✓ Currently Selected
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                💡 <strong>Tip:</strong> Click on any CV version above to load its data into the form. Each version contains data extracted for different companies or positions.
              </p>
            </div>
          </div>
        )}

        {/* CV Upload and Auto-fill Section */}
        <div className="p-6 mb-6 bg-white rounded-lg shadow-md border-l-4 border-green-500">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-[#8b6914]">
              Upload CV for Auto-fill
            </h2>
            <span className="px-3 py-1 text-sm bg-green-100 text-green-800 rounded-full">
              AI Extraction
            </span>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select CV File(s) (PDF, JPG, PNG) - Multiple files supported
              </label>
              <div className="space-y-2">
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  multiple
                  className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#8b6914] file:text-white hover:file:bg-[#7a5a11] file:cursor-pointer border border-gray-300 rounded-lg p-2"
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      handleMultipleUploads(Array.from(e.target.files));
                    }
                  }}
                  disabled={cvExtracting}
                />
                <button
                  type="button"
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.capture = 'environment';
                    input.onchange = (e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (file) {
                        handleUpload(file);
                      }
                    };
                    input.click();
                  }}
                  disabled={cvExtracting}
                  className="flex items-center justify-center w-full h-12 border-2 border-dashed border-blue-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors bg-blue-50 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Camera className="w-5 h-5 mr-2 text-blue-600" />
                  <span className="text-blue-600">Take Photo of CV</span>
                </button>
              </div>
            </div>
            
            {cvExtracting && (
              <div className="space-y-2">
                <div className="flex items-center space-x-2 text-[#8b6914]">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#8b6914]"></div>
                  <span className="text-sm">
                    {extractionProgress.total > 1 
                      ? `Processing document ${extractionProgress.current + 1} of ${extractionProgress.total}...`
                      : 'Extracting data from CV...'}
                  </span>
                </div>
                {extractionProgress.total > 1 && (
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-[#8b6914] h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(extractionProgress.current / extractionProgress.total) * 100}%` }}
                    />
                  </div>
                )}
              </div>
            )}
            
            <div className="text-sm text-gray-600">
              💡 <strong>Tip:</strong> Upload one or multiple CV files and we'll automatically extract and merge candidate information to fill the form. You can edit any field after auto-fill.
            </div>
          </div>
        </div>

        {/* Template Selection */}
        <div className="p-6 mb-6 bg-white rounded-lg shadow-md">
          <h2 className="mb-4 text-xl font-semibold" style={{color: '#8b6914'}}>Select CV Template</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
            <div 
              className={`p-4 border-2 rounded-lg cursor-pointer ${selectedTemplate === 'template2' ? 'border-[#8b6914] bg-[#f5f5dc]' : 'border-gray-300'}`}
              onClick={() => {
                setSelectedTemplate('template2');
              }}
            >
              <div className="mb-2 text-center font-bold">Template 1</div>
              <div className="h-32 flex items-center justify-center border border-gray-300">
                <p className="text-center text-sm">NAIM INVESTMENT LIMITED<br/>(Green Headers)</p>
              </div>
            </div>
            <div 
              className={`p-4 border-2 rounded-lg cursor-pointer ${selectedTemplate === 'template3' ? 'border-[#8b6914] bg-[#f5f5dc]' : 'border-gray-300'}`}
              onClick={() => {
                setSelectedTemplate('template3');
              }}
            >
              <div className="mb-2 text-center font-bold">Template 2</div>
              <div className="h-32 flex items-center justify-center border border-gray-300">
                <p className="text-center text-sm">MODERN LAYOUT<br/>(Blue Headers)</p>
              </div>
            </div>
            <div 
              className={`p-4 border-2 rounded-lg cursor-pointer ${selectedTemplate === 'template4' ? 'border-[#8b6914] bg-[#f5f5dc]' : 'border-gray-300'}`}
              onClick={() => {
                setSelectedTemplate('template4');
              }}
            >
              <div className="mb-2 text-center font-bold">Template 3</div>
              <div className="h-32 flex items-center justify-center border border-gray-300">
                <p className="text-center text-sm">NAIM INVESTMENTS<br/>(Maroon/Gold Headers)</p>
              </div>
            </div>
            <div 
              className={`p-4 border-2 rounded-lg cursor-pointer ${selectedTemplate === 'template5' ? 'border-[#8b6914] bg-[#f5f5dc]' : 'border-gray-300'}`}
              onClick={() => {
                setSelectedTemplate('template5');
              }}
            >
              <div className="mb-2 text-center font-bold">Template 4</div>
              <div className="h-32 flex items-center justify-center border border-gray-300">
                <p className="text-center text-sm">NAIM INVESTMENTS LIMITED<br/>(Arabic Style)</p>
              </div>
            </div>
            <div 
              className={`p-4 border-2 rounded-lg cursor-pointer ${selectedTemplate === 'template6' ? 'border-[#8b6914] bg-[#f5f5dc]' : 'border-gray-300'}`}
              onClick={() => {
                setSelectedTemplate('template6');
              }}
            >
              <div className="mb-2 text-center font-bold">Template 5</div>
              <div className="h-32 flex items-center justify-center border border-gray-300">
                <p className="text-center text-sm">DOMESTIC HELPER<br/>(Almelhem Layout)</p>
              </div>
            </div>
          </div>
        </div>

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div className="p-4 mb-6 bg-red-100 border border-red-400 rounded-lg">
            <h3 className="font-bold text-red-700">Please fix the following errors:</h3>
            <ul className="mt-2 ml-4 list-disc text-red-600">
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}


        {/* Company Letterhead Information */}
        <div className="p-6 mb-6 bg-white rounded-lg shadow-md">
          <h2 className="mb-4 text-xl font-semibold" style={{color: '#8b6914'}}>Company Letterhead Information</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Company Logo</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange('logoFile', e.target.files ? e.target.files[0] : null)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
              />
              {formData.logoFile && (
                <div className="relative mt-2">
                  <img src={formData.logoFile} alt="Logo" className="object-contain w-20 h-20 border border-gray-300 rounded" />
                  <button
                    type="button"
                    onClick={() => handleFileDelete('logoFile')}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full hover:bg-red-600 flex items-center justify-center text-sm font-bold"
                    title="Delete logo"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Company Name</label>
              <input
                id="companyName"
                type="text"
                value={formData.companyName}
                onChange={(e) => handleInputChange('companyName', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Company Name (Arabic)</label>
              <input
                id="companyNameArabic"
                type="text"
                value={formData.companyNameArabic}
                onChange={(e) => handleInputChange('companyNameArabic', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
                dir="rtl"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Location</label>
              <input
                id="companyLocation"
                type="text"
                value={formData.companyLocation}
                onChange={(e) => handleInputChange('companyLocation', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Contact Number</label>
              <input
                id="contactNumber"
                type="text"
                value={formData.contactNumber}
                onChange={(e) => handleInputChange('contactNumber', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Position Information */}
        <div className="p-6 mb-6 bg-white rounded-lg shadow-md">
          <h2 className="mb-4 text-xl font-semibold text-[#8b6914]">Position Information</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Position</label>
              <input
                id="position"
                type="text"
                value={formData.position}
                onChange={(e) => handleInputChange('position', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
                placeholder="e.g., DOMESTIC HELPER"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Position (Arabic)</label>
              <input
                id="positionArabic"
                type="text"
                value={formData.positionArabic}
                onChange={(e) => handleInputChange('positionArabic', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
                dir="rtl"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Salary</label>
              <input
                id="salary"
                type="text"
                value={formData.salary}
                onChange={(e) => handleInputChange('salary', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
                placeholder="e.g., 1,100 SAR"
              />
            </div>
          </div>
        </div>

        {/* Passport Details Section */}
        <div className="p-6 mb-6 bg-white rounded-lg shadow-md">
          <h2 className="mb-4 text-xl font-semibold text-[#8b6914]">Passport Details</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Passport Number</label>
              <input
                id="passportNo"
                type="text"
                value={formData.passportNo}
                onChange={(e) => handleInputChange('passportNo', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
                placeholder="e.g., AK1198362"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Date Issued</label>
              <input
                id="dateIssued"
                type="date"
                value={formData.dateIssued}
                onChange={(e) => handleInputChange('dateIssued', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Date Expiry</label>
              <input
                id="dateExpiry"
                type="date"
                value={formData.dateExpiry}
                onChange={(e) => handleInputChange('dateExpiry', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Place Issued</label>
              <input
                id="placeIssued"
                type="text"
                value={formData.placeIssued}
                onChange={(e) => handleInputChange('placeIssued', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
                placeholder="e.g., NAIROBI"
              />
            </div>
          </div>
        </div>

        {/* Personal Information Section */}
        <div className="p-6 mb-6 bg-white rounded-lg shadow-md">
          <h2 className="mb-4 text-xl font-semibold text-[#8b6914]">Personal Information</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Full Name</label>
              <input
                id="fullName"
                type="text"
                value={formData.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
                placeholder="e.g., MESAIDI HASSAN KOMBO"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Full Name (Arabic)</label>
              <input
                id="fullNameArabic"
                type="text"
                value={formData.fullNameArabic}
                onChange={(e) => handleInputChange('fullNameArabic', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
                dir="rtl"
                placeholder="الاسم بالعربية"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Address</label>
              <input
                id="address"
                type="text"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
                placeholder="e.g., MOMBASA, KENYA"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Nationality</label>
              <input
                id="nationality"
                type="text"
                value={formData.nationality}
                onChange={(e) => handleInputChange('nationality', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
                placeholder="e.g., KENYAN"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Religion</label>
              <select
                id="religion"
                value={formData.religion}
                onChange={(e) => handleInputChange('religion', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
              >
                <option value="">Select Religion</option>
                <option value="MUSLIM">MUSLIM</option>
                <option value="CHRISTIAN">CHRISTIAN</option>
                <option value="HINDU">HINDU</option>
                <option value="BUDDHIST">BUDDHIST</option>
                <option value="OTHER">OTHER</option>
              </select>
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Date of Birth</label>
              <input
                id="dateOfBirth"
                type="date"
                value={formData.dateOfBirth}
                onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Place of Birth</label>
              <input
                id="placeOfBirth"
                type="text"
                value={formData.placeOfBirth}
                onChange={(e) => handleInputChange('placeOfBirth', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
                placeholder="e.g., KWALE, KENYA"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Father's Name</label>
              <input
                id="father"
                type="text"
                value={formData.father}
                onChange={(e) => handleInputChange('father', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
                placeholder="e.g., HASSAN KOMBO"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Mother's Name</label>
              <input
                id="mother"
                type="text"
                value={formData.mother}
                onChange={(e) => handleInputChange('mother', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
                placeholder="e.g., UMAZI KOMBO"
              />
            </div>
          </div>
        </div>

        {/* Physical Details Section */}
        <div className="p-6 mb-6 bg-white rounded-lg shadow-md">
          <h2 className="mb-4 text-xl font-semibold text-[#8b6914]">Physical Details</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Age</label>
              <input
                id="age"
                type="text"
                value={formData.age}
                onChange={(e) => handleInputChange('age', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
                placeholder="e.g., 25"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Height</label>
              <input
                id="height"
                type="text"
                value={formData.height}
                onChange={(e) => handleInputChange('height', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
                placeholder="e.g., 5'3' FT"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Weight</label>
              <input
                id="weight"
                type="text"
                value={formData.weight}
                onChange={(e) => handleInputChange('weight', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
                placeholder="e.g., 58.5 KG"
              />
            </div>
          </div>
        </div>

        {/* Family Status Section */}
        <div className="p-6 mb-6 bg-white rounded-lg shadow-md">
          <h2 className="mb-4 text-xl font-semibold text-[#8b6914]">Family Status</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Civil Status</label>
              <select
                id="civilStatus"
                value={formData.civilStatus}
                onChange={(e) => handleInputChange('civilStatus', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
              >
                <option value="">Select Status</option>
                <option value="SINGLE">SINGLE</option>
                <option value="MARRIED">MARRIED</option>
                <option value="DIVORCED">DIVORCED</option>
                <option value="WIDOWED">WIDOWED</option>
              </select>
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Spouse</label>
              <select
                id="spouse"
                value={formData.spouse}
                onChange={(e) => handleInputChange('spouse', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
              >
                <option value="">Select</option>
                <option value="YES">YES</option>
                <option value="NO">NO</option>
              </select>
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Number of Kids</label>
              <input
                id="numberOfKids"
                type="text"
                value={formData.numberOfKids}
                onChange={(e) => handleInputChange('numberOfKids', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
                placeholder="e.g., 1 KID"
              />
            </div>
          </div>
        </div>

        {/* Education & Experience Section */}
        <div className="p-6 mb-6 bg-white rounded-lg shadow-md">
          <h2 className="mb-4 text-xl font-semibold text-[#8b6914]">Education & Experience</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Education Level</label>
              <select
                id="educationLevel"
                value={formData.educationLevel}
                onChange={(e) => handleInputChange('educationLevel', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
              >
                <option value="">Select Level</option>
                <option value="PRIMARY SCHOOL LEVEL">PRIMARY SCHOOL LEVEL</option>
                <option value="SECONDARY SCHOOL LEVEL">SECONDARY SCHOOL LEVEL</option>
                <option value="HIGH SCHOOL LEVEL">HIGH SCHOOL LEVEL</option>
                <option value="COLLEGE LEVEL">COLLEGE LEVEL</option>
                <option value="UNIVERSITY LEVEL">UNIVERSITY LEVEL</option>
              </select>
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">English Level</label>
              <select
                id="englishLevel"
                value={formData.englishLevel}
                onChange={(e) => handleInputChange('englishLevel', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
              >
                <option value="">Select Level</option>
                <option value="BASIC">BASIC</option>
                <option value="GOOD">GOOD</option>
                <option value="FLUENT">FLUENT</option>
                <option value="EXCELLENT">EXCELLENT</option>
              </select>
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Arabic Level</label>
              <select
                id="arabicLevel"
                value={formData.arabicLevel}
                onChange={(e) => handleInputChange('arabicLevel', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
              >
                <option value="">Select Level</option>
                <option value="NONE">NONE</option>
                <option value="LITTLE">LITTLE</option>
                <option value="BASIC">BASIC</option>
                <option value="GOOD">GOOD</option>
                <option value="FLUENT">FLUENT</option>
              </select>
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Work Position</label>
              <input
                id="workPosition"
                type="text"
                value={formData.workPosition}
                onChange={(e) => handleInputChange('workPosition', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
                placeholder="e.g., DOMESTIC WORKER"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Work Country</label>
              <input
                id="workCountry"
                type="text"
                value={formData.workCountry}
                onChange={(e) => handleInputChange('workCountry', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
                placeholder="e.g., SAUDI ARABIA"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Work Years</label>
              <input
                id="workYears"
                type="text"
                value={formData.workYears}
                onChange={(e) => handleInputChange('workYears', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
                placeholder="e.g., 2 YEARS"
              />
            </div>
          </div>
        </div>

        {/* Other Information Section - Only for Candidates Page */}
        <div className="p-6 mb-6 bg-yellow-50 rounded-lg shadow-md border border-yellow-200">
          <h2 className="mb-2 text-xl font-semibold text-[#8b6914]">Other Information</h2>
          <p className="mb-4 text-sm text-gray-600 italic">
            (These are details that appear only in the Candidates page and not the CV preview)
          </p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Contact</label>
              <input
                id="contact"
                type="text"
                value={formData.contact}
                onChange={(e) => handleInputChange('contact', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
                placeholder="e.g., +966508050208"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Email</label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
                placeholder="e.g., candidate@email.com"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Emergency Contact</label>
              <input
                id="emergencyContact"
                type="text"
                value={formData.emergencyContact}
                onChange={(e) => handleInputChange('emergencyContact', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
                placeholder="e.g., +254700000000"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Work Company</label>
              <input
                id="workCompany"
                type="text"
                value={formData.workCompany}
                onChange={(e) => handleInputChange('workCompany', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
                placeholder="e.g., ALJABRIYAH RECRUITMENT OFFICE"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Work City</label>
              <input
                id="workCity"
                type="text"
                value={formData.workCity}
                onChange={(e) => handleInputChange('workCity', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
                placeholder="e.g., RIYADH"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Stage</label>
              <select
                id="stage"
                value={formData.stage}
                onChange={(e) => handleInputChange('stage', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
              >
                {candidateStatusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Template 1 Extra Information */}
        {selectedTemplate === 'template2' && (
          <div className="p-6 mb-6 bg-green-50 border border-green-200 rounded-lg shadow-md">
            <h2 className="mb-4 text-xl font-semibold text-green-800">EXTRA INFORMATION FOR TEMPLATE 1</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Contract Period</label>
                <input
                  type="text"
                  value={formData.contractPeriod || ''}
                  onChange={(e) => handleInputChange('contractPeriod', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
                  placeholder="e.g., 2 YEARS"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Monthly Salary</label>
                <input
                  type="text"
                  value={formData.monthlySalary || ''}
                  onChange={(e) => handleInputChange('monthlySalary', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
                  placeholder="e.g., 1200 SAR"
                />
              </div>
            </div>
          </div>
        )}

        {/* Template 2 Extra Information */}
        {selectedTemplate === 'template3' && (
          <div className="p-6 mb-6 bg-blue-50 border border-blue-200 rounded-lg shadow-md">
            <h2 className="mb-4 text-xl font-semibold text-blue-800">EXTRA INFORMATION FOR TEMPLATE 2</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Custom Company Name</label>
                <input
                  type="text"
                  value={formData.customCompanyName || ''}
                  onChange={(e) => handleInputChange('customCompanyName', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  placeholder="Enter company name"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Custom Company Location</label>
                <input
                  type="text"
                  value={formData.customCompanyLocation || ''}
                  onChange={(e) => handleInputChange('customCompanyLocation', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  placeholder="Enter company location"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Salary Expectation</label>
                <input
                  type="text"
                  value={formData.salaryExpectation || ''}
                  onChange={(e) => handleInputChange('salaryExpectation', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  placeholder="Enter salary expectation"
                />
              </div>
            </div>
          </div>
        )}

        {/* Template 3 Extra Information */}
        {selectedTemplate === 'template4' && (
          <div className="p-6 mb-6 bg-red-50 border border-red-200 rounded-lg shadow-md">
            <h2 className="mb-4 text-xl font-semibold text-red-800">EXTRA INFORMATION FOR TEMPLATE 3</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Reference Number</label>
                <input
                  type="text"
                  value={formData.referenceNumber || ''}
                  onChange={(e) => handleInputChange('referenceNumber', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                  placeholder="e.g., 001"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Date</label>
                <input
                  type="text"
                  value={formData.cvDate || ''}
                  onChange={(e) => handleInputChange('cvDate', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                  placeholder="e.g., 18/JUL/2025"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Destination</label>
                <input
                  type="text"
                  value={formData.destination || ''}
                  onChange={(e) => handleInputChange('destination', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                  placeholder="e.g., SAUDI ARABIA"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Next of Kin Name</label>
                <input
                  type="text"
                  value={formData.nextOfKinName || ''}
                  onChange={(e) => handleInputChange('nextOfKinName', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                  placeholder="Enter next of kin name"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Kinship</label>
                <input
                  type="text"
                  value={formData.kinship || ''}
                  onChange={(e) => handleInputChange('kinship', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                  placeholder="e.g., SISTER"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Other Emergency Info</label>
                <input
                  type="text"
                  value={formData.otherEmergencyInfo || ''}
                  onChange={(e) => handleInputChange('otherEmergencyInfo', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                  placeholder="e.g., 0728773569 (WILLIAM-Cuz, BRO)"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">ID Number</label>
                <input
                  type="text"
                  value={formData.idNumber || ''}
                  onChange={(e) => handleInputChange('idNumber', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                  placeholder="Enter ID number"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Complexion</label>
                <input
                  type="text"
                  value={formData.complexion || ''}
                  onChange={(e) => handleInputChange('complexion', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                  placeholder="e.g., Fair, Medium, Dark"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Education Period</label>
                <input
                  type="text"
                  value={formData.educationPeriod || ''}
                  onChange={(e) => handleInputChange('educationPeriod', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                  placeholder="e.g., 1994-2007"
                />
              </div>
              <div className="md:col-span-3">
                <label className="block mb-2 text-sm font-medium text-gray-700">Additional Education Info</label>
                <input
                  type="text"
                  value={formData.additionalEducationInfo || ''}
                  onChange={(e) => handleInputChange('additionalEducationInfo', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                  placeholder="Enter additional education information"
                />
              </div>
              <div className="md:col-span-3">
                <label className="block mb-2 text-sm font-medium text-gray-700">Hobbies</label>
                <textarea
                  value={formData.hobbies || ''}
                  onChange={(e) => handleInputChange('hobbies', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                  rows={3}
                  placeholder="1. COOKING&#10;2. GOING OUT&#10;3. HOUSE CHORES"
                />
              </div>
              <div className="md:col-span-3">
                <label className="block mb-2 text-sm font-medium text-gray-700">Medical History/Sickness</label>
                <textarea
                  value={formData.medicalHistory || ''}
                  onChange={(e) => handleInputChange('medicalHistory', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                  rows={2}
                  placeholder="Enter any medical history or conditions"
                />
              </div>
            </div>
          </div>
        )}

        {/* Template 4 Extra Information */}
        {selectedTemplate === 'template5' && (
          <div className="p-6 mb-6 bg-purple-50 border border-purple-200 rounded-lg shadow-md">
            <h2 className="mb-4 text-xl font-semibold text-purple-800">EXTRA INFORMATION FOR TEMPLATE 4</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">English Proficiency Level</label>
                <select
                  value={formData.englishLevel || ''}
                  onChange={(e) => handleInputChange('englishLevel', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                >
                  <option value="">Select Level</option>
                  <option value="EXCELLENT">EXCELLENT</option>
                  <option value="GOOD">GOOD</option>
                  <option value="FAIR">FAIR</option>
                  <option value="LITTLE">LITTLE</option>
                  <option value="NO">NO</option>
                </select>
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Arabic Proficiency Level</label>
                <select
                  value={formData.arabicLevel || ''}
                  onChange={(e) => handleInputChange('arabicLevel', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                >
                  <option value="">Select Level</option>
                  <option value="EXCELLENT">EXCELLENT</option>
                  <option value="GOOD">GOOD</option>
                  <option value="FAIR">FAIR</option>
                  <option value="LITTLE">LITTLE</option>
                  <option value="NO">NO</option>
                </select>
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Previous Employment Period</label>
                <input
                  type="text"
                  value={formData.workYears || ''}
                  onChange={(e) => handleInputChange('workYears', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  placeholder="e.g., 2 YEARS"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Previous Employment Position</label>
                <input
                  type="text"
                  value={formData.workPosition || ''}
                  onChange={(e) => handleInputChange('workPosition', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  placeholder="e.g., DOMESTIC WORKER"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Previous Employment Country</label>
                <input
                  type="text"
                  value={formData.workCountry || ''}
                  onChange={(e) => handleInputChange('workCountry', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  placeholder="e.g., SAUDI ARABIA"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Place of Birth</label>
                <input
                  type="text"
                  value={formData.placeOfBirth || ''}
                  onChange={(e) => handleInputChange('placeOfBirth', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  placeholder="e.g., KWALE, KEN"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Civil Status</label>
                <select
                  value={formData.civilStatus || ''}
                  onChange={(e) => handleInputChange('civilStatus', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                >
                  <option value="">Select Status</option>
                  <option value="SINGLE">SINGLE</option>
                  <option value="MARRIED">MARRIED</option>
                  <option value="DIVORCED">DIVORCED</option>
                  <option value="WIDOWED">WIDOWED</option>
                </select>
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Number of Children</label>
                <input
                  type="text"
                  value={formData.numberOfKids || ''}
                  onChange={(e) => handleInputChange('numberOfKids', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  placeholder="e.g., 0 or 2 KIDS"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Height</label>
                <input
                  type="text"
                  value={formData.height || ''}
                  onChange={(e) => handleInputChange('height', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  placeholder="e.g., 5.4 FT"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Weight</label>
                <input
                  type="text"
                  value={formData.weight || ''}
                  onChange={(e) => handleInputChange('weight', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  placeholder="e.g., 54 KGs"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Reference Number</label>
                <input
                  type="text"
                  value={formData.referenceNumber || ''}
                  onChange={(e) => handleInputChange('referenceNumber', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  placeholder="e.g., GDAL09/S"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Education Level</label>
                <input
                  type="text"
                  value={formData.educationLevel || ''}
                  onChange={(e) => handleInputChange('educationLevel', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  placeholder="e.g., HIGH SCHOOL LEVEL"
                />
              </div>
            </div>
          </div>
        )}

        {/* Template 5 Extra Information */}
        {selectedTemplate === 'template6' && (
          <div className="p-6 mb-6 bg-amber-50 border border-amber-200 rounded-lg shadow-md">
            <h2 className="mb-4 text-xl font-semibold text-amber-800">EXTRA INFORMATION FOR TEMPLATE 5</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Worker Name (Header Badge)</label>
                <input
                  type="text"
                  value={formData.workerName || ''}
                  onChange={(e) => handleInputChange('workerName', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent"
                  placeholder="e.g., NAIM"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Duration of Insurance</label>
                <input
                  type="text"
                  value={formData.durationInsurance || ''}
                  onChange={(e) => handleInputChange('durationInsurance', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent"
                  placeholder="e.g., 2 YEARS"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Monthly Salary</label>
                <input
                  type="text"
                  value={formData.monthlySalary || ''}
                  onChange={(e) => handleInputChange('monthlySalary', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent"
                  placeholder="e.g., 1200 SAR"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Previously Worked</label>
                <input
                  type="text"
                  value={formData.previouslyWorked || ''}
                  onChange={(e) => handleInputChange('previouslyWorked', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent"
                  placeholder="e.g., SAUDI ARABIA"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Duration (Years)</label>
                <input
                  type="text"
                  value={formData.workYears || ''}
                  onChange={(e) => handleInputChange('workYears', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent"
                  placeholder="e.g., 3 YEARS"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">City</label>
                <input
                  type="text"
                  value={formData.workCity || ''}
                  onChange={(e) => handleInputChange('workCity', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent"
                  placeholder="e.g., RIYADH"
                />
              </div>
              <div className="md:col-span-3">
                <label className="block mb-2 text-sm font-medium text-gray-700">Other Experiences</label>
                <textarea
                  value={formData.otherExperiences || ''}
                  onChange={(e) => handleInputChange('otherExperiences', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent"
                  rows={2}
                  placeholder="Enter other experiences for this template"
                />
              </div>
            </div>

            <div className="mt-6">
              <h3 className="text-md font-semibold text-amber-900 mb-3">Skills and Experience Checkboxes</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {template6SkillRows.map((row) => (
                  <label key={row.key} className="flex items-center justify-between bg-white border border-amber-200 rounded-lg px-3 py-2 cursor-pointer">
                    <span className="text-sm text-gray-700">{row.english}</span>
                    <input
                      type="checkbox"
                      checked={hasTemplate6Skill(row.aliases)}
                      onChange={(e) => setTemplate6Skill(row.aliases, e.target.checked, row.english)}
                      className="w-4 h-4"
                    />
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Remarks Section */}
        <div className="p-6 mb-6 bg-white rounded-lg shadow-md">
          <h2 className="mb-4 text-xl font-semibold text-[#8b6914]">Remarks</h2>
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">Additional Remarks</label>
            <textarea
              id="remarks"
              value={formData.remarks}
              onChange={(e) => handleInputChange('remarks', e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
              rows={3}
              placeholder="e.g., COOPERATIVE / MEDIUM BODY BUILD / HARDWORKING / EXPERIENCED"
            />
          </div>
        </div>

        <div className="p-6 mb-6 bg-white rounded-lg shadow-md">
          <h2 className="mb-4 text-xl font-semibold text-[#8b6914]">Skills Selection</h2>
          
          {/* Add Skills Section */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
            <div className="flex items-center gap-3">
              <input
                id="newSkillName"
                type="text"
                value={newSkillName}
                onChange={(e) => setNewSkillName(e.target.value)}
                placeholder="Enter new skill..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8b6914] focus:border-transparent"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addNewSkill();
                  }
                }}
              />
              <button
                type="button"
                onClick={addNewSkill}
                className="px-4 py-2 bg-[#8b6914] text-white rounded-md hover:bg-[#6d5411] transition-colors duration-200 flex items-center gap-2"
              >
                <Plus size={16} />
                Add Skills
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {formData.allSkills.map((skill, index) => (
              <div key={index} className="flex items-center p-3 border border-gray-300 rounded-lg">
                <input
                  type="checkbox"
                  checked={skill.checked}
                  onChange={() => toggleSkill(index)}
                  className="w-5 h-5 mr-3 text-[#8b6914]"
                />
                <span className="flex-1">{skill.name}</span>
                <button
                  type="button"
                  onClick={() => removeSkill(index)}
                  className="ml-2 text-red-500 hover:text-red-700 transition-colors duration-200"
                  title="Remove skill"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 mb-6 bg-white rounded-lg shadow-md">
          <h2 className="mb-4 text-xl font-semibold text-[#8b6914]">Photo Upload</h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="p-6 text-center border-2 border-amber-300 border-dashed rounded-lg">
              <Camera className="w-12 h-12 mx-auto mb-4 text-[#8b6914]" />
              <h4 className="mb-2 text-lg font-medium">Profile Photo</h4>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange('profilePhoto', e.target.files ? e.target.files[0] : null)}
                className="hidden"
                id="profile-photo"
              />
              <div className="space-x-2">
                <label htmlFor="profile-photo" className="inline-block px-4 py-2 text-white bg-[#8b6914] rounded-lg cursor-pointer hover:bg-[#7a5a11]">
                  Choose Photo
                </label>
                <button
                  type="button"
                  onClick={() => handleCameraCapture('profilePhoto')}
                  className="inline-block px-4 py-2 text-blue-600 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors"
                >
                  Take Photo
                </button>
              </div>
              {formData.profilePhoto && (
                <div className="mt-4">
                  <div className="relative inline-block">
                    <img src={formData.profilePhoto} alt="Profile" className="object-contain w-24 h-24 mx-auto rounded bg-gray-100" />
                    <button
                      type="button"
                      onClick={() => handleFileDelete('profilePhoto')}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full hover:bg-red-600 flex items-center justify-center text-sm font-bold"
                      title="Delete profile photo"
                    >
                      ×
                    </button>
                  </div>
                  <p className="mt-2 text-sm text-green-600">✓ Profile photo uploaded</p>
                </div>
              )}
            </div>

            <div className="p-6 text-center border-2 border-amber-300 border-dashed rounded-lg">
              <Camera className="w-12 h-12 mx-auto mb-4 text-[#8b6914]" />
              <h4 className="mb-2 text-lg font-medium">Full Body Photo</h4>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange('fullBodyPhoto', e.target.files ? e.target.files[0] : null)}
                className="hidden"
                id="fullbody-photo"
              />
              <div className="space-x-2">
                <label htmlFor="fullbody-photo" className="inline-block px-4 py-2 text-white bg-[#8b6914] rounded-lg cursor-pointer hover:bg-[#7a5a11]">
                  Choose Photo
                </label>
                <button
                  type="button"
                  onClick={() => handleCameraCapture('fullBodyPhoto')}
                  className="inline-block px-4 py-2 text-blue-600 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors"
                >
                  Take Photo
                </button>
              </div>
              {formData.fullBodyPhoto && (
                <div className="mt-4">
                  <div className="relative inline-block">
                    <img src={formData.fullBodyPhoto} alt="Full Body" className="object-contain w-24 h-32 mx-auto rounded bg-gray-100" />
                    <button
                      type="button"
                      onClick={() => handleFileDelete('fullBodyPhoto')}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full hover:bg-red-600 flex items-center justify-center text-sm font-bold"
                      title="Delete full body photo"
                    >
                      ×
                    </button>
                  </div>
                  <p className="mt-2 text-sm text-green-600">✓ Full body photo uploaded</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Document Upload Section */}
        <div className="p-6 mb-6 bg-white rounded-lg shadow-md">
          <h2 className="mb-4 text-xl font-semibold text-[#8b6914]">Document Upload</h2>
          
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Passport Document */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Passport Document</label>
              <div className="space-y-2">
                <input
                  type="file"
                  onChange={(e) => handleDocumentUpload('passportDocument', e.target.files?.[0] || null)}
                  className="hidden"
                  id="passport-upload"
                  disabled={uploading}
                  accept="image/*,.pdf,.docx,.doc"
                />
                <label htmlFor="passport-upload" className={`flex items-center justify-center w-full h-12 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#8b6914] transition-colors ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#8b6914] mr-2"></div>
                      <span className="text-gray-600">Processing document...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5 mr-2 text-gray-500" />
                      <span className="text-gray-600">Choose Passport Document</span>
                    </>
                  )}
                </label>
                <button
                  type="button"
                  onClick={() => handleCameraCapture('passportDocument')}
                  className="flex items-center justify-center w-full h-12 border-2 border-dashed border-blue-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors bg-blue-50 hover:bg-blue-100"
                  disabled={uploading}
                >
                  <Camera className="w-5 h-5 mr-2 text-blue-600" />
                  <span className="text-blue-600">Take Photo of Passport</span>
                </button>
              </div>
              {formData.passportDocument && (
                <div className="mt-2 p-2 bg-green-50 rounded border border-green-200 flex items-center justify-between">
                  <p className="text-sm text-green-600">✓ {formData.passportDocument.name}</p>
                  <button
                    type="button"
                    onClick={() => handleFileDelete('passportDocument')}
                    className="w-6 h-6 bg-red-500 text-white rounded-full hover:bg-red-600 flex items-center justify-center text-sm font-bold ml-2"
                    title="Delete passport document"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>

            {/* ID Document */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">ID Document</label>
              <div className="space-y-2">
                <input
                  type="file"
                  onChange={(e) => handleDocumentUpload('idDocument', e.target.files?.[0] || null)}
                  className="hidden"
                  id="id-upload"
                  disabled={uploading}
                  accept="image/*,.pdf,.docx,.doc"
                />
                <label htmlFor="id-upload" className={`flex items-center justify-center w-full h-12 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#8b6914] transition-colors ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#8b6914] mr-2"></div>
                      <span className="text-gray-600">Processing document...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5 mr-2 text-gray-500" />
                      <span className="text-gray-600">Choose ID Document</span>
                    </>
                  )}
                </label>
                <button
                  type="button"
                  onClick={() => handleCameraCapture('idDocument')}
                  className="flex items-center justify-center w-full h-12 border-2 border-dashed border-blue-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors bg-blue-50 hover:bg-blue-100"
                  disabled={uploading}
                >
                  <Camera className="w-5 h-5 mr-2 text-blue-600" />
                  <span className="text-blue-600">Take Photo of ID</span>
                </button>
              </div>
              {formData.idDocument && (
                <div className="mt-2 p-2 bg-green-50 rounded border border-green-200 flex items-center justify-between">
                  <p className="text-sm text-green-600">✓ {formData.idDocument.name}</p>
                  <button
                    type="button"
                    onClick={() => handleFileDelete('idDocument')}
                    className="w-6 h-6 bg-red-500 text-white rounded-full hover:bg-red-600 flex items-center justify-center text-sm font-bold ml-2"
                    title="Delete ID document"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>

            {/* Qualification Certificates */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Qualification Certificates</label>
              <div className="space-y-2">
                <input
                  type="file"
                  multiple
                  onChange={(e) => handleMultipleDocumentUpload('qualificationsCertificates', e.target.files)}
                  className="hidden"
                  id="qualifications-upload"
                />
                <label htmlFor="qualifications-upload" className="flex items-center justify-center w-full h-12 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#8b6914] transition-colors">
                  <Upload className="w-5 h-5 mr-2 text-gray-500" />
                  <span className="text-gray-600">Choose Qualification Certificates</span>
                </label>
                <button
                  type="button"
                  onClick={() => handleCameraCapture('qualificationsCertificates')}
                  className="flex items-center justify-center w-full h-12 border-2 border-dashed border-blue-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors bg-blue-50 hover:bg-blue-100"
                >
                  <Camera className="w-5 h-5 mr-2 text-blue-600" />
                  <span className="text-blue-600">Take Photo of Certificates</span>
                </button>
              </div>
              {formData.qualificationsCertificates.length > 0 && (
                <div className="mt-2 space-y-1">
                  {formData.qualificationsCertificates.map((file: any, index: number) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
                      <p className="text-sm text-green-600">✓ {file.name}</p>
                      <button 
                        onClick={() => removeDocument('qualificationsCertificates', index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Good Conduct Certificates */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Good Conduct Certificates</label>
              <div className="space-y-2">
                <input
                  type="file"
                  multiple
                  onChange={(e) => handleMultipleDocumentUpload('goodConductCertificates', e.target.files)}
                  className="hidden"
                  id="conduct-upload"
                />
                <label htmlFor="conduct-upload" className="flex items-center justify-center w-full h-12 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#8b6914] transition-colors">
                  <Upload className="w-5 h-5 mr-2 text-gray-500" />
                  <span className="text-gray-600">Choose Good Conduct Certificates</span>
                </label>
                <button
                  type="button"
                  onClick={() => handleCameraCapture('goodConductCertificates')}
                  className="flex items-center justify-center w-full h-12 border-2 border-dashed border-blue-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors bg-blue-50 hover:bg-blue-100"
                >
                  <Camera className="w-5 h-5 mr-2 text-blue-600" />
                  <span className="text-blue-600">Take Photo of Certificates</span>
                </button>
              </div>
              {formData.goodConductCertificates.length > 0 && (
                <div className="mt-2 space-y-1">
                  {formData.goodConductCertificates.map((file: any, index: number) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
                      <p className="text-sm text-green-600">✓ {file.name}</p>
                      <button 
                        onClick={() => removeDocument('goodConductCertificates', index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Other Documents - Full Width */}
          <div className="mt-6">
            <label className="block mb-2 text-sm font-medium text-gray-700">Other Documents</label>
            <div className="space-y-2">
              <input
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                multiple
                onChange={(e) => handleMultipleDocumentUpload('otherDocuments', e.target.files)}
                className="hidden"
                id="other-docs-upload"
              />
              <label htmlFor="other-docs-upload" className="flex items-center justify-center w-full h-12 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#8b6914] transition-colors">
                <Upload className="w-5 h-5 mr-2 text-gray-500" />
                <span className="text-gray-600">Choose Other Documents (PDF, Word, Excel, Images)</span>
              </label>
              <button
                type="button"
                onClick={() => handleCameraCapture('otherDocuments')}
                className="flex items-center justify-center w-full h-12 border-2 border-dashed border-blue-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors bg-blue-50 hover:bg-blue-100"
              >
                <Camera className="w-5 h-5 mr-2 text-blue-600" />
                <span className="text-blue-600">Take Photo of Documents</span>
              </button>
            </div>
            {formData.otherDocuments.length > 0 && (
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                {formData.otherDocuments.map((file: any, index: number) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
                    <p className="text-sm text-green-600">✓ {file.name}</p>
                    <button 
                      onClick={() => removeDocument('otherDocuments', index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Media Upload Section */}
        <div className="p-6 mb-6 bg-white rounded-lg shadow-md">
          <h2 className="mb-4 text-xl font-semibold text-[#8b6914]">Media Upload</h2>
          
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">Images, Videos & Music</label>
            <input
              type="file"
              accept="image/*,video/*,audio/*"
              multiple
              onChange={(e) => handleMediaUpload(e.target.files)}
              className="hidden"
              id="media-upload"
            />
            <label htmlFor="media-upload" className="flex items-center justify-center w-full h-16 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#8b6914] transition-colors">
              <div className="flex items-center space-x-2">
                <Image className="w-5 h-5 text-gray-500" />
                <Video className="w-5 h-5 text-gray-500" />
                <Music className="w-5 h-5 text-gray-500" />
                <span className="text-gray-600 ml-2">Choose Media Files (Images, Videos, Music)</span>
              </div>
            </label>
            {formData.mediaFiles.length > 0 && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {formData.mediaFiles.map((file: any, index: number) => (
                  <div key={index} className="relative p-3 bg-blue-50 rounded border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {file.type.startsWith('image/') && <Image className="w-4 h-4 text-blue-500" />}
                        {file.type.startsWith('video/') && <Video className="w-4 h-4 text-blue-500" />}
                        {file.type.startsWith('audio/') && <Music className="w-4 h-4 text-blue-500" />}
                        <p className="text-sm text-blue-600 truncate">{file.name}</p>
                      </div>
                      <button 
                        onClick={() => removeDocument('mediaFiles', index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    {file.type.startsWith('image/') && (
                      <div className="mt-2">
                        <img src={file.data} alt={file.name} className="w-full h-20 object-cover rounded" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>


        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <button
            type="button"
            onClick={saveDraft}
            className={`${recruitmentTheme.components.button.secondary} w-full justify-start`}
          >
            <Save className="mr-2 h-4 w-4" />
            Save Draft
          </button>
          <button
            type="button"
            onClick={saveCandidateFromCV}
            className={`${recruitmentTheme.components.button.secondary} w-full justify-start`}
          >
            <Plus className="mr-2 h-4 w-4" />
            Save as Candidate
          </button>
          <button
            type="button"
            onClick={previewCV}
            className={`${recruitmentTheme.components.button.secondary} w-full justify-start`}
          >
            <Eye className="mr-2 h-4 w-4" />
            Preview & Print CV
          </button>
          <button
            type="button"
            onClick={clearStorageManually}
            className={`${recruitmentTheme.components.button.secondary} w-full justify-start`}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Clear Storage
          </button>
        </div>
      </div>
    </div>
  );

  // CV Preview Mode
  const CVPreview = () => (
    <div className="min-h-screen bg-gray-100">
      <div className="sticky top-0 z-10 p-4 bg-white shadow-md">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <h2 className="text-xl font-bold">CV Preview - {formData.fullName || 'Draft'}</h2>
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={() => setPreviewMode(false)}
              className={`${recruitmentTheme.components.button.secondary} w-full justify-start`}
            >
              <Eye className="mr-2 h-4 w-4" />
              Edit
            </button>
            <button
              type="button"
              onClick={saveDraft}
              className={`${recruitmentTheme.components.button.secondary} w-full justify-start`}
            >
              <Save className="mr-2 h-4 w-4" />
              Save Draft
            </button>
            <button
              type="button"
              onClick={downloadAndPrintCV}
              className={`${recruitmentTheme.components.button.secondary} w-full justify-start`}
            >
              <Printer className="mr-2 h-4 w-4" />
              Print/Download
            </button>
          </div>
        </div>
      </div>

      <div className="flex justify-center p-8">
        <div className="shadow-2xl" ref={printRef}>
          {selectedTemplate === 'template2' ? (
            <Template2Preview />
          ) : selectedTemplate === 'template3' ? (
            <Template3Preview />
          ) : selectedTemplate === 'template4' ? (
            <Template4Preview />
          ) : selectedTemplate === 'template5' ? (
            <Template5Preview />
          ) : selectedTemplate === 'template6' ? (
            <Template6Preview />
          ) : (
            <Template4Preview />
          )}
          
          {/* Uploaded Documents Section - displayed below CV */}
          {(formData.passportDocument || 
            formData.idDocument || 
            formData.qualificationsCertificates.length > 0 || 
            formData.goodConductCertificates.length > 0 || 
            formData.otherDocuments.length > 0) && (
            <div className="bg-white">
              <div className="space-y-0">
                {/* Passport Document */}
                {formData.passportDocument && (
                  <div className="document-container page-break">
                    <div className="zoom-controls no-print" style={{padding: '10px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                      <span style={{fontSize: '12px', fontWeight: 'bold'}}>Passport Document - Zoom:</span>
                      <select 
                        value={formData.passportZoom} 
                        onChange={(e) => handleZoomChange('passport', parseInt(e.target.value))}
                        style={{padding: '4px', border: '1px solid #ccc', borderRadius: '4px'}}
                      >
                        <option value={25}>25%</option>
                        <option value={33}>33%</option>
                        <option value={50}>50%</option>
                        <option value={75}>75%</option>
                        <option value={100}>100%</option>
                        <option value={125}>125%</option>
                        <option value={150}>150%</option>
                      </select>
                    </div>
                    {formData.passportDocument.type === 'application/pdf' ? (
                      <iframe 
                        src={formData.passportDocument.data} 
                        className="w-full"
                        title="Passport Document"
                        style={{
                          border: 'none', 
                          margin: 0, 
                          padding: 0, 
                          display: 'block',
                          height: `${(1400 * formData.passportZoom) / 100}px`,
                          transform: `scale(${formData.passportZoom / 100})`,
                          transformOrigin: 'top left',
                          width: `${100 / (formData.passportZoom / 100)}%`
                        }}
                      />
                    ) : (
                      <img 
                        src={formData.passportDocument.data} 
                        alt="Passport Document" 
                        className="w-full h-auto"
                        style={{
                          border: 'none', 
                          margin: 0, 
                          padding: 0, 
                          display: 'block',
                          transform: `scale(${formData.passportZoom / 100})`,
                          transformOrigin: 'top left',
                          width: `${100 / (formData.passportZoom / 100)}%`
                        }}
                      />
                    )}
                  </div>
                )}

                {/* ID Document */}
                {formData.idDocument && (
                  <div className="document-container page-break">
                    <div className="zoom-controls no-print" style={{padding: '10px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                      <span style={{fontSize: '12px', fontWeight: 'bold'}}>ID Document - Zoom:</span>
                      <select 
                        value={formData.idZoom} 
                        onChange={(e) => handleZoomChange('id', parseInt(e.target.value))}
                        style={{padding: '4px', border: '1px solid #ccc', borderRadius: '4px'}}
                      >
                        <option value={25}>25%</option>
                        <option value={33}>33%</option>
                        <option value={50}>50%</option>
                        <option value={75}>75%</option>
                        <option value={100}>100%</option>
                        <option value={125}>125%</option>
                        <option value={150}>150%</option>
                      </select>
                    </div>
                    {formData.idDocument.type === 'application/pdf' ? (
                      <iframe 
                        src={formData.idDocument.data} 
                        className="w-full"
                        title="ID Document"
                        style={{
                          border: 'none', 
                          margin: 0, 
                          padding: 0, 
                          display: 'block',
                          height: `${(1400 * formData.idZoom) / 100}px`,
                          transform: `scale(${formData.idZoom / 100})`,
                          transformOrigin: 'top left',
                          width: `${100 / (formData.idZoom / 100)}%`
                        }}
                      />
                    ) : (
                      <img 
                        src={formData.idDocument.data} 
                        alt="ID Document" 
                        className="w-full h-auto"
                        style={{
                          border: 'none', 
                          margin: 0, 
                          padding: 0, 
                          display: 'block',
                          transform: `scale(${formData.idZoom / 100})`,
                          transformOrigin: 'top left',
                          width: `${100 / (formData.idZoom / 100)}%`
                        }}
                      />
                    )}
                  </div>
                )}

                {/* Qualification Certificates */}
                {formData.qualificationsCertificates.length > 0 && (
                  <div>
                    {formData.qualificationsCertificates.map((doc: any, index: number) => (
                      <div key={index} className="document-container page-break">
                        <div className="zoom-controls no-print" style={{padding: '10px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                          <span style={{fontSize: '12px', fontWeight: 'bold'}}>Qualification Certificate {index + 1} - Zoom:</span>
                          <select 
                            value={formData.qualificationZooms[index] || 100} 
                            onChange={(e) => handleMultipleZoomChange('qualificationZooms', index, parseInt(e.target.value))}
                            style={{padding: '4px', border: '1px solid #ccc', borderRadius: '4px'}}
                          >
                            <option value={25}>25%</option>
                            <option value={33}>33%</option>
                            <option value={50}>50%</option>
                            <option value={75}>75%</option>
                            <option value={100}>100%</option>
                            <option value={125}>125%</option>
                            <option value={150}>150%</option>
                          </select>
                        </div>
                        {doc.type === 'application/pdf' ? (
                          <iframe 
                            src={doc.data} 
                            className="w-full"
                            title={`Qualification Certificate ${index + 1}`}
                            style={{
                              border: 'none', 
                              margin: 0, 
                              padding: 0, 
                              display: 'block',
                              height: `${(1400 * (formData.qualificationZooms[index] || 100)) / 100}px`,
                              transform: `scale(${(formData.qualificationZooms[index] || 100) / 100})`,
                              transformOrigin: 'top left',
                              width: `${100 / ((formData.qualificationZooms[index] || 100) / 100)}%`
                            }}
                          />
                        ) : (
                          <img 
                            src={doc.data} 
                            alt={`Qualification Certificate ${index + 1}`} 
                            className="w-full h-auto"
                            style={{
                              border: 'none', 
                              margin: 0, 
                              padding: 0, 
                              display: 'block',
                              transform: `scale(${(formData.qualificationZooms[index] || 100) / 100})`,
                              transformOrigin: 'top left',
                              width: `${100 / ((formData.qualificationZooms[index] || 100) / 100)}%`
                            }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Good Conduct Certificates */}
                {formData.goodConductCertificates.length > 0 && (
                  <div>
                    {formData.goodConductCertificates.map((doc: any, index: number) => (
                      <div key={index} className="document-container page-break">
                        <div className="zoom-controls no-print" style={{padding: '10px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                          <span style={{fontSize: '12px', fontWeight: 'bold'}}>Good Conduct Certificate {index + 1} - Zoom:</span>
                          <select 
                            value={formData.conductZooms[index] || 100} 
                            onChange={(e) => handleMultipleZoomChange('conductZooms', index, parseInt(e.target.value))}
                            style={{padding: '4px', border: '1px solid #ccc', borderRadius: '4px'}}
                          >
                            <option value={25}>25%</option>
                            <option value={33}>33%</option>
                            <option value={50}>50%</option>
                            <option value={75}>75%</option>
                            <option value={100}>100%</option>
                            <option value={125}>125%</option>
                            <option value={150}>150%</option>
                          </select>
                        </div>
                        {doc.type === 'application/pdf' ? (
                          <iframe 
                            src={doc.data} 
                            className="w-full"
                            title={`Good Conduct Certificate ${index + 1}`}
                            style={{
                              border: 'none', 
                              margin: 0, 
                              padding: 0, 
                              display: 'block',
                              height: `${(1400 * (formData.conductZooms[index] || 100)) / 100}px`,
                              transform: `scale(${(formData.conductZooms[index] || 100) / 100})`,
                              transformOrigin: 'top left',
                              width: `${100 / ((formData.conductZooms[index] || 100) / 100)}%`
                            }}
                          />
                        ) : (
                          <img 
                            src={doc.data} 
                            alt={`Good Conduct Certificate ${index + 1}`} 
                            className="w-full h-auto"
                            style={{
                              border: 'none', 
                              margin: 0, 
                              padding: 0, 
                              display: 'block',
                              transform: `scale(${(formData.conductZooms[index] || 100) / 100})`,
                              transformOrigin: 'top left',
                              width: `${100 / ((formData.conductZooms[index] || 100) / 100)}%`
                            }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Other Documents */}
                {formData.otherDocuments.length > 0 && (
                  <div>
                    {formData.otherDocuments.map((doc: any, index: number) => (
                      <div key={index} className="document-container page-break">
                        <div className="zoom-controls no-print" style={{padding: '10px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                          <span style={{fontSize: '12px', fontWeight: 'bold'}}>Other Document {index + 1} - Zoom:</span>
                          <select 
                            value={formData.otherZooms[index] || 100} 
                            onChange={(e) => handleMultipleZoomChange('otherZooms', index, parseInt(e.target.value))}
                            style={{padding: '4px', border: '1px solid #ccc', borderRadius: '4px'}}
                          >
                            <option value={25}>25%</option>
                            <option value={33}>33%</option>
                            <option value={50}>50%</option>
                            <option value={75}>75%</option>
                            <option value={100}>100%</option>
                            <option value={125}>125%</option>
                            <option value={150}>150%</option>
                          </select>
                        </div>
                        {doc.type === 'application/pdf' ? (
                          <iframe 
                            src={doc.data} 
                            className="w-full"
                            title={`Other Document ${index + 1}`}
                            style={{
                              border: 'none', 
                              margin: 0, 
                              padding: 0, 
                              display: 'block',
                              height: `${(1400 * (formData.otherZooms[index] || 100)) / 100}px`,
                              transform: `scale(${(formData.otherZooms[index] || 100) / 100})`,
                              transformOrigin: 'top left',
                              width: `${100 / ((formData.otherZooms[index] || 100) / 100)}%`
                            }}
                          />
                        ) : doc.type.startsWith('image/') ? (
                          <img 
                            src={doc.data} 
                            alt={`Other Document ${index + 1}`} 
                            className="w-full h-auto"
                            style={{
                              border: 'none', 
                              margin: 0, 
                              padding: 0, 
                              display: 'block',
                              transform: `scale(${(formData.otherZooms[index] || 100) / 100})`,
                              transformOrigin: 'top left',
                              width: `${100 / ((formData.otherZooms[index] || 100) / 100)}%`
                            }}
                          />
                        ) : (
                          <div className="w-full h-auto document-container">
                            <p className="text-sm text-center py-4">{doc.name}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Important: call these render helpers directly to avoid remounting on each keystroke.
  return previewMode ? CVPreview() : CVForm();
};

// Demo component to showcase the CV Builder
const Demo = () => {
  return (
    <div className="bg-gray-100 min-h-screen">
      <div className="max-w-6xl mx-auto p-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#8b6914] mb-2">CV Builder with Multiple Templates</h1>
          <p className="text-gray-600">Create professional CVs with our easy-to-use builder</p>
        </div>
        
        <CVBuilder />
      </div>
    </div>
  );
};

export default Demo;
